// Frontend entrypoint for Atlas with Supabase integration.
import { refreshIcons } from './core/dom';
import {
  supabase,
  signIn,
  signOut,
  getCurrentSession,
  getCurrentProfile,
  getWorkspaces,
  getProcesses,
  getCompanies,
  getIndicators,
  getMeasurements,
  getRisks,
  getActions,
  getImprovementPlans,
  getImprovementFollowups,
  getDocuments,
  getAudits,
  getAuditFindings,
  getAuditCommitments,
  getPeople,
  getRoles,
  createWorkspace,
  createProcess,
  updateProcess,
  createCompany,
  updateCompany,
  createRisk,
  updateRisk,
  createAction,
  updateAction,
  createImprovementPlan,
  updateImprovementPlan,
  createImprovementFollowup,
  createDocument,
  updateDocument,
  deleteDocument,
  createAudit,
  updateAudit,
  createAuditFinding,
  updateAuditFinding,
  createAuditCommitment,
  updateAuditCommitment,
  createPerson,
  updatePerson,
  createRole,
  updateRole,
  deleteRole,
  createIndicator,
  createMeasurement,
} from './services/supabase';
import type {
  Action,
  Audit,
  AuditFinding,
  AuditCommitment,
  Company,
  CompanyObjective,
  Document,
  Indicator,
  ImprovementFollowup,
  ImprovementPlan,
  Measurement,
  Person,
  ProcessSummary,
  Profile,
  Role,
  Risk,
  Workspace,
} from './types/domain';

const state = {
  currentWorkspace: null as Workspace | null,
  currentProfile: null as Profile | null,
  currentCompany: null as Company | null,
  currentProcessId: null as string | null,
  currentProcessTab: 'Resumen' as 'Resumen' | 'Flujo' | 'Indicadores' | 'Riesgos' | 'Responsables' | 'Documentos' | 'Historial',
  processFormMode: 'create' as 'create' | 'edit',
  riskFormMode: 'create' as 'create' | 'edit',
  roleFormMode: 'create' as 'create' | 'edit',
  currentIndicatorId: null as string | null,
  currentIndicatorRangePreset: '6m' as '6m' | '3m' | '30d' | 'custom',
  currentIndicatorDateFrom: null as string | null,
  currentIndicatorDateTo: null as string | null,
  currentRoleId: null as string | null,
  currentRiskId: null as string | null,
  currentRiskProcessFilter: null as string | null,
  currentRiskPage: 1,
  riskPageSize: 5,
  actionFormMode: 'create' as 'create' | 'edit',
  currentActionId: null as string | null,
  currentActionScope: 'all' as 'mine' | 'all',
  currentActionView: 'board' as 'board' | 'table' | 'calendar',
  hideCompletedActions: true,
  actionVisibleCount: 6,
  currentImprovementPlanId: null as string | null,
  currentDocumentCategoryFilter: 'all' as 'all' | 'process' | 'policy' | 'format' | 'archive',
  currentDocumentProcessFilter: 'all' as string,
  currentDocumentStatusFilter: 'all' as 'all' | 'Vigente' | 'Por revisar' | 'Próximo a vencer',
  currentDocumentSearch: '',
  currentDocumentId: null as string | null,
  currentManagedDocumentId: null as string | null,
  currentManagedPersonId: null as string | null,
  currentManagedPersonNextStatus: null as string | null,
  currentAuditId: null as string | null,
  currentAuditTab: 'planned' as 'planned' | 'completed' | 'findings',
  currentFindingId: null as string | null,
  currentIndicatorTab: 'followup' as 'followup' | 'catalog' | 'calendar',
  currentMeasurementIndicatorId: null as string | null,
  processes: [] as ProcessSummary[],
  indicators: [] as Indicator[],
  measurementsByIndicator: {} as Record<string, Measurement[]>,
  risks: [] as Risk[],
  actions: [] as Action[],
  improvementPlans: [] as ImprovementPlan[],
  improvementFollowupsByPlan: {} as Record<string, ImprovementFollowup[]>,
  documents: [] as Document[],
  audits: [] as Audit[],
  auditFindingsByAudit: {} as Record<string, AuditFinding[]>,
  auditCommitmentsByFinding: {} as Record<string, AuditCommitment[]>,
  people: [] as Person[],
  roles: [] as Role[],
};

let documentAutosaveTimer: number | null = null;
let documentSaving = false;
let defaultWordEditorTemplate = '';
let defaultSheetEditorTemplate = '';
type SpreadsheetWorkbook = {
  sheetNames: string[];
  sheets: string[][][];
  activeSheetIndex: number;
};

const WORKBOOK_CONTENT_PREFIX = '__ATLAS_WORKBOOK__:';

let spreadsheetWorkbook: SpreadsheetWorkbook | null = null;
let spreadsheetSelection: {
  anchorRow: number;
  anchorColumn: number;
  dragging: boolean;
} | null = null;

function isSpreadsheetFormat(format?: string | null) {
  const value = (format || '').toLowerCase();
  return value.includes('xls') || value.includes('xlsx') || value.includes('csv');
}

function getSpreadsheetColumnLabel(index: number) {
  let value = index;
  let label = '';
  do {
    label = String.fromCharCode(65 + (value % 26)) + label;
    value = Math.floor(value / 26) - 1;
  } while (value >= 0);
  return label;
}

function createDefaultSheetMatrix(columns = 4, rows = 5) {
  const header = Array.from({ length: columns }, (_, index) => `Columna ${getSpreadsheetColumnLabel(index)}`);
  const body = Array.from({ length: Math.max(1, rows - 1) }, () => Array.from({ length: columns }, () => ''));
  return [header, ...body];
}

function readSheetMatrix(sheetGrid: HTMLElement) {
  const children = Array.from(sheetGrid.children) as HTMLElement[];
  if (children.length === 0) {
    return [[
      'Columna A',
      'Columna B',
      'Columna C',
      'Columna D',
    ]];
  }

  let columnCount = 0;
  let cursor = 1;
  while (cursor < children.length) {
    const item = children[cursor];
    const text = (item.textContent || '').trim();
    if (item.tagName === 'B' && /^\d+$/.test(text)) break;
    columnCount += 1;
    cursor += 1;
  }

  if (!columnCount) columnCount = 4;

  const rows: string[][] = [];
  while (cursor < children.length) {
    cursor += 1; // Skip row header
    const row: string[] = [];
    for (let columnIndex = 0; columnIndex < columnCount && cursor < children.length; columnIndex += 1) {
      row.push((children[cursor] as HTMLElement).innerHTML);
      cursor += 1;
    }
    if (row.length > 0) rows.push(row);
  }

  if (rows.length === 0) {
    rows.push(Array.from({ length: columnCount }, (_, index) => `Columna ${getSpreadsheetColumnLabel(index)}`));
  }

  return rows;
}

function normalizeWorkbook(workbook: SpreadsheetWorkbook): SpreadsheetWorkbook {
  const names = workbook.sheetNames.length > 0 ? workbook.sheetNames : ['Hoja 1'];
  const sheets = workbook.sheets.length > 0 ? workbook.sheets : [createDefaultSheetMatrix()];
  while (sheets.length < names.length) sheets.push(createDefaultSheetMatrix());
  while (names.length < sheets.length) names.push(`Hoja ${names.length + 1}`);

  const normalizedSheets = sheets.map((sheet) => {
    const columns = Math.max(1, sheet[0]?.length || 4);
    const rows = sheet.length > 0 ? sheet : createDefaultSheetMatrix(columns);
    return rows.map((row, rowIndex) => {
      const clone = [...row];
      while (clone.length < columns) clone.push(rowIndex === 0 ? `Columna ${getSpreadsheetColumnLabel(clone.length)}` : '');
      return clone;
    });
  });

  return {
    sheetNames: names,
    sheets: normalizedSheets,
    activeSheetIndex: Math.min(Math.max(0, workbook.activeSheetIndex || 0), normalizedSheets.length - 1),
  };
}

function serializeWorkbookContent(workbook: SpreadsheetWorkbook) {
  return `${WORKBOOK_CONTENT_PREFIX}${JSON.stringify(normalizeWorkbook(workbook))}`;
}

function deserializeWorkbookContent(content?: string | null): SpreadsheetWorkbook | null {
  if (!content || !content.startsWith(WORKBOOK_CONTENT_PREFIX)) return null;
  const payload = content.slice(WORKBOOK_CONTENT_PREFIX.length);
  try {
    return normalizeWorkbook(JSON.parse(payload) as SpreadsheetWorkbook);
  } catch {
    return null;
  }
}

function parseWorkbookFromSheetEditor(sheetRoot: HTMLElement): SpreadsheetWorkbook {
  const storage = sheetRoot.querySelector<HTMLTextAreaElement>('.sheet-workbook-storage');
  if (storage?.value) {
    try {
      const parsed = JSON.parse(storage.value) as SpreadsheetWorkbook;
      return normalizeWorkbook(parsed);
    } catch {
      // Fallback to parsing visible grid if storage is malformed.
    }
  }

  const grid = sheetRoot.querySelector<HTMLElement>('.sheet-grid');
  const matrix = grid ? readSheetMatrix(grid) : createDefaultSheetMatrix();
  return normalizeWorkbook({
    sheetNames: ['Hoja 1'],
    sheets: [matrix],
    activeSheetIndex: 0,
  });
}

function applySpreadsheetSelection(sheetRoot: HTMLElement, rowA: number, colA: number, rowB: number, colB: number) {
  const minRow = Math.min(rowA, rowB);
  const maxRow = Math.max(rowA, rowB);
  const minCol = Math.min(colA, colB);
  const maxCol = Math.max(colA, colB);

  sheetRoot.querySelectorAll<HTMLElement>('.sheet-cell').forEach((cell) => {
    const row = Number(cell.dataset.row || 0);
    const column = Number(cell.dataset.column || 0);
    const selected = row >= minRow && row <= maxRow && column >= minCol && column <= maxCol;
    cell.classList.toggle('selected', selected);
  });
}

function getSelectedSheetRange(sheetRoot: HTMLElement) {
  const selectedCells = Array.from(sheetRoot.querySelectorAll<HTMLElement>('.sheet-cell.selected'));
  if (selectedCells.length === 0) {
    return null;
  }

  const rows = selectedCells.map((cell) => Number(cell.dataset.row || 0));
  const cols = selectedCells.map((cell) => Number(cell.dataset.column || 0));

  return {
    minRow: Math.min(...rows),
    maxRow: Math.max(...rows),
    minCol: Math.min(...cols),
    maxCol: Math.max(...cols),
  };
}

function renderSpreadsheetWorkbook(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  spreadsheetWorkbook = normalizeWorkbook(spreadsheetWorkbook);

  const tabs = document.createElement('div');
  tabs.className = 'sheet-tabs';
  spreadsheetWorkbook.sheetNames.forEach((name, index) => {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.dataset.sheetIndex = String(index);
    if (index === spreadsheetWorkbook!.activeSheetIndex) tab.classList.add('active');
    tab.textContent = name;
    tabs.append(tab);
  });
  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.dataset.sheetAdd = 'true';
  addButton.textContent = '+ Nueva hoja';
  tabs.append(addButton);

  const deleteButton = document.createElement('button');
  deleteButton.type = 'button';
  deleteButton.dataset.sheetDelete = 'true';
  deleteButton.textContent = 'Eliminar hoja';
  tabs.append(deleteButton);

  const hint = document.createElement('p');
  hint.className = 'sheet-selection-help';
  hint.textContent = 'Mantén Shift o arrastra para seleccionar varias celdas.';

  const grid = document.createElement('div');
  grid.className = 'sheet-grid';
  renderSheetMatrix(grid, spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex]);

  const storage = document.createElement('textarea');
  storage.className = 'sheet-workbook-storage';
  storage.hidden = true;
  storage.value = JSON.stringify(spreadsheetWorkbook);

  sheetRoot.innerHTML = '';
  sheetRoot.append(tabs, hint, grid, storage);
}

function syncWorkbookFromSheetEditor(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  const grid = sheetRoot.querySelector<HTMLElement>('.sheet-grid');
  if (!grid) return;
  spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] = readSheetMatrix(grid);
  const storage = sheetRoot.querySelector<HTMLTextAreaElement>('.sheet-workbook-storage');
  if (storage) storage.value = JSON.stringify(spreadsheetWorkbook);
}

function initializeSpreadsheetWorkbook(sheetRoot: HTMLElement, savedContent?: string | null) {
  const fromSavedContent = deserializeWorkbookContent(savedContent);
  spreadsheetWorkbook = fromSavedContent || parseWorkbookFromSheetEditor(sheetRoot);
  renderSpreadsheetWorkbook(sheetRoot);
}

function renderSheetMatrix(sheetGrid: HTMLElement, rows: string[][]) {
  const columnCount = Math.max(1, rows[0]?.length || 0);
  const normalizedRows = rows.map((row, rowIndex) => {
    const clone = [...row];
    while (clone.length < columnCount) clone.push(rowIndex === 0 ? `Columna ${getSpreadsheetColumnLabel(clone.length)}` : '');
    return clone;
  });

  sheetGrid.innerHTML = '';

  const corner = document.createElement('span');
  corner.className = 'sheet-corner';
  sheetGrid.append(corner);

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const columnHeader = document.createElement('b');
    columnHeader.className = 'sheet-column-header';
    columnHeader.textContent = getSpreadsheetColumnLabel(columnIndex);
    sheetGrid.append(columnHeader);
  }

  normalizedRows.forEach((row, rowIndex) => {
    const rowHeader = document.createElement('b');
    rowHeader.className = 'sheet-row-header';
    rowHeader.textContent = String(rowIndex + 1);
    sheetGrid.append(rowHeader);

    row.forEach((cellValue, columnIndex) => {
      const cell = document.createElement('span');
      cell.className = `sheet-cell${rowIndex === 0 ? ' sheet-header-cell' : ''}`;
      cell.contentEditable = 'true';
      cell.spellcheck = false;
      cell.dataset.row = String(rowIndex);
      cell.dataset.column = String(columnIndex);
      cell.innerHTML = cellValue || (rowIndex === 0 ? `Columna ${getSpreadsheetColumnLabel(columnIndex)}` : '');
      sheetGrid.append(cell);
    });
  });
}

function appendSheetRow(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  const activeSheet = spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] || createDefaultSheetMatrix();
  const columnCount = Math.max(1, activeSheet[0]?.length || 4);
  activeSheet.push(Array.from({ length: columnCount }, () => ''));
  spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] = activeSheet;
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function appendSheetColumn(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  const activeSheet = spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] || createDefaultSheetMatrix();
  const nextIndex = activeSheet[0]?.length || 0;
  activeSheet.forEach((row, rowIndex) => {
    row.push(rowIndex === 0 ? `Columna ${getSpreadsheetColumnLabel(nextIndex)}` : '');
  });
  spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] = activeSheet;
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function appendSpreadsheetSheet(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  const nextIndex = spreadsheetWorkbook.sheetNames.length + 1;
  spreadsheetWorkbook.sheetNames.push(`Hoja ${nextIndex}`);
  spreadsheetWorkbook.sheets.push(createDefaultSheetMatrix());
  spreadsheetWorkbook.activeSheetIndex = spreadsheetWorkbook.sheetNames.length - 1;
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function deleteActiveSpreadsheetSheet(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  if (spreadsheetWorkbook.sheets.length <= 1) {
    spreadsheetWorkbook.sheets[0] = createDefaultSheetMatrix();
    spreadsheetWorkbook.sheetNames[0] = 'Hoja 1';
    spreadsheetWorkbook.activeSheetIndex = 0;
  } else {
    spreadsheetWorkbook.sheets.splice(spreadsheetWorkbook.activeSheetIndex, 1);
    spreadsheetWorkbook.sheetNames.splice(spreadsheetWorkbook.activeSheetIndex, 1);
    spreadsheetWorkbook.activeSheetIndex = Math.max(0, Math.min(spreadsheetWorkbook.activeSheetIndex, spreadsheetWorkbook.sheets.length - 1));
  }
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function deleteSheetRows(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  const range = getSelectedSheetRange(sheetRoot);
  if (!range) return;

  const activeSheet = spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] || createDefaultSheetMatrix();
  const start = Math.max(1, range.minRow);
  const end = Math.max(start, range.maxRow);
  const rowsToDelete = end - start + 1;

  if (activeSheet.length <= 2) {
    for (let i = 1; i < activeSheet.length; i += 1) {
      activeSheet[i] = activeSheet[i].map(() => '');
    }
  } else {
    activeSheet.splice(start, rowsToDelete);
    if (activeSheet.length === 1) {
      activeSheet.push(activeSheet[0].map(() => ''));
    }
  }

  spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] = activeSheet;
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function deleteSheetColumns(sheetRoot: HTMLElement) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  const range = getSelectedSheetRange(sheetRoot);
  if (!range) return;

  const activeSheet = spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] || createDefaultSheetMatrix();
  const currentColumns = Math.max(1, activeSheet[0]?.length || 1);
  if (currentColumns <= 1) return;

  const start = Math.max(0, range.minCol);
  const end = Math.max(start, range.maxCol);
  const colsToDelete = Math.min(currentColumns - 1, end - start + 1);

  activeSheet.forEach((row, rowIndex) => {
    row.splice(start, colsToDelete);
    if (row.length === 0) {
      row.push(rowIndex === 0 ? `Columna ${getSpreadsheetColumnLabel(0)}` : '');
    }
  });

  spreadsheetWorkbook.sheets[spreadsheetWorkbook.activeSheetIndex] = activeSheet;
  renderSpreadsheetWorkbook(sheetRoot);
  scheduleDocumentAutosave();
}

function switchSpreadsheetSheet(sheetRoot: HTMLElement, index: number) {
  if (!spreadsheetWorkbook) return;
  syncWorkbookFromSheetEditor(sheetRoot);
  spreadsheetWorkbook.activeSheetIndex = Math.min(Math.max(0, index), spreadsheetWorkbook.sheets.length - 1);
  renderSpreadsheetWorkbook(sheetRoot);
}

function sanitizeFilename(value: string) {
  const sanitized = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9-_\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  return sanitized || 'documento';
}

function buildCsvFromMatrix(matrix: string[][]) {
  return matrix
    .map((row) => row.map((cell) => {
      const text = (cell || '').replace(/<[^>]+>/g, '').replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
      const escaped = text.replace(/"/g, '""');
      return `"${escaped}"`;
    }).join(','))
    .join('\n');
}

function triggerFileDownload(fileName: string, content: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

function setDocumentEditorSaveState(message: string) {
  const stateLabel = document.querySelector<HTMLElement>('#editor-save-state');
  if (stateLabel) stateLabel.textContent = message;
}

async function saveCurrentDocumentEditor() {
  if (documentSaving) return;
  if (!state.currentWorkspace || !state.currentDocumentId) return;

  const modal = document.querySelector<HTMLDialogElement>('#document-editor-modal');
  if (!modal) return;
  const editorWord = modal.querySelector<HTMLElement>('#editor-word');
  const editorSheet = modal.querySelector<HTMLElement>('#editor-sheet');
  const statusField = modal.querySelector<HTMLSelectElement>('#editor-status');
  const currentDoc = state.documents.find((doc) => doc.id === state.currentDocumentId);
  if (!editorWord || !editorSheet || !statusField || !currentDoc) return;

  const spreadsheetMode = !editorSheet.hidden;
  if (spreadsheetMode) {
    syncWorkbookFromSheetEditor(editorSheet);
  }
  const content = spreadsheetMode
    ? (spreadsheetWorkbook ? serializeWorkbookContent(spreadsheetWorkbook) : editorSheet.innerHTML.trim())
    : editorWord.innerHTML.trim();
  const status = statusField.value || currentDoc.status || 'Por revisar';
  const noContentChanges = (currentDoc.content || '').trim() === content;
  const noStatusChanges = (currentDoc.status || '') === status;
  if (noContentChanges && noStatusChanges) {
    setDocumentEditorSaveState('Sin cambios por guardar');
    return;
  }

  try {
    documentSaving = true;
    setDocumentEditorSaveState('Guardando...');
    const updated = await updateDocument(state.currentWorkspace.id, state.currentDocumentId, {
      content,
      status,
    });
    if (!updated?.id) {
      throw new Error('No se recibio el documento actualizado desde la API.');
    }
    state.documents = state.documents.map((doc) => (doc.id === updated.id ? { ...doc, ...updated } : doc));
    renderDocumentTable(state.documents);
    updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, state.documents.length);
    setDocumentEditorSaveState('Guardado automaticamente');
  } catch (error) {
    console.error('Error al guardar documento:', error);
    setDocumentEditorSaveState('Error al guardar');
  } finally {
    documentSaving = false;
  }
}

function scheduleDocumentAutosave() {
  setDocumentEditorSaveState('Guardando...');
  if (documentAutosaveTimer) {
    window.clearTimeout(documentAutosaveTimer);
  }
  documentAutosaveTimer = window.setTimeout(() => {
    void saveCurrentDocumentEditor();
  }, 600);
}

function setUserProfile(profile: Profile) {
  const avatar = document.querySelector<HTMLElement>('.profile-avatar');
  const topAvatar = document.querySelector<HTMLElement>('.top-avatar');
  const profileName = document.querySelector<HTMLElement>('.profile strong');
  const profileRole = document.querySelector<HTMLElement>('.profile small');
  const greeting = document.querySelector<HTMLElement>('#dashboard-greeting');
  const initials = profile.full_name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join('');
  const firstName = profile.full_name.split(' ')[0] || profile.full_name;

  if (avatar) avatar.textContent = initials;
  if (topAvatar) topAvatar.textContent = initials;
  if (profileName) profileName.textContent = profile.full_name;
  if (profileRole) profileRole.textContent = profile.role === 'admin' ? 'Administrador' : 'Colaborador';
  if (greeting) greeting.textContent = `Buenos días, ${firstName}`;
}

function setCurrentWorkspace(workspace: Workspace) {
  const workspaceName = document.querySelector<HTMLElement>('#workspace-name');
  const workspacePlan = document.querySelector<HTMLElement>('#workspace-plan');
  const breadcrumbWorkspace = document.querySelector('.breadcrumb span');

  if (workspaceName) workspaceName.textContent = workspace.name;
  if (workspacePlan) workspacePlan.textContent = workspace.plan;
  if (breadcrumbWorkspace) breadcrumbWorkspace.textContent = workspace.name;
  state.currentWorkspace = workspace;
}

function selectView(id: string) {
  const view = document.getElementById(id);
  if (!view) return;
  document.querySelectorAll('.view').forEach((item) => item.classList.remove('active-view'));
  view.classList.add('active-view');
  document.querySelectorAll<HTMLElement>('.nav-item[data-view]').forEach((item) => item.classList.toggle('active', item.dataset.view === id));
  const labels: Record<string, string> = {
    dashboard: 'Centro de control',
    company: 'Mi empresa',
    processes: 'Procesos',
    'process-detail': 'Gestión del proceso',
    indicators: 'Indicadores',
    'indicator-detail': 'Indicador',
    risks: 'Riesgos',
    'risk-detail': 'Riesgo',
    actions: 'Acciones',
    improvement: 'Mejora continua',
    'improvement-detail': 'Plan de mejora',
    documents: 'Documentos',
    settings: 'Configuración',
    audit: 'Auditorías',
    people: 'Personas',
    roles: 'Roles y cargos',
    ai: 'Atlas AI',
  };
  const crumb = document.querySelector('#crumb');
  if (crumb) crumb.textContent = labels[id] || 'Centro de control';
  if (id === 'risk-detail') {
    renderRiskDetail(state.currentRiskId);
  }
  document.querySelector('.sidebar')?.classList.remove('open');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function setAuthenticated(authenticated: boolean) {
  document.body.classList.toggle('authenticated', authenticated);
  const loginPage = document.querySelector<HTMLElement>('#login-page');
  if (loginPage) loginPage.hidden = authenticated;
}

function renderWorkspaceMenu(workspaces: Workspace[]) {
  const workspaceMenu = document.querySelector<HTMLElement>('#workspace-menu');
  const workspaceTrigger = document.querySelector<HTMLElement>('#workspace-trigger');
  const workspaceName = document.querySelector<HTMLElement>('#workspace-name');
  const workspacePlan = document.querySelector<HTMLElement>('#workspace-plan');
  if (!workspaceMenu || !workspaceTrigger || !workspaceName || !workspacePlan) return;

  workspaceMenu.innerHTML = '<span class="workspace-menu-label">ESPACIOS DE TRABAJO</span>';
  workspaces.forEach((workspace, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'workspace-option';
    button.dataset.workspace = workspace.name;
    button.dataset.workspacePlan = workspace.plan;
    button.innerHTML = `<span class="workspace-avatar">${workspace.name.slice(0, 1).toUpperCase()}</span><span><strong>${workspace.name}</strong><small>${workspace.plan}</small></span>`;
    button.addEventListener('click', () => {
      workspaceName.textContent = workspace.name;
      workspacePlan.textContent = workspace.plan;
      document.querySelector('.breadcrumb span')!.textContent = workspace.name;
      state.currentWorkspace = workspace;
      loadWorkspaceData(workspace.id);
    });
    workspaceMenu.appendChild(button);

    if (index === 0) {
      workspaceName.textContent = workspace.name;
      workspacePlan.textContent = workspace.plan;
      document.querySelector('.breadcrumb span')!.textContent = workspace.name;
      state.currentWorkspace = workspace;
    }
  });
}

function renderProcessCards(processes: ProcessSummary[]) {
  state.processes = processes;
  recalculateProcessesHealth();
  const list = document.querySelector('#process-list');
  if (!list) return;
  list.innerHTML = state.processes
    .map((process) => `
      <article class="process-card" data-view-target="process-detail" data-process-id="${process.id}">
        <header><span class="process-symbol"><i data-lucide="${process.icon || 'git-branch'}"></i></span><button class="icon-button"><i data-lucide="more-horizontal"></i></button></header>
        <h3>${process.name}</h3><p>${process.description || 'Sin descripción disponible.'}</p>
        <footer><i></i> Salud <strong>${process.health ?? 0}%</strong></footer>
      </article>`)
    .join('');

  const toolbarCount = document.querySelector<HTMLElement>('#toolbar-process-count');
  if (toolbarCount) toolbarCount.textContent = `${processes.length} procesos`;

  list.querySelectorAll<HTMLElement>('[data-view-target]').forEach((card) => {
    card.addEventListener('click', () => {
      const processId = card.dataset.processId || null;
      if (processId) {
        state.currentProcessId = processId;
        state.currentProcessTab = 'Resumen';
        renderProcessDetail(processId, state.currentProcessTab);
      }
      selectView(card.dataset.viewTarget || 'process-detail');
    });
  });

  if (processes.length > 0) {
    state.currentProcessId = state.currentProcessId && processes.some((item) => item.id === state.currentProcessId)
      ? state.currentProcessId
      : processes[0].id;
    renderProcessDetail(state.currentProcessId, state.currentProcessTab);
  }

  populateProcessSelectors(processes);
  renderProcessMap(processes);
  refreshIcons();
}

function resolveProcessType(process: ProcessSummary) {
  const raw = (process.process_type || '').trim().toLowerCase();
  if (raw.includes('estrat')) return 'Estratégico';
  if (raw.includes('misional')) return 'Misional';
  if (raw.includes('apoyo')) return 'Apoyo';
  if (raw.includes('evalu')) return 'Evaluación';

  const desc = (process.description || '').trim().toLowerCase();
  if (desc.includes('estrat')) return 'Estratégico';
  if (desc.includes('misional')) return 'Misional';
  if (desc.includes('apoyo')) return 'Apoyo';
  if (desc.includes('evalu')) return 'Evaluación';

  return 'Misional';
}

function renderProcessMap(processes: ProcessSummary[]) {
  const container = document.querySelector<HTMLElement>('#process-map-container');
  if (!container) return;

  if (processes.length === 0) {
    container.innerHTML = `<div class="empty-state"><strong>Sin procesos registrados.</strong><p>Crea tu primer proceso para visualizar el mapa automáticamente.</p></div>`;
    return;
  }

  const strategic = processes.filter((process) => resolveProcessType(process) === 'Estratégico');
  const misional = processes.filter((process) => resolveProcessType(process) === 'Misional');
  const support = processes.filter((process) => resolveProcessType(process) === 'Apoyo' || resolveProcessType(process) === 'Evaluación');

  const renderRow = (items: ProcessSummary[], className = '') => {
    if (items.length === 0) return `<div class="mini-map-row ${className}"><b>Sin procesos</b></div>`;
    return `<div class="mini-map-row ${className}">${items.slice(0, 4).map((item) => `<b title="${item.owner || 'Sin responsable'}">${item.name}</b>`).join('')}</div>`;
  };

  const strategicTitle = strategic[0]?.name || 'Sin proceso estratégico';
  const supportBlock = support.length > 0
    ? `<i data-lucide="arrow-down"></i>${renderRow(support, 'support')}`
    : '';
  container.innerHTML = `
    <div>
      <span>Dirección</span>
      <b>${strategicTitle}</b>
    </div>
    <i data-lucide="arrow-down"></i>
    ${renderRow(misional)}
    ${supportBlock}
  `;
  refreshIcons();
}

function getProcessTypeLabel(process: ProcessSummary) {
  return resolveProcessType(process).toUpperCase();
}

function normalizeFlowSteps(process: ProcessSummary) {
  if (Array.isArray(process.flow_steps)) return process.flow_steps.filter(Boolean);
  return [] as string[];
}

function renderProcessDetail(processId: string | null, activeTab: 'Resumen' | 'Flujo' | 'Indicadores' | 'Riesgos' | 'Responsables' | 'Documentos' | 'Historial' = 'Resumen') {
  recalculateProcessesHealth();
  const processDetail = document.querySelector<HTMLElement>('#process-detail');
  if (!processDetail) return;

  const process = processId ? state.processes.find((item) => item.id === processId) : undefined;
  const currentProcess = process || state.processes[0];
  if (!currentProcess) return;

  const processIndicators = state.indicators.filter((item) => item.process_id === currentProcess.id);
  const processRisks = state.risks.filter((item) => item.process_id === currentProcess.id);
  const processDocuments = state.documents.filter((item) => {
    const processLabel = (item.process || '').toLowerCase();
    const processName = currentProcess.name.toLowerCase();
    return processLabel.includes(processName) || processName.includes(processLabel);
  });
  const processActions = state.actions.filter((item) => {
    const source = (item.source || '').toLowerCase();
    return source.includes(currentProcess.name.toLowerCase());
  });
  const ownerLower = (currentProcess.owner || '').trim().toLowerCase();
  const processRoles = state.roles.filter((role) => {
    const participation = role.process_participation && typeof role.process_participation === 'object'
      ? role.process_participation
      : {};
    const participates = Boolean(participation[currentProcess.id]);
    return participates
      || (role.purpose || '').toLowerCase().includes(currentProcess.name.toLowerCase())
      || (role.owner || '').toLowerCase() === ownerLower;
  });
  const processRoleNames = new Set(processRoles.map((role) => (role.name || '').trim().toLowerCase()).filter(Boolean));
  const processPeople = state.people.filter((person) => {
    const personRole = (person.position || '').trim().toLowerCase();
    return person.name.toLowerCase() === ownerLower || (personRole && processRoleNames.has(personRole));
  });
  const flowSteps = normalizeFlowSteps(currentProcess);
  const initials = (currentProcess.owner || 'NA')
    .split(' ')
    .filter(Boolean)
    .map((item) => item[0].toUpperCase())
    .slice(0, 2)
    .join('') || 'NA';

  const detailHead = processDetail.querySelector<HTMLElement>('.process-detail-head');
  if (detailHead) {
    const iconEl = detailHead.querySelector<HTMLElement>('.process-symbol i');
    const badge = detailHead.querySelector<HTMLElement>('.eyebrow');
    const title = detailHead.querySelector<HTMLElement>('h1');
    const description = detailHead.querySelector<HTMLElement>('p');
    const statusTag = detailHead.querySelector<HTMLElement>('em.tag');
    if (iconEl) iconEl.setAttribute('data-lucide', currentProcess.icon || 'briefcase-business');
    if (badge) badge.textContent = `PROCESO ${getProcessTypeLabel(currentProcess)} · ${currentProcess.id.slice(0, 8).toUpperCase()}`;
    if (title) title.textContent = currentProcess.name;
    if (description) description.textContent = currentProcess.description || 'Sin descripción definida para este proceso.';
    if (statusTag) statusTag.textContent = `${currentProcess.status || 'Activo'} · ${currentProcess.health ?? 0}% salud`;
  }

  const tabs = processDetail.querySelector<HTMLElement>('#process-tabs');
  if (tabs) {
    const indicatorTab = tabs.querySelector<HTMLButtonElement>('button[data-process-tab="Indicadores"] b');
    const riskTab = tabs.querySelector<HTMLButtonElement>('button[data-process-tab="Riesgos"] b');
    if (indicatorTab) indicatorTab.textContent = String(processIndicators.length);
    if (riskTab) riskTab.textContent = String(processRisks.length);
    tabs.querySelectorAll<HTMLButtonElement>('button[data-process-tab]').forEach((button) => {
      button.classList.toggle('active', button.dataset.processTab === activeTab);
      button.onclick = () => {
        const tab = button.dataset.processTab || 'Resumen';
        state.currentProcessTab = (tab as typeof state.currentProcessTab) || 'Resumen';
        renderProcessDetail(state.currentProcessId, state.currentProcessTab);
      };
    });
  }

  const grid = processDetail.querySelector<HTMLElement>('.process-detail-grid');
  if (!grid) return;

  const indicatorSnapshots = processIndicators.map((indicator) => ({
    indicator,
    snapshot: getIndicatorSnapshot(indicator),
  }));
  const indicatorsOnTarget = indicatorSnapshots.filter((entry) => entry.snapshot.status.toLowerCase().includes('meta')).length;
  const indicatorsPending = indicatorSnapshots.filter((entry) => entry.snapshot.status.toLowerCase().includes('pendiente')).length;
  const criticalRisks = processRisks.filter((risk) => {
    const status = (risk.status || '').toLowerCase();
    const impact = (risk.impact || '').toLowerCase();
    return status.includes('crítico') || impact.includes('alto');
  }).length;

  const flowContent = flowSteps.length > 0
    ? flowSteps.map((step, index) => `${index > 0 ? '<i data-lucide="arrow-right"></i>' : ''}<span class="${index === flowSteps.length - 1 ? 'done-step' : ''}">${step}</span>`).join('')
    : '<span>Definir flujo del proceso</span>';

  const summaryContent = `
      <div class="canvas-row">
        <div>
          <span>Objetivo del proceso</span>
          <p>${currentProcess.objective || 'Sin objetivo definido.'}</p>
        </div>
        <div>
          <span>Responsable principal</span>
          <p><b class="inline-avatar teal">${initials}</b> ${currentProcess.owner || 'No aplica'}</p>
        </div>
      </div>
      <div class="canvas-row">
        <div>
          <span>Tipo y estado</span>
          <p>${currentProcess.process_type || resolveProcessType(currentProcess)} · ${currentProcess.status || 'Activo'}</p>
        </div>
        <div>
          <span>Salud del proceso</span>
          <p>${currentProcess.health ?? 0}%</p>
        </div>
      </div>
      <div class="canvas-row">
        <div>
          <span>Entradas</span>
          <p>${currentProcess.inputs || 'Sin entradas registradas.'}</p>
        </div>
        <div>
          <span>Salidas</span>
          <p>${currentProcess.outputs || 'Sin salidas registradas.'}</p>
        </div>
      </div>
      <div class="canvas-row">
        <div>
          <span>Alcance</span>
          <p>${currentProcess.scope || 'Sin alcance definido.'}</p>
        </div>
        <div>
          <span>ID del proceso</span>
          <p>${currentProcess.id}</p>
        </div>
      </div>
  `;

  const flowTabContent = `
    <div class="process-kpi-grid">
      <article><span>Pasos definidos</span><strong>${flowSteps.length}</strong><small>En el flujo operativo</small></article>
      <article><span>Indicadores vinculados</span><strong>${processIndicators.length}</strong><small>Miden desempeño del proceso</small></article>
      <article><span>Riesgos del flujo</span><strong>${processRisks.length}</strong><small>${criticalRisks} críticos</small></article>
      <article><span>Roles operando</span><strong>${processRoles.length}</strong><small>Con participación en el proceso</small></article>
    </div>
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Flujo operativo detallado</h3>
          <p>Secuencia de ejecución y puntos de control del proceso.</p>
        </div>
      </div>
      <div class="workflow">
        ${flowSteps.length > 0
          ? flowSteps.map((step, index) => `<div><b>${String(index + 1).padStart(2, '0')}</b><span><strong>${step}</strong><small>${index === 0 ? 'Inicio del proceso' : index === flowSteps.length - 1 ? 'Cierre del flujo' : 'Actividad intermedia'}</small></span><em class="tag neutral">Paso ${index + 1}</em></div>`).join('')
          : '<div><b>01</b><span><strong>Sin flujo documentado</strong><small>Agrega pasos desde Editar ficha para habilitar la ruta operativa.</small></span><em class="tag review">Pendiente</em></div>'}
      </div>
    </article>
  `;

  const indicatorsTabContent = `
    <div class="process-kpi-grid">
      <article><span>Total indicadores</span><strong>${processIndicators.length}</strong><small>Ligados a este proceso</small></article>
      <article><span>En meta</span><strong>${indicatorsOnTarget}</strong><small>Con desempeño esperado</small></article>
      <article><span>Pendientes</span><strong>${indicatorsPending}</strong><small>Sin medición o seguimiento</small></article>
      <article><span>Último valor</span><strong>${indicatorSnapshots[0]?.snapshot.currentValue || '---'}</strong><small>Indicador principal</small></article>
    </div>
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Indicadores del proceso</h3>
          <p>Haz clic en un indicador para ir directo a su ficha y evolución.</p>
        </div>
      </div>
      <div class="embedded-list">
        ${indicatorSnapshots.length > 0
          ? indicatorSnapshots.map(({ indicator, snapshot }) => `<button class="linked-indicator" type="button" data-process-link="indicator-detail" data-indicator-id="${indicator.id}"><span><strong>${indicator.name}</strong><small>${indicator.periodicity || 'Sin periodicidad'} · Meta ${snapshot.goalLabel}</small></span><b>${snapshot.currentValue}</b><em class="tag ${snapshot.statusClass}">${snapshot.status}</em><i data-lucide="arrow-up-right"></i></button>`).join('')
          : '<div class="empty-state"><strong>Sin indicadores asociados.</strong><p>Crea indicadores y asígnalos a este proceso para seguimiento en tiempo real.</p></div>'}
      </div>
    </article>
  `;

  const risksTabContent = `
    <div class="process-kpi-grid">
      <article><span>Riesgos activos</span><strong>${processRisks.length}</strong><small>Registrados en el proceso</small></article>
      <article><span>Críticos / altos</span><strong>${criticalRisks}</strong><small>Prioridad de tratamiento</small></article>
      <article><span>En control</span><strong>${processRisks.filter((risk) => (risk.status || '').toLowerCase().includes('control')).length}</strong><small>Con acciones implementadas</small></article>
      <article><span>Próxima revisión</span><strong>${processRisks[0]?.review_date ? formatDisplayDate(processRisks[0].review_date) : 'Sin fecha'}</strong><small>Según matriz de riesgos</small></article>
    </div>
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Mapa de riesgos del proceso</h3>
          <p>Consulta detalle y abre la matriz para análisis y tratamiento.</p>
        </div>
      </div>
      <div class="embedded-list">
        ${processRisks.length > 0
          ? processRisks.map((risk) => `<button type="button" data-process-link="risks"><span><strong>${risk.name}</strong><small>${risk.causes || 'Sin causa registrada'}</small></span><b>${risk.impact || 'N/D'}</b><em class="tag ${(risk.status || '').toLowerCase().includes('control') ? 'done' : (risk.impact || '').toLowerCase().includes('alto') ? 'high' : 'medium'}">${risk.status || 'Abierto'}</em><i data-lucide="arrow-up-right"></i></button>`).join('')
          : '<div class="empty-state"><strong>Sin riesgos asociados.</strong><p>Registra riesgos del proceso para activar controles y seguimiento.</p></div>'}
      </div>
    </article>
  `;

  const responsablesTabContent = `
    <div class="process-kpi-grid">
      <article><span>Dueño del proceso</span><strong>${currentProcess.owner || 'No aplica'}</strong><small>Responsable principal</small></article>
      <article><span>Personas vinculadas</span><strong>${processPeople.length}</strong><small>Con rol o asignación directa</small></article>
      <article><span>Cargos relacionados</span><strong>${processRoles.length}</strong><small>Participan en la operación</small></article>
      <article><span>Salud de coordinación</span><strong>${processPeople.length > 0 && processRoles.length > 0 ? 'Alta' : 'Media'}</strong><small>Basada en estructura actual</small></article>
    </div>
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Equipo y responsabilidades</h3>
          <p>Entra directo al módulo de Personas o Roles para editar asignaciones.</p>
        </div>
      </div>
      <div class="canvas-row">
        <div>
          <span>Personas vinculadas</span>
          <p>${processPeople.length > 0 ? processPeople.map((person) => `${person.name}${person.position ? ` (${person.position})` : ''}`).join(', ') : 'No hay personas vinculadas a este proceso.'}</p>
          <button class="text-button" type="button" data-process-link="people">Ir a Personas <i data-lucide="arrow-right"></i></button>
        </div>
        <div>
          <span>Cargos relacionados</span>
          <p>${processRoles.length > 0 ? processRoles.map((role) => role.name).join(', ') : 'No hay cargos vinculados en este momento.'}</p>
          <button class="text-button" type="button" data-process-link="roles">Ir a Roles y cargos <i data-lucide="arrow-right"></i></button>
        </div>
      </div>
    </article>
  `;

  const documentsTabContent = `
    <div class="process-kpi-grid">
      <article><span>Documentos</span><strong>${processDocuments.length}</strong><small>Relacionados al proceso</small></article>
      <article><span>Vigentes</span><strong>${processDocuments.filter((doc) => (doc.status || '').toLowerCase().includes('vigen')).length}</strong><small>Alineados con operación</small></article>
      <article><span>Por revisar</span><strong>${processDocuments.filter((doc) => !(doc.status || '').toLowerCase().includes('vigen')).length}</strong><small>Requieren actualización</small></article>
      <article><span>Última actualización</span><strong>${processDocuments[0]?.updated_at ? formatDisplayDate(processDocuments[0].updated_at) : 'Sin fecha'}</strong><small>Documento más reciente</small></article>
    </div>
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Documentación controlada</h3>
          <p>Accede al módulo documental para versionar y mantener trazabilidad.</p>
        </div>
      </div>
      <div class="embedded-list">
        ${processDocuments.length > 0
          ? processDocuments.map((doc) => `<button type="button" data-process-link="documents"><span><strong>${doc.name}</strong><small>Versión ${doc.version || 'N/D'} · ${doc.format || 'Documento'}</small></span><b>${formatDisplayDate(doc.updated_at)}</b><em class="tag ${(doc.status || '').toLowerCase().includes('vigen') ? 'done' : 'review'}">${doc.status || 'Pendiente'}</em><i data-lucide="arrow-up-right"></i></button>`).join('')
          : '<div class="empty-state"><strong>Sin documentos ligados.</strong><p>Asocia formatos, procedimientos o políticas a este proceso.</p></div>'}
      </div>
    </article>
  `;

  const historyItems = [
    ...processActions.map((action) => ({ date: action.created_at || '', title: `Acción: ${action.title}`, detail: action.status || 'Sin estado', type: 'action', view: 'actions' as const })),
    ...processRisks.map((risk) => ({ date: risk.created_at || '', title: `Riesgo: ${risk.name}`, detail: risk.status || 'Sin estado', type: 'risk', view: 'risks' as const })),
    ...processIndicators.map((indicator) => ({ date: indicator.created_at || '', title: `Indicador: ${indicator.name}`, detail: indicator.status || 'Sin estado', type: 'indicator', view: 'indicator-detail' as const, indicatorId: indicator.id })),
    ...processDocuments.map((doc) => ({ date: doc.updated_at || '', title: `Documento: ${doc.name}`, detail: doc.status || 'Sin estado', type: 'document', view: 'documents' as const })),
  ]
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 10);

  const historyTabContent = `
    <article class="panel process-module-panel">
      <div class="panel-header">
        <div>
          <h3>Historial integrado del proceso</h3>
          <p>Eventos recientes en indicadores, riesgos, acciones y documentos.</p>
        </div>
      </div>
      <div class="workflow">
        ${historyItems.length > 0
          ? historyItems.map((item, index) => {
            const indicatorId = 'indicatorId' in item ? item.indicatorId : null;
            return `<button type="button" data-process-link="${item.view}" ${indicatorId ? `data-indicator-id="${indicatorId}"` : ''}><b>${String(index + 1).padStart(2, '0')}</b><span><strong>${item.title}</strong><small>${item.detail}</small></span><em class="tag neutral">${formatDisplayDate(item.date)}</em></button>`;
          }).join('')
          : '<div><b>01</b><span><strong>Sin eventos registrados</strong><small>El historial se alimentará cuando haya acciones, riesgos, documentos e indicadores vinculados.</small></span><em class="tag review">Pendiente</em></div>'}
      </div>
    </article>
  `;

  const detailMainContentMap = {
    Resumen: summaryContent,
    Flujo: flowTabContent,
    Indicadores: indicatorsTabContent,
    Riesgos: risksTabContent,
    Responsables: responsablesTabContent,
    Documentos: documentsTabContent,
    Historial: historyTabContent,
  } as const;

  const detailMainContent = detailMainContentMap[activeTab] || detailMainContentMap.Resumen;

  grid.innerHTML = `
    <article class="panel process-canvas">
      <div class="panel-header">
        <div>
          <h3>${activeTab === 'Resumen' ? 'Ficha ejecutiva del proceso' : activeTab}</h3>
          <p>${activeTab === 'Resumen' ? 'Vista integral para operación, control y mejora.' : 'Información operativa conectada con su módulo correspondiente.'}</p>
        </div>
      </div>
      ${detailMainContent}
      <div class="flow-strip">${flowContent}</div>
    </article>
    <aside class="detail-side process-side-stack">
      <article class="panel mini-detail">
        <h3>Ruta rápida del proceso</h3>
        <div><span>Flujo</span><b>${flowSteps.length}</b><em class="tag neutral">pasos</em></div>
        <div><span>Indicadores</span><b>${processIndicators.length}</b><em class="tag ${indicatorsOnTarget > 0 ? 'done' : 'review'}">${indicatorsOnTarget} en meta</em></div>
        <div><span>Riesgos</span><b>${processRisks.length}</b><em class="tag ${criticalRisks > 0 ? 'high' : 'done'}">${criticalRisks} críticos</em></div>
        <button type="button" data-process-tab-target="Flujo">Ir a Flujo <i data-lucide="arrow-right"></i></button>
        <button type="button" data-process-tab-target="Indicadores">Ir a Indicadores <i data-lucide="arrow-right"></i></button>
        <button type="button" data-process-tab-target="Riesgos">Ir a Riesgos <i data-lucide="arrow-right"></i></button>
      </article>
      <article class="panel mini-detail">
        <h3>Navegación por módulo</h3>
        <div><span>Responsables</span><b>${processPeople.length}</b><em class="tag neutral">personas</em></div>
        <div><span>Documentos</span><b>${processDocuments.length}</b><em class="tag neutral">controlados</em></div>
        <div><span>Acciones</span><b>${processActions.length}</b><em class="tag neutral">de mejora</em></div>
        <button type="button" data-process-link="roles">Ir a Roles y cargos <i data-lucide="arrow-right"></i></button>
        <button type="button" data-process-link="people">Ir a Personas <i data-lucide="arrow-right"></i></button>
        <button type="button" data-process-link="documents">Ir a Documentos <i data-lucide="arrow-right"></i></button>
      </article>
      <article class="panel mini-detail">
        <h3>Siguiente foco recomendado</h3>
        <div><span>Tab activo</span><b>${activeTab}</b><em class="tag review">contexto</em></div>
        <div><span>Prioridad</span><b>${criticalRisks > 0 ? 'Riesgos' : indicatorsPending > 0 ? 'Indicadores' : 'Seguimiento'}</b><em class="tag ${criticalRisks > 0 ? 'high' : indicatorsPending > 0 ? 'review' : 'done'}">enfoque</em></div>
        <button type="button" data-process-tab-target="Historial">Ir a Historial <i data-lucide="arrow-right"></i></button>
        <button type="button" data-process-link="actions">Ir a Acciones <i data-lucide="arrow-right"></i></button>
      </article>
    </aside>
  `;

  grid.querySelectorAll<HTMLButtonElement>('[data-process-tab]').forEach((button) => {
    button.onclick = () => {
      const tab = button.dataset.processTab || 'Resumen';
      state.currentProcessTab = (tab as typeof state.currentProcessTab) || 'Resumen';
      renderProcessDetail(state.currentProcessId, state.currentProcessTab);
    };
  });
  grid.querySelectorAll<HTMLButtonElement>('[data-view-target]').forEach((button) => {
    button.onclick = () => {
      selectView(button.dataset.viewTarget || 'dashboard');
    };
  });
  grid.querySelectorAll<HTMLButtonElement>('[data-process-tab-target]').forEach((button) => {
    button.onclick = () => {
      const tab = button.dataset.processTabTarget as typeof state.currentProcessTab | undefined;
      if (!tab) return;
      state.currentProcessTab = tab;
      renderProcessDetail(state.currentProcessId, state.currentProcessTab);
    };
  });
  grid.querySelectorAll<HTMLButtonElement>('[data-process-link]').forEach((button) => {
    button.onclick = () => {
      const target = button.dataset.processLink;
      if (!target) return;
      if (target === 'indicator-detail') {
        const indicatorId = button.dataset.indicatorId;
        if (!indicatorId) return;
        state.currentIndicatorId = indicatorId;
        renderIndicatorDetail(indicatorId);
        selectView('indicator-detail');
        return;
      }
      selectView(target);
    };
  });
  refreshIcons();
}

function populateProcessSelectors(processes: ProcessSummary[]) {
  const selectors = [
    document.querySelector<HTMLSelectElement>('#indicator-process'),
    document.querySelector<HTMLSelectElement>('#risk-process'),
  ];

  selectors.forEach((select) => {
    if (!select) return;
    const currentValue = select.value;
    const defaultOption = '<option value="">Selecciona un proceso</option>';
    const options = processes.map((process) => `<option value="${process.id}">${process.name}</option>`).join('');
    select.innerHTML = `${defaultOption}${options}`;
    if (currentValue && processes.some((item) => item.id === currentValue)) {
      select.value = currentValue;
    } else if (state.currentProcessId && processes.some((item) => item.id === state.currentProcessId)) {
      select.value = state.currentProcessId;
    }
  });

  populateActionProcessOptions();
}

function populateActionProcessOptions(selectedProcess?: string | null) {
  const processSelect = document.querySelector<HTMLSelectElement>('#action-source');
  if (!processSelect) return;
  const preserved = selectedProcess !== undefined ? selectedProcess : processSelect.value || '';
  const options = ['<option value="">Sin proceso</option>', ...state.processes.map((process) => `<option value="${process.name}">${process.name}</option>`)];
  processSelect.innerHTML = options.join('');
  processSelect.value = preserved && state.processes.some((process) => process.name === preserved) ? preserved : '';
}

function populateActionAssigneeOptions(selectedAssignee?: string | null) {
  const assigneeSelect = document.querySelector<HTMLSelectElement>('#action-assignee');
  if (!assigneeSelect) return;
  const preserved = selectedAssignee !== undefined ? selectedAssignee : assigneeSelect.value || '';
  const options = ['<option value="">No aplica</option>', ...state.people.map((person) => `<option value="${person.name}">${person.name}</option>`)];
  if (preserved && !state.people.some((person) => person.name === preserved)) {
    options.push(`<option value="${preserved}">${preserved}</option>`);
  }
  assigneeSelect.innerHTML = options.join('');
  assigneeSelect.value = preserved;
}

function renderCompanySection(companies: Company[]) {
  const company = companies[0];
  state.currentCompany = company ?? null;
  if (!company) return;

  const companyLogo = document.querySelector<HTMLElement>('.company-logo');
  const companyId = document.querySelector<HTMLElement>('#company-id-display');
  const companyName = document.querySelector<HTMLElement>('#company-name-display');
  const companyLocation = document.querySelector<HTMLElement>('#company-location-display');
  const companyPeople = document.querySelector<HTMLElement>('#company-meta-people');
  const companyProcesses = document.querySelector<HTMLElement>('#company-meta-processes');
  const companyImplementation = document.querySelector<HTMLElement>('#company-meta-implementation');

  const processCount = state.processes.length;
  const peopleCount = state.people.length;
  const averageHealth = processCount > 0 ? Math.round(state.processes.reduce((sum, item) => sum + (item.health || 0), 0) / processCount) : 0;

  if (companyLogo) companyLogo.textContent = company.short_name.slice(0, 1).toUpperCase();
  if (companyId) companyId.textContent = `${company.short_name} · NIT ${company.nit || 'N/A'}`;
  if (companyName) companyName.textContent = company.legal_name;
  if (companyLocation) companyLocation.textContent = `${company.sector || 'Sin sector definido'} · ${company.city || 'Sin ciudad definida'}${company.company_size ? ` · ${company.company_size}` : ''}`;
  if (companyPeople) companyPeople.textContent = String(peopleCount);
  if (companyProcesses) companyProcesses.textContent = String(processCount);
  if (companyImplementation) companyImplementation.textContent = String(averageHealth);
  renderCompanyObjectives(company);
}

function renderCompanyObjectives(company: Company) {
  const objectiveList = document.querySelector<HTMLElement>('#company-objective-list');
  if (!objectiveList) return;
  const objectives = (company.company_objectives || []) as CompanyObjective[];
  if (objectives.length === 0) {
    objectiveList.innerHTML = `<div class="empty-state"><strong>Sin objetivos estratégicos definidos.</strong><p>Agrega objetivos desde el perfil de empresa para que el dashboard refleje la estrategia.</p></div>`;
    return;
  }

  objectiveList.innerHTML = objectives
    .slice(0, 3)
    .map((objective) => `
      <div>
        <i class="objective-dot ${objective.sort_order % 3 === 1 ? 'blue-dot' : objective.sort_order % 3 === 2 ? 'green-dot' : 'purple-dot'}"></i>
        <span>
          <strong>${objective.objective}</strong>
          <small>${objective.expected_result || 'Resultado esperado no definido'}</small>
        </span>
        <b>${objective.annual_target || '---'}</b>
      </div>`)
    .join('');
}

function collectCompanyObjectives(form: HTMLFormElement) {
  const objectiveFields = Array.from(form.querySelectorAll<HTMLDivElement>('.strategic-objective-field'));
  return objectiveFields
    .map((field, index) => {
      const inputs = field.querySelectorAll<HTMLInputElement>('input');
      return {
        objective: inputs[0]?.value.trim() || '',
        expected_result: inputs[1]?.value.trim() || '',
        annual_target: inputs[2]?.value.trim() || '',
        sort_order: index + 1,
      };
    })
    .filter((item) => item.objective.length > 0);
}

function addStrategicObjective(form: HTMLFormElement) {
  const objectiveFields = form.querySelector<HTMLDivElement>('#strategic-objective-fields');
  if (!objectiveFields) return;
  const field = document.createElement('div');
  field.className = 'strategic-objective-field';
  field.innerHTML = `<span class="objective-index"></span><label>Objetivo<input placeholder="Ej. Desarrollo de personas" /></label><label>Resultado esperado<input placeholder="Describe el resultado esperado" /></label><label>Meta anual<input placeholder="Ej. 90" /></label><button type="button" class="icon-button remove-objective" aria-label="Eliminar objetivo"><i data-lucide="trash-2"></i></button>`;
  objectiveFields.append(field);
  renumberStrategicObjectives(form);
  refreshIcons();
}

function renumberStrategicObjectives(form: HTMLFormElement) {
  const objectiveFields = form.querySelectorAll<HTMLSpanElement>('.strategic-objective-field .objective-index');
  objectiveFields.forEach((item, index) => {
    item.textContent = String(index + 1).padStart(2, '0');
  });
}

function attachStrategicObjectiveControls() {
  const companyForm = document.querySelector('#company-form') as HTMLFormElement | null;
  if (!companyForm) return;
  const objectiveFields = companyForm.querySelector<HTMLDivElement>('#strategic-objective-fields');
  const addButton = companyForm.querySelector<HTMLButtonElement>('#add-strategic-objective');
  if (addButton) {
    addButton.addEventListener('click', () => addStrategicObjective(companyForm));
  }
  if (objectiveFields) {
    objectiveFields.addEventListener('click', (event) => {
      const removeButton = (event.target as HTMLElement).closest('.remove-objective');
      if (!removeButton) return;
      const currentFields = objectiveFields.querySelectorAll('.strategic-objective-field');
      if (currentFields.length <= 1) return;
      removeButton.closest('.strategic-objective-field')?.remove();
      renumberStrategicObjectives(companyForm);
    });
  }
}
function formatDisplayDate(dateValue?: string | null) {
  if (!dateValue) return 'Sin fecha';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function parseNumericValue(value?: string | null) {
  if (!value) return null;
  const normalized = value.replace(',', '.').replace(/[^0-9.-]/g, '');
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function getProcessName(processId?: string | null) {
  if (!processId) return 'Sin proceso asociado';
  return state.processes.find((process) => process.id === processId)?.name || 'Proceso no encontrado';
}

function getProcessIcon(processId?: string | null) {
  if (!processId) return 'git-branch';
  return state.processes.find((process) => process.id === processId)?.icon || 'git-branch';
}

function getProcessOwner(processId?: string | null) {
  if (!processId) return 'Sin responsable';
  return state.processes.find((process) => process.id === processId)?.owner || 'Sin responsable';
}

function getIndicatorGoalLabel(indicator: Indicator) {
  return indicator.goal || indicator.code || 'Meta no definida';
}

function getIndicatorMeasurements(indicatorId: string) {
  const measurements = state.measurementsByIndicator[indicatorId] || [];
  return [...measurements].sort((a, b) => {
    const left = new Date(a.date || a.registered_at || 0).getTime();
    const right = new Date(b.date || b.registered_at || 0).getTime();
    return left - right;
  });
}

function toIsoDate(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function computeNextDueFromPeriodicity(periodicity?: string, baseDateValue?: string | null) {
  const baseDate = baseDateValue ? new Date(baseDateValue) : new Date();
  if (Number.isNaN(baseDate.getTime())) return null;

  const period = (periodicity || '').toLowerCase();
  if (period.includes('seman')) {
    baseDate.setDate(baseDate.getDate() + 7);
  } else if (period.includes('trimes')) {
    baseDate.setMonth(baseDate.getMonth() + 3);
  } else {
    baseDate.setMonth(baseDate.getMonth() + 1);
  }

  return toIsoDate(baseDate);
}

function getIndicatorDueDate(indicator: Indicator) {
  if (indicator.next_due) return indicator.next_due;
  const measurements = getIndicatorMeasurements(indicator.id);
  const latestMeasurement = measurements[measurements.length - 1];
  const baseDate = latestMeasurement?.date || latestMeasurement?.registered_at || indicator.created_at;
  return computeNextDueFromPeriodicity(indicator.periodicity, baseDate || null);
}

function getIndicatorSnapshot(indicator: Indicator) {
  const measurements = getIndicatorMeasurements(indicator.id);
  const latestMeasurement = measurements[measurements.length - 1];
  const currentValue = latestMeasurement?.result || indicator.current_value || '---';
  const goalLabel = getIndicatorGoalLabel(indicator);
  const currentNumeric = parseNumericValue(currentValue);
  const goalNumeric = parseNumericValue(goalLabel);
  const calculatedStatus =
    currentNumeric !== null && goalNumeric !== null
      ? currentNumeric >= goalNumeric
        ? 'En meta'
        : 'Bajo meta'
      : latestMeasurement
        ? 'Registrado'
        : 'Pendiente';
  const status = indicator.status && indicator.status !== 'Activo' ? indicator.status : calculatedStatus;
  const progress =
    currentNumeric !== null && goalNumeric !== null && goalNumeric > 0
      ? Math.max(0, Math.min(100, Math.round((currentNumeric / goalNumeric) * 100)))
      : currentNumeric !== null
        ? Math.max(0, Math.min(100, Math.round(currentNumeric)))
        : 8;
  const statusClass =
    status.toLowerCase().includes('meta') || status.toLowerCase().includes('registrado')
      ? 'done'
      : status.toLowerCase().includes('pendiente')
        ? 'review'
        : 'medium';

  return {
    measurements,
    latestMeasurement,
    currentValue,
    goalLabel,
    status,
    statusClass,
    progress,
  };
}

function calculateProcessHealth(processId: string): number {
  const processIndicators = state.indicators.filter((ind) => ind.process_id === processId);
  if (processIndicators.length === 0) {
    return 100;
  }
  let totalCompliance = 0;
  let count = 0;
  for (const ind of processIndicators) {
    const snapshot = getIndicatorSnapshot(ind);
    totalCompliance += snapshot.progress;
    count++;
  }
  return count > 0 ? Math.round(totalCompliance / count) : 100;
}

function recalculateProcessesHealth() {
  for (const process of state.processes) {
    process.health = calculateProcessHealth(process.id);
  }
}

function buildIndicatorCard(indicator: Indicator) {
  const snapshot = getIndicatorSnapshot(indicator);
  return `
    <article class="panel indicator-card">
      <header>
        <span class="process-symbol"><i data-lucide="${getProcessIcon(indicator.process_id)}"></i></span>
        <em class="tag ${snapshot.statusClass}">${snapshot.status}</em>
      </header>
      <span>${getProcessName(indicator.process_id).toUpperCase()}</span>
      <h3>${indicator.name}</h3>
      <div class="metric-line">
        <strong>${snapshot.currentValue}</strong>
        <b>${snapshot.goalLabel}</b>
      </div>
      <div class="metric-bar ${snapshot.statusClass === 'done' ? 'good-bar' : ''}">
        <i style="width:${snapshot.progress}%"></i>
      </div>
      <footer>
        <button type="button" data-indicator-open="${indicator.id}">Ver historial <i data-lucide="arrow-right"></i></button>
        <small>${indicator.periodicity || 'Periodicidad no definida'}</small>
      </footer>
    </article>`;
}

function renderIndicatorTabContent(tab: 'followup' | 'catalog' | 'calendar') {
  state.currentIndicatorTab = tab;
  const content = document.querySelector<HTMLElement>('#indicator-tab-content');
  if (!content) return;

  const indicators = [...state.indicators];
  const sortedByDue = indicators
    .map((indicator) => ({
      indicator,
      dueDate: getIndicatorDueDate(indicator),
    }))
    .filter((entry) => !!entry.dueDate)
    .sort((a, b) => new Date(a.dueDate as string).getTime() - new Date(b.dueDate as string).getTime());
  const pendingMeasurements = indicators.filter((indicator) => getIndicatorMeasurements(indicator.id).length === 0).length;
  const deviatedIndicators = indicators.filter((indicator) => getIndicatorSnapshot(indicator).status.toLowerCase().includes('bajo')).length;
  const onTimeIndicators = indicators.filter((indicator) => {
    const dueDate = getIndicatorDueDate(indicator);
    const due = dueDate ? new Date(dueDate) : null;
    if (!due) return false;
    return due.getTime() >= Date.now();
  }).length;

  if (tab === 'catalog') {
    content.innerHTML = `
      <article class="panel">
        <div class="panel-header">
          <div>
            <h3>Catálogo de indicadores</h3>
            <p>${indicators.length} indicadores activos en el espacio de trabajo.</p>
          </div>
        </div>
        <div class="measurement-history-head">
          <span>Indicador</span>
          <span>Proceso</span>
          <span>Resultado</span>
          <span>Meta</span>
          <span>Próxima medición</span>
          <span></span>
        </div>
        ${indicators
          .map((indicator) => {
            const snapshot = getIndicatorSnapshot(indicator);
            return `<button class="measurement-history-row" type="button" data-indicator-open="${indicator.id}"><span><strong>${indicator.name}</strong><small>${indicator.periodicity || 'Sin periodicidad'}</small></span><span>${getProcessName(indicator.process_id)}</span><b>${snapshot.currentValue}</b><span>${snapshot.goalLabel}</span><span>${formatDisplayDate(getIndicatorDueDate(indicator))}</span><i data-lucide="chevron-right"></i></button>`;
          })
          .join('') || '<div class="empty-state"><strong>No hay indicadores registrados.</strong><p>Crea el primer indicador para iniciar el seguimiento.</p></div>'}
      </article>`;
  } else if (tab === 'calendar') {
    content.innerHTML = `
      <article class="panel measure-panel">
        <div class="panel-header">
          <div>
            <h3>Calendario de medición</h3>
            <p>Agenda real de próximos registros según fecha objetivo.</p>
          </div>
        </div>
        <div class="measurement-list">
          ${sortedByDue
            .map(({ indicator, dueDate }) => {
              const due = new Date(dueDate as string);
              const snapshot = getIndicatorSnapshot(indicator);
              return `<div><span class="measure-date"><b>${String(due.getDate()).padStart(2, '0')}</b><small>${due.toLocaleDateString('es-CO', { month: 'short' }).toUpperCase()}</small></span><span><strong>${indicator.name}</strong><small>${getProcessName(indicator.process_id)} · ${indicator.periodicity || 'Sin periodicidad'}</small></span><span class="measure-progress">Último: <b>${snapshot.currentValue}</b><em class="tag ${snapshot.statusClass}">${snapshot.status}</em></span><button class="primary-button small" type="button" data-open-measurement="${indicator.id}"><i data-lucide="plus"></i>Registrar avance</button></div>`;
            })
            .join('') || '<div class="empty-state"><strong>No hay fechas de medición programadas.</strong><p>Define la próxima fecha en cada indicador para visualizar el calendario.</p></div>'}
        </div>
      </article>`;
  } else {
    content.innerHTML = `
      <div class="indicator-guidance">
        <article><i data-lucide="clock-3"></i><span><strong>${pendingMeasurements} mediciones por registrar</strong><small>Indicadores sin resultados aún.</small></span></article>
        <article><i data-lucide="chart-no-axes-combined"></i><span><strong>${deviatedIndicators} indicadores desviados</strong><small>Requieren análisis y acciones.</small></span></article>
        <article><i data-lucide="circle-check-big"></i><span><strong>${onTimeIndicators} al día</strong><small>Con próxima medición vigente.</small></span></article>
      </div>
      <div class="panel measure-panel">
        <div class="panel-header">
          <div>
            <h3>Próximas mediciones</h3>
            <p>Registra los resultados según la periodicidad acordada.</p>
          </div>
          <button class="text-button" type="button" data-indicator-tab-target="calendar">Ver calendario</button>
        </div>
        <div class="measurement-list">
          ${sortedByDue
            .slice(0, 5)
            .map(({ indicator, dueDate }) => {
              const due = new Date(dueDate as string);
              const snapshot = getIndicatorSnapshot(indicator);
              return `<div><span class="measure-date"><b>${String(due.getDate()).padStart(2, '0')}</b><small>${due.toLocaleDateString('es-CO', { month: 'short' }).toUpperCase()}</small></span><span><strong>${indicator.name}</strong><small>${getProcessName(indicator.process_id)} · ${indicator.periodicity || 'Sin periodicidad'}</small></span><span class="measure-progress">Último: <b>${snapshot.currentValue}</b><em class="tag ${snapshot.statusClass}">${snapshot.status}</em></span><button class="primary-button small" type="button" data-open-measurement="${indicator.id}"><i data-lucide="plus"></i>Registrar avance</button></div>`;
            })
            .join('') || '<div class="empty-state"><strong>No hay mediciones próximas.</strong><p>Actualiza la fecha de próxima medición en los indicadores.</p></div>'}
        </div>
      </div>
      <div class="indicator-cards">
        ${indicators.map((indicator) => buildIndicatorCard(indicator)).join('') || '<div class="empty-state"><strong>Sin indicadores.</strong><p>Crea indicadores para visualizar el tablero de seguimiento.</p></div>'}
      </div>`;
  }

  refreshIcons();
}

function renderIndicatorSection(indicators: Indicator[]) {
  state.indicators = indicators;
  if (!state.currentIndicatorId && indicators[0]) {
    state.currentIndicatorId = indicators[0].id;
  }

  const total = indicators.length;
  const onTarget = indicators.filter((indicator) => getIndicatorSnapshot(indicator).status.toLowerCase().includes('meta')).length;
  const pending = indicators.filter((indicator) => getIndicatorSnapshot(indicator).status.toLowerCase().includes('pendiente')).length;
  const uniqueProcesses = new Set(indicators.map((item) => item.process_id).filter(Boolean)).size;
  const calendarCount = indicators.filter((item) => !!getIndicatorDueDate(item)).length;

  const activeCountEl = document.querySelector<HTMLElement>('#indicator-active-count');
  const activeTextEl = document.querySelector<HTMLElement>('#indicator-active-text');
  const onTargetCountEl = document.querySelector<HTMLElement>('#indicator-on-target-count');
  const onTargetTextEl = document.querySelector<HTMLElement>('#indicator-on-target-text');
  const pendingCountEl = document.querySelector<HTMLElement>('#indicator-pending-count');
  const pendingTextEl = document.querySelector<HTMLElement>('#indicator-pending-text');
  const catalogCountEl = document.querySelector<HTMLElement>('#indicator-tab-catalog-count');
  const calendarCountEl = document.querySelector<HTMLElement>('#indicator-tab-calendar-count');

  if (activeCountEl) activeCountEl.textContent = String(total);
  if (activeTextEl) activeTextEl.textContent = `en ${uniqueProcesses} procesos`;
  if (onTargetCountEl) onTargetCountEl.textContent = String(onTarget);
  if (onTargetTextEl) onTargetTextEl.textContent = total ? `${Math.round((onTarget / total) * 100)}% del total` : '0% del total';
  if (pendingCountEl) pendingCountEl.textContent = String(pending);
  if (pendingTextEl) pendingTextEl.textContent = `en los próximos ${calendarCount} días`;
  if (catalogCountEl) catalogCountEl.textContent = String(total);
  if (calendarCountEl) calendarCountEl.textContent = String(calendarCount);

  const tabs = document.querySelector<HTMLElement>('#indicator-tabs');
  if (tabs) {
    tabs.querySelectorAll<HTMLButtonElement>('button[data-indicator-tab]').forEach((button) => {
      button.classList.toggle('active', button.dataset.indicatorTab === state.currentIndicatorTab);
    });
  }

  renderIndicatorTabContent(state.currentIndicatorTab);
  renderIndicatorDetail(state.currentIndicatorId);
}

function openMeasurementModal(indicatorId: string) {
  const indicator = state.indicators.find((item) => item.id === indicatorId);
  if (!indicator) return;
  state.currentMeasurementIndicatorId = indicatorId;
  const form = document.querySelector('#measurement-modal form') as HTMLFormElement | null;
  if (form) form.reset();

  const title = document.querySelector<HTMLElement>('#measurement-title');
  const period = document.querySelector<HTMLElement>('#measurement-period');
  const target = document.querySelector<HTMLElement>('#measurement-target');
  const dateInput = document.querySelector<HTMLInputElement>('#measurement-date');
  if (title) title.textContent = indicator.name;
  if (period) {
    const dueDate = getIndicatorDueDate(indicator);
    period.textContent = dueDate
      ? `${indicator.periodicity || 'Periodo'} · ${formatDisplayDate(dueDate)}`
      : `${indicator.periodicity || 'Periodo no definido'}`;
  }
  if (target) target.textContent = `Meta: ${getIndicatorGoalLabel(indicator)}`;
  if (dateInput) dateInput.value = toIsoDate(new Date());
  (document.querySelector('#measurement-modal') as HTMLDialogElement | null)?.showModal();
}

function openMeasurementDetailModal(indicator: Indicator, measurement: Measurement) {
  const measurementResult = measurement.result || '---';
  const goalLabel = getIndicatorGoalLabel(indicator);
  const resultNumeric = parseNumericValue(measurementResult);
  const goalNumeric = parseNumericValue(goalLabel);
  const isOnTarget = resultNumeric !== null && goalNumeric !== null ? resultNumeric >= goalNumeric : false;

  const title = document.querySelector<HTMLElement>('#measurement-detail-title');
  const result = document.querySelector<HTMLElement>('#measurement-detail-result');
  const status = document.querySelector<HTMLElement>('#measurement-detail-status');
  const period = document.querySelector<HTMLElement>('#measurement-detail-period');
  const target = document.querySelector<HTMLElement>('#measurement-detail-target');
  const author = document.querySelector<HTMLElement>('#measurement-detail-author');
  const date = document.querySelector<HTMLElement>('#measurement-detail-date');
  const source = document.querySelector<HTMLElement>('#measurement-detail-source');
  const calculation = document.querySelector<HTMLElement>('#measurement-detail-calculation');
  const notes = document.querySelector<HTMLElement>('#measurement-detail-notes');

  if (title) title.textContent = `${measurement.period || 'Registro'} · ${indicator.name}`;
  if (result) result.textContent = measurementResult;
  if (status) {
    status.textContent = isOnTarget ? 'En meta' : 'Bajo meta';
    status.className = `tag ${isOnTarget ? 'done' : 'medium'}`;
  }
  if (period) period.textContent = measurement.period || 'Sin período';
  if (target) target.textContent = goalLabel;
  if (author) author.textContent = measurement.author || 'Sin responsable';
  if (date) date.textContent = formatDisplayDate(measurement.date || measurement.registered_at || '');
  if (source) source.textContent = measurement.source || 'Sin evidencia registrada';
  if (calculation) calculation.textContent = measurement.calculation || 'Sin cálculo detallado';
  if (notes) notes.textContent = measurement.notes || 'Sin observaciones';

  (document.querySelector('#measurement-detail-modal') as HTMLDialogElement | null)?.showModal();
}

function buildIndicatorChartMarkup(measurements: Measurement[], goalLabel: string) {
  const points = measurements
    .map((measurement) => ({
      id: measurement.id,
      value: parseNumericValue(measurement.result),
      dateLabel: formatDisplayDate(measurement.date || measurement.registered_at || ''),
    }))
    .filter((item) => item.value !== null) as Array<{ id: string; value: number; dateLabel: string }>;

  if (points.length === 0) {
    return {
      yLabels: '<span>100</span><span>75</span><span>50</span><span>25</span>',
      svg: '<svg viewBox="0 0 660 210" preserveAspectRatio="none" aria-label="Evolución del indicador"></svg>',
      xLabels: '<span>Sin datos suficientes</span>',
    };
  }

  const goalNumeric = parseNumericValue(goalLabel);
  const values = points.map((point) => point.value);
  let minValue = Math.min(...values, goalNumeric ?? Math.min(...values));
  let maxValue = Math.max(...values, goalNumeric ?? Math.max(...values));
  if (maxValue === minValue) {
    minValue -= 1;
    maxValue += 1;
  }
  const range = Math.max(1, maxValue - minValue);

  const plotTop = 24;
  const plotBottom = 186;
  const plotLeft = 32;
  const plotRight = 628;
  const step = (plotRight - plotLeft) / Math.max(1, points.length - 1);

  const toY = (value: number) => {
    const ratio = (value - minValue) / range;
    return Math.round(plotBottom - ratio * (plotBottom - plotTop));
  };

  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'}${Math.round(plotLeft + index * step)},${toY(point.value)}`)
    .join(' ');

  const circles = points
    .map((point, index) => {
      const cx = Math.round(plotLeft + index * step);
      const cy = toY(point.value);
      const lowClass = goalNumeric !== null && point.value < goalNumeric ? ' low' : '';
      return `<circle class="indicator-point${lowClass}" data-measurement-id="${point.id}" cx="${cx}" cy="${cy}" r="6"></circle>`;
    })
    .join('');

  const targetLine = goalNumeric !== null
    ? `<line x1="0" y1="${toY(goalNumeric)}" x2="660" y2="${toY(goalNumeric)}" class="indicator-target-line"></line>`
    : '';

  const yLabels = Array.from({ length: 4 }, (_, index) => {
    const label = maxValue - (range / 3) * index;
    const rounded = Math.round(label * 100) / 100;
    return `<span>${Number.isInteger(rounded) ? rounded : rounded.toFixed(2)}</span>`;
  }).join('');

  const xLabels = points
    .map((point) => `<span>${point.dateLabel}</span>`)
    .join('');

  return {
    yLabels,
    svg: `<svg viewBox="0 0 660 210" preserveAspectRatio="none" aria-label="Evolución del indicador">${targetLine}<path d="${path}" class="indicator-trend"></path>${circles}</svg>`,
    xLabels,
  };
}

function resolveIndicatorRange(period: '6m' | '3m' | '30d', toDateValue?: string | null) {
  const end = toDateValue ? new Date(toDateValue) : new Date();
  if (Number.isNaN(end.getTime())) return null;
  const start = new Date(end);
  if (period === '30d') start.setDate(start.getDate() - 30);
  if (period === '3m') start.setMonth(start.getMonth() - 3);
  if (period === '6m') start.setMonth(start.getMonth() - 6);
  return {
    from: toIsoDate(start),
    to: toIsoDate(end),
  };
}

function filterMeasurementsByRange(measurements: Measurement[], fromDate?: string | null, toDate?: string | null) {
  if (!fromDate && !toDate) return measurements;
  const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
  const toTs = toDate ? new Date(`${toDate}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;

  return measurements.filter((measurement) => {
    const measurementDate = measurement.date || measurement.registered_at;
    if (!measurementDate) return false;
    const ts = new Date(measurementDate).getTime();
    if (Number.isNaN(ts)) return false;
    return ts >= fromTs && ts <= toTs;
  });
}

function renderIndicatorDetail(indicatorId: string | null) {
  const detailView = document.querySelector<HTMLElement>('#indicator-detail');
  if (!detailView) return;

  const indicator = state.indicators.find((item) => item.id === indicatorId) || state.indicators[0];
  if (!indicator) {
    detailView.innerHTML = '<div class="empty-state"><strong>No hay indicador seleccionado.</strong><p>Crea un indicador para ver su ficha y evolución.</p></div>';
    return;
  }

  const previousIndicatorId = state.currentIndicatorId;
  state.currentIndicatorId = indicator.id;
  const indicatorMeasurements = getIndicatorMeasurements(indicator.id);

  const todayIso = toIsoDate(new Date());
  if (previousIndicatorId !== indicator.id) {
    if (indicatorMeasurements.length > 0) {
      const firstMeasurementDate = indicatorMeasurements[0].date || indicatorMeasurements[0].registered_at || todayIso;
      const lastMeasurementDate = indicatorMeasurements[indicatorMeasurements.length - 1].date || indicatorMeasurements[indicatorMeasurements.length - 1].registered_at || todayIso;
      state.currentIndicatorRangePreset = 'custom';
      state.currentIndicatorDateFrom = toIsoDate(new Date(firstMeasurementDate));
      state.currentIndicatorDateTo = toIsoDate(new Date(lastMeasurementDate));
    } else {
      state.currentIndicatorRangePreset = '6m';
      state.currentIndicatorDateTo = todayIso;
      const defaultRange = resolveIndicatorRange('6m', todayIso);
      state.currentIndicatorDateFrom = defaultRange?.from || todayIso;
    }
  }

  if (!state.currentIndicatorDateTo) state.currentIndicatorDateTo = todayIso;
  if (!state.currentIndicatorDateFrom) {
    const resolved = resolveIndicatorRange(state.currentIndicatorRangePreset === 'custom' ? '6m' : state.currentIndicatorRangePreset, state.currentIndicatorDateTo);
    state.currentIndicatorDateFrom = resolved?.from || todayIso;
  }

  const snapshot = getIndicatorSnapshot(indicator);
  const rangedMeasurements = filterMeasurementsByRange(snapshot.measurements, state.currentIndicatorDateFrom, state.currentIndicatorDateTo);
  const measurements = [...rangedMeasurements].reverse();
  const latest = measurements[0] || [...snapshot.measurements].reverse()[0];
  const previous = measurements[1];
  const latestNumeric = parseNumericValue(latest?.result || snapshot.currentValue);
  const previousNumeric = parseNumericValue(previous?.result || null);
  const variation = latestNumeric !== null && previousNumeric !== null ? latestNumeric - previousNumeric : null;
  const variationClass = variation !== null && variation < 0 ? 'negative' : 'positive';
  const chart = buildIndicatorChartMarkup(rangedMeasurements, snapshot.goalLabel);
  const fromIso = state.currentIndicatorDateFrom || todayIso;
  const toIso = state.currentIndicatorDateTo || todayIso;
  const displayValue = measurements[0]?.result || snapshot.currentValue;
  const historyRows = measurements
    .map((measurement) => `<button class="measurement-history-row" type="button" data-measurement-id="${measurement.id}"><span><strong>${measurement.period || 'Sin período'}</strong><small>${formatDisplayDate(measurement.date || measurement.registered_at || '')}</small></span><b>${measurement.result || '---'}</b><span>${snapshot.goalLabel}</span><span>${measurement.author || 'Sin responsable'}</span><time>${formatDisplayDate(measurement.registered_at || measurement.date || '')}</time><i data-lucide="chevron-right"></i></button>`)
    .join('');

  detailView.innerHTML = `
    <button class="back-button" data-view-target="indicators" type="button">
      <i data-lucide="arrow-left"></i>Todos los indicadores
    </button>
    <div class="process-detail-head">
      <span class="process-symbol large-symbol"><i data-lucide="${getProcessIcon(indicator.process_id)}"></i></span>
      <div>
        <span class="eyebrow">INDICADOR · ${indicator.code || 'SIN CÓDIGO'} · ${indicator.periodicity || 'Sin periodicidad'}</span>
        <h1>${indicator.name}</h1>
        <p>${getProcessName(indicator.process_id)}</p>
      </div>
      <em class="tag ${snapshot.statusClass}">${snapshot.status}</em>
      <button class="primary-button" type="button" data-open-measurement="${indicator.id}"><i data-lucide="plus"></i>Registrar avance</button>
    </div>
    <div class="indicator-detail-grid">
      <article class="panel indicator-chart-large">
        <div class="panel-header">
          <div>
            <h3>Evolución del indicador</h3>
            <p>Valor registrado y meta por período de medición.</p>
          </div>
          <button class="secondary-button" data-open="export-modal" type="button"><i data-lucide="download"></i>Exportar</button>
        </div>
        <div class="indicator-range-controls"><div class="range-presets" id="indicator-range"><button class="${state.currentIndicatorRangePreset === '6m' ? 'active' : ''}" data-indicator-range="6m" type="button">6 meses</button><button class="${state.currentIndicatorRangePreset === '3m' ? 'active' : ''}" data-indicator-range="3m" type="button">3 meses</button><button class="${state.currentIndicatorRangePreset === '30d' ? 'active' : ''}" data-indicator-range="30d" type="button">30 días</button></div><label>Desde<input id="indicator-date-from" type="date" value="${fromIso}" /></label><label>Hasta<input id="indicator-date-to" type="date" value="${toIso}" /></label><button id="apply-indicator-range" class="secondary-button small" type="button">Aplicar</button></div>
        <div class="big-stat">
          <strong>${displayValue}</strong>
          <span>${variation !== null ? `<b class="${variationClass}">${variation > 0 ? '+' : ''}${Math.round(variation * 100) / 100}</b> frente a la medición anterior` : `Meta actual: ${snapshot.goalLabel}`}</span>
        </div>
        <div class="indicator-chart-plot" id="indicator-chart-plot"><div class="chart-y-labels">${chart.yLabels}</div><div class="chart-canvas">${chart.svg}<div class="chart-x-labels">${chart.xLabels}</div></div></div><div class="chart-key"><span><i></i>Resultado registrado</span><span><i class="target-key"></i>Meta ${snapshot.goalLabel}</span><small>Selecciona un punto para ver el registro.</small></div>
      </article>
      <aside class="panel formula-card">
        <h3>Ficha técnica</h3>
        <dl>
          <dt>Fórmula</dt>
          <dd>${latest?.calculation || 'Sin fórmula registrada'}</dd>
          <dt>Meta</dt>
          <dd>${snapshot.goalLabel}</dd>
          <dt>Responsable</dt>
          <dd>${getProcessOwner(indicator.process_id)}</dd>
          <dt>Periodicidad</dt>
          <dd>${indicator.periodicity || 'Sin periodicidad'}</dd>
          <dt>Fuente</dt>
          <dd>${latest?.source || 'Sin fuente registrada'}</dd>
        </dl>
        <button class="text-button" type="button" data-open="indicator-modal">Editar indicador <i data-lucide="arrow-up-right"></i></button>
      </aside>
    </div>
    <article class="panel measurement-history-panel">
      <div class="panel-header">
        <div>
          <h3>Historial de mediciones</h3>
          <p>Cada resultado conserva registro, fuente, responsable y análisis.</p>
        </div>
        <span id="indicator-history-count" class="tag neutral">${measurements.length} registros en rango</span>
      </div>
      <div class="measurement-history-head">
        <span>Período</span><span>Resultado</span><span>Meta</span><span>Registrado por</span><span>Fecha de registro</span><span></span>
      </div>
      ${historyRows || '<div class="empty-state"><strong>Sin historial disponible.</strong><p>Aún no hay mediciones asociadas a este indicador.</p></div>'}
    </article>`;

  detailView.querySelectorAll<HTMLButtonElement>('[data-open-measurement]').forEach((button) => {
    button.addEventListener('click', () => {
      const targetId = button.dataset.openMeasurement;
      if (targetId) openMeasurementModal(targetId);
    });
  });
  detailView.querySelectorAll<HTMLButtonElement>('[data-measurement-id]').forEach((button) => {
    button.addEventListener('click', () => {
      const measurementId = button.dataset.measurementId;
      if (!measurementId) return;
      const measurement = snapshot.measurements.find((item) => item.id === measurementId);
      if (!measurement) return;
      openMeasurementDetailModal(indicator, measurement);
    });
  });

  detailView.querySelectorAll<HTMLButtonElement>('[data-indicator-range]').forEach((button) => {
    button.addEventListener('click', () => {
      const preset = button.dataset.indicatorRange as '6m' | '3m' | '30d' | undefined;
      if (!preset) return;
      const resolved = resolveIndicatorRange(preset, state.currentIndicatorDateTo || toIsoDate(new Date()));
      state.currentIndicatorRangePreset = preset;
      state.currentIndicatorDateFrom = resolved?.from || state.currentIndicatorDateFrom;
      state.currentIndicatorDateTo = resolved?.to || state.currentIndicatorDateTo;
      renderIndicatorDetail(indicator.id);
    });
  });

  const applyRangeButton = detailView.querySelector<HTMLButtonElement>('#apply-indicator-range');
  if (applyRangeButton) {
    applyRangeButton.addEventListener('click', () => {
      const fromInput = detailView.querySelector<HTMLInputElement>('#indicator-date-from');
      const toInput = detailView.querySelector<HTMLInputElement>('#indicator-date-to');
      state.currentIndicatorRangePreset = 'custom';
      state.currentIndicatorDateFrom = fromInput?.value || null;
      state.currentIndicatorDateTo = toInput?.value || null;
      renderIndicatorDetail(indicator.id);
    });
  }

  refreshIcons();
}

function renderRiskSection(risks: Risk[]) {
  state.risks = risks;
  if (!state.currentRiskId && risks[0]) {
    state.currentRiskId = risks[0].id;
  }

  const summary = document.querySelectorAll<HTMLElement>('.risk-summary article strong');
  const critical = risks.filter((risk) => (risk.status || '').toLowerCase().includes('crítico') || (risk.probability || '').toLowerCase().includes('alto') || (risk.impact || '').toLowerCase().includes('alto')).length;
  const treated = risks.filter((risk) => (risk.status || '').toLowerCase().includes('control') || (risk.status || '').toLowerCase().includes('tratamiento') || (risk.status || '').toLowerCase().includes('controlado')).length;
  const uniqueProcesses = new Set(risks.map((item) => item.process_id).filter(Boolean)).size;
  const treatedPercent = risks.length ? Math.round((treated / risks.length) * 100) : 0;

  if (summary[0]) summary[0].textContent = String(risks.length);
  if (summary[1]) summary[1].textContent = String(critical);
  if (summary[2]) summary[2].textContent = String(treated);

  const activeText = document.querySelector<HTMLElement>('#risk-active-text');
  const criticalText = document.querySelector<HTMLElement>('#risk-critical-text');
  const treatedText = document.querySelector<HTMLElement>('#risk-treated-text');

  if (activeText) activeText.textContent = `en ${uniqueProcesses} procesos`;
  if (criticalText) criticalText.textContent = critical > 0 ? `${critical} requieren plan` : 'sin criticidad alta';
  if (treatedText) treatedText.textContent = `${treatedPercent}% del total`;

  const matrix = document.querySelector<HTMLElement>('.heat-grid .matrix');
  if (matrix) {
    const normalizeLevel = (value?: string | null) => {
      const normalized = (value || '').trim().toLowerCase();
      if (normalized.startsWith('alt')) return 'alto';
      if (normalized.startsWith('med')) return 'medio';
      if (normalized.startsWith('baj')) return 'bajo';
      return '';
    };

    const levels = [
      { probability: 'bajo', impact: 'bajo', className: 'green-cell' },
      { probability: 'medio', impact: 'bajo', className: 'yellow-cell' },
      { probability: 'alto', impact: 'bajo', className: 'orange-cell' },
      { probability: 'bajo', impact: 'medio', className: 'yellow-cell' },
      { probability: 'medio', impact: 'medio', className: 'orange-cell' },
      { probability: 'alto', impact: 'medio', className: 'red-cell' },
      { probability: 'bajo', impact: 'alto', className: 'orange-cell' },
      { probability: 'medio', impact: 'alto', className: 'red-cell' },
      { probability: 'alto', impact: 'alto', className: 'red-cell' },
    ];

    matrix.innerHTML = levels.map((level) => {
      const count = risks.filter((risk) => normalizeLevel(risk.probability) === level.probability && normalizeLevel(risk.impact) === level.impact).length;
      return `<i class="cell ${level.className}">${count > 0 ? `<b>${count}</b>` : ''}</i>`;
    }).join('');
  }

  const processOptions = state.processes
    .filter((process) => risks.some((risk) => risk.process_id === process.id))
    .map((process) => `<option value="${process.id}">${process.name}</option>`)
    .join('');

  if (state.currentRiskProcessFilter && !risks.some((risk) => risk.process_id === state.currentRiskProcessFilter)) {
    state.currentRiskProcessFilter = null;
  }

  const filteredRisks = state.currentRiskProcessFilter
    ? risks.filter((risk) => risk.process_id === state.currentRiskProcessFilter)
    : risks;

  const filteredIds = new Set(filteredRisks.map((risk) => risk.id));
  if (filteredRisks.length > 0 && (!state.currentRiskId || !filteredIds.has(state.currentRiskId))) {
    state.currentRiskId = filteredRisks[0].id;
  }

  const riskScore = (risk: Risk) => {
    const impact = (risk.impact || '').toLowerCase();
    const probability = (risk.probability || '').toLowerCase();
    const status = (risk.status || '').toLowerCase();
    if (impact.includes('alto') && probability.includes('alto')) return 4;
    if (status.includes('crítico') || impact.includes('alto') || probability.includes('alto')) return 3;
    if (impact.includes('medio') || probability.includes('medio')) return 2;
    return 1;
  };

  const orderedRisks = [...filteredRisks].sort((a, b) => {
    const scoreDiff = riskScore(b) - riskScore(a);
    if (scoreDiff !== 0) return scoreDiff;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const totalPages = Math.max(1, Math.ceil(orderedRisks.length / state.riskPageSize));
  if (state.currentRiskPage > totalPages) state.currentRiskPage = totalPages;
  if (state.currentRiskPage < 1) state.currentRiskPage = 1;
  const startIndex = (state.currentRiskPage - 1) * state.riskPageSize;
  const pagedRisks = orderedRisks.slice(startIndex, startIndex + state.riskPageSize);

  const riskListPanel = document.querySelector<HTMLElement>('.risk-list');
  if (riskListPanel) {
    riskListPanel.innerHTML = `
      <div class="panel-header">
        <div>
          <h3>Lista de riesgos</h3>
          <p>${orderedRisks.length} visibles · Página ${state.currentRiskPage} de ${totalPages}</p>
        </div>
        <div class="process-filter">
          <i data-lucide="filter"></i>
          <select data-risk-process-filter>
            <option value="">Todos los procesos</option>
            ${processOptions}
          </select>
        </div>
      </div>
      <div class="attention-list">
        ${pagedRisks.length > 0
          ? pagedRisks.map((risk) => `
            <button type="button" data-risk-open="${risk.id}">
              <span class="attention-icon ${risk.status?.toLowerCase().includes('crítico') ? 'risk-bg' : 'action-bg'}"><i data-lucide="shield-alert"></i></span>
              <span><strong>${risk.name}</strong><small>${getProcessName(risk.process_id)} · ${formatDisplayDate(risk.review_date || risk.created_at || '')}</small></span>
              <em class="tag ${risk.status?.toLowerCase().includes('crítico') ? 'critical' : (risk.impact || '').toLowerCase().includes('alto') ? 'high' : 'medium'}">${risk.status || 'Abierto'}</em>
            </button>`).join('')
          : '<div class="empty-state"><strong>Sin riesgos para este filtro.</strong><p>Prueba otro proceso o registra un riesgo nuevo.</p></div>'}
      </div>
      <div class="risk-pagination">
        <button type="button" class="secondary-button small" data-risk-page-action="prev" ${state.currentRiskPage <= 1 ? 'disabled' : ''}>Anterior</button>
        <span>${startIndex + 1}-${Math.min(startIndex + state.riskPageSize, orderedRisks.length)} de ${orderedRisks.length}</span>
        <button type="button" class="secondary-button small" data-risk-page-action="next" ${state.currentRiskPage >= totalPages ? 'disabled' : ''}>Siguiente</button>
      </div>`;
    const select = riskListPanel.querySelector<HTMLSelectElement>('[data-risk-process-filter]');
    if (select) select.value = state.currentRiskProcessFilter || '';
  }

  renderRiskDetail(state.currentRiskId);
  refreshIcons();
}

function renderRiskDetail(riskId: string | null) {
  const riskDetail = document.querySelector<HTMLElement>('#risk-detail');
  if (!riskDetail) return;

  const risk = state.risks.find((item) => item.id === riskId) || state.risks[0];
  if (!risk) {
    riskDetail.innerHTML = '<div class="empty-state"><strong>Sin riesgo seleccionado.</strong><p>Registra un riesgo para ver su ficha y tratamiento.</p></div>';
    return;
  }

  state.currentRiskId = risk.id;
  const processName = getProcessName(risk.process_id);
  const processIcon = getProcessIcon(risk.process_id);
  const inherentLevel = (risk.impact || '').toLowerCase().includes('alto') || (risk.probability || '').toLowerCase().includes('alto') ? 'Crítico' : (risk.impact || '').toLowerCase().includes('medio') || (risk.probability || '').toLowerCase().includes('medio') ? 'Moderado' : 'Bajo';
  const severityClass = inherentLevel === 'Crítico' ? 'critical' : inherentLevel === 'Moderado' ? 'high' : 'done';
  const statusClass = (risk.status || '').toLowerCase().includes('control') ? 'done' : (risk.status || '').toLowerCase().includes('tratamiento') ? 'high' : 'critical';

  riskDetail.innerHTML = `
    <button class="back-button" data-view-target="risks" type="button">
      <i data-lucide="arrow-left"></i>Mapa de riesgos
    </button>
    <div class="process-detail-head">
      <span class="process-symbol large-symbol red"><i data-lucide="${processIcon}"></i></span>
      <div>
        <span class="eyebrow">RIESGO · ${risk.id.slice(0, 8).toUpperCase()} · ${processName.toUpperCase()}</span>
        <h1>${risk.name}</h1>
        <p>${risk.causes || 'Sin causas y consecuencias registradas.'}</p>
      </div>
      <em class="tag ${statusClass}">${risk.status || 'Abierto'}</em>
      <button class="secondary-button" type="button" data-risk-edit="${risk.id}"><i data-lucide="pencil"></i>Editar riesgo</button>
    </div>
    <div class="risk-detail-grid">
      <article class="panel control-plan">
        <div class="panel-header">
          <div>
            <h3>Evaluación y tratamiento</h3>
            <p>Plan real de control y reducción de exposición.</p>
          </div>
        </div>
        <div class="risk-score">
          <div><span>Probabilidad</span><strong>${risk.probability || 'N/D'}</strong></div>
          <i data-lucide="x"></i>
          <div><span>Impacto</span><strong>${risk.impact || 'N/D'}</strong></div>
          <i data-lucide="equal"></i>
          <div class="score-danger"><span>Nivel inherente</span><strong>${inherentLevel}</strong></div>
        </div>
        <div class="control-block">
          <span>Control actual</span>
          <p>${risk.controls || 'Sin control documentado.'}</p>
        </div>
        <div class="control-block">
          <span>Plan de tratamiento</span>
          <p>${risk.treatment || 'Sin plan de tratamiento definido.'}</p>
          <button class="text-button" type="button" data-view-target="actions">Ver acción relacionada <i data-lucide="arrow-right"></i></button>
        </div>
      </article>
      <aside class="panel risk-activity">
        <h3>Seguimiento</h3>
        <div><i data-lucide="calendar-clock"></i><span><strong>Revisión programada</strong><small>${formatDisplayDate(risk.review_date || '')} · ${risk.owner || 'No aplica'}</small></span></div>
        <div><i data-lucide="shield-alert"></i><span><strong>Estado actual</strong><small>${risk.status || 'Abierto'} · ${processName}</small></span></div>
        <div><i data-lucide="clock-3"></i><span><strong>Creado</strong><small>${formatDisplayDate(risk.created_at)} · Atlas</small></span></div>
      </aside>
    </div>`;

  refreshIcons();
}

function renderActionBoard(actions: Action[]) {
  state.actions = actions;
  const showMoreButton = document.querySelector<HTMLElement>('#show-more-actions');

  const mineActions = actions.filter((action) => {
    const assignee = (action.assignee || '').trim().toLowerCase();
    const me = (state.currentProfile?.full_name || '').trim().toLowerCase();
    return assignee && me ? assignee === me : false;
  });

  const scopeHost = document.querySelector<HTMLElement>('#action-scope');
  if (scopeHost) {
    const mineButton = scopeHost.querySelector<HTMLButtonElement>('[data-action-scope="mine"]');
    const allButton = scopeHost.querySelector<HTMLButtonElement>('[data-action-scope="all"]');
    if (mineButton) {
      mineButton.classList.toggle('active', state.currentActionScope === 'mine');
      const badge = mineButton.querySelector<HTMLElement>('b');
      if (badge) badge.textContent = String(mineActions.length);
    }
    if (allButton) {
      allButton.classList.toggle('active', state.currentActionScope === 'all');
      const badge = allButton.querySelector<HTMLElement>('b');
      if (badge) badge.textContent = String(actions.length);
    }
  }

  const viewsHost = document.querySelector<HTMLElement>('#action-views');
  if (viewsHost) {
    viewsHost.querySelectorAll<HTMLButtonElement>('button[data-action-view]').forEach((button) => {
      button.classList.toggle('active', button.dataset.actionView === state.currentActionView);
    });
  }

  const hideCompleted = document.querySelector<HTMLInputElement>('#hide-completed');
  if (hideCompleted) hideCompleted.checked = state.hideCompletedActions;

  const scopedActions = state.currentActionScope === 'mine' ? mineActions : actions;
  const visibleActions = state.hideCompletedActions
    ? scopedActions.filter((action) => !(action.status || '').toLowerCase().includes('completad'))
    : scopedActions;
  const displayedActions = visibleActions.slice(0, state.actionVisibleCount);

  const viewContent = document.querySelector<HTMLElement>('#action-view-content');
  if (!viewContent) return;

  if (state.currentActionView === 'table') {
    viewContent.innerHTML = `
      <article class="panel">
        <div class="action-table-head">
          <span>Acción</span><span>Proceso</span><span>Responsable</span><span>Vence</span><span>Estado</span>
        </div>
        ${displayedActions.map((action) => `
          <button class="action-table-row" type="button" data-action-open="${action.id}">
            <span><strong>${action.title || 'Acción sin título'}</strong><small>${action.description || 'Sin detalle registrado'}</small></span>
            <span>${action.source || 'Sin proceso'}</span>
            <span>${action.assignee || 'No aplica'}</span>
            <span>${formatDisplayDate(action.due_date || '')}</span>
            <span><em class="tag ${(action.status || '').toLowerCase().includes('completad') ? 'done' : (action.status || '').toLowerCase().includes('progreso') ? 'high' : 'review'}">${action.status || 'Por iniciar'}</em></span>
          </button>`).join('') || '<div class="empty-state"><strong>Sin acciones.</strong><p>No hay acciones para el filtro seleccionado.</p></div>'}
      </article>`;
  } else if (state.currentActionView === 'calendar') {
    const calendarItems = [...displayedActions].sort((a, b) => new Date(a.due_date || a.created_at).getTime() - new Date(b.due_date || b.created_at).getTime());
    viewContent.innerHTML = `
      <article class="panel action-calendar">
        <div class="panel-header"><div><h3>Calendario de acciones</h3><p>Ordenadas por fecha de vencimiento.</p></div></div>
        <div class="measurement-list">
          ${calendarItems.map((action) => {
            const due = action.due_date ? new Date(action.due_date) : null;
            return `<button type="button" data-action-open="${action.id}"><span class="measure-date"><b>${due ? String(due.getDate()).padStart(2, '0') : '--'}</b><small>${due ? due.toLocaleDateString('es-CO', { month: 'short' }).toUpperCase() : 'S/F'}</small></span><span><strong>${action.title || 'Acción sin título'}</strong><small>${action.source || 'Sin proceso'} · ${action.assignee || 'No aplica'}</small></span><span class="measure-progress"><em class="tag ${(action.status || '').toLowerCase().includes('completad') ? 'done' : (action.status || '').toLowerCase().includes('progreso') ? 'high' : 'review'}">${action.status || 'Por iniciar'}</em></span></button>`;
          }).join('') || '<div class="empty-state"><strong>Sin acciones.</strong><p>No hay fechas programadas para el filtro seleccionado.</p></div>'}
        </div>
      </article>`;
  } else {
    const groups = {
      'Por iniciar': [] as Action[],
      'En progreso': [] as Action[],
      'En validación': [] as Action[],
      Completadas: [] as Action[],
    };

    displayedActions.forEach((action) => {
      const status = action.status || 'Por iniciar';
      if (groups[status]) groups[status].push(action);
      else groups['Por iniciar'].push(action);
    });

    viewContent.innerHTML = `<div class="kanban" id="action-board">${Object.entries(groups)
      .map(([status, items]) => `
        <div class="kanban-column">
          <header><span>${status}</span><b>${items.length}</b></header>
          ${items.map((item) => `
            <article class="task-card${(item.status || '').toLowerCase().includes('completad') ? ' complete' : ''}" data-action-open="${item.id}">
              <em class="tag ${(item.status || '').toLowerCase().includes('completad') ? 'done' : (item.status || '').toLowerCase().includes('progreso') ? 'high' : 'review'}">${item.status || 'Por iniciar'}</em>
              <h3>${item.title || 'Acción sin título'}</h3>
              <p>${item.source || 'Sin proceso asignado'}</p>
              <footer>
                <span class="avatar one">${item.assignee?.split(' ').map((token) => token[0]).join('').slice(0, 2) || 'NA'}</span>
                <time>${formatDisplayDate(item.due_date || '')}</time>
              </footer>
            </article>`).join('')}
        </div>`)
      .join('')}</div>`;
  }

  if (showMoreButton) {
    const remaining = visibleActions.length - displayedActions.length;
    showMoreButton.hidden = remaining <= 0;
    showMoreButton.textContent = remaining > 0 ? `Mostrar ${Math.min(6, remaining)} acciones más` : 'Mostrar acciones';
    if (remaining > 0 && !showMoreButton.querySelector('svg')) {
      showMoreButton.innerHTML = `<i data-lucide="chevron-down"></i>${showMoreButton.textContent}`;
    }
  }

  const toolbarCount = document.querySelector<HTMLElement>('#toolbar-process-count');
  if (toolbarCount) toolbarCount.textContent = `${visibleActions.length} acciones`;
  refreshIcons();
}

function resolveImprovementPhase(phase: string | undefined) {
  const value = (phase || '').toLowerCase();
  if (value.includes('hacer')) return { code: 'H', name: 'Hacer', className: 'phase-do' };
  if (value.includes('verificar')) return { code: 'V', name: 'Verificar', className: 'phase-check' };
  if (value.includes('actuar')) return { code: 'A', name: 'Actuar', className: 'phase-act' };
  return { code: 'P', name: 'Planear', className: 'phase-plan' };
}

function renderImprovementDetail(planId?: string | null) {
  const detailView = document.querySelector<HTMLElement>('#improvement-detail');
  if (!detailView) return;

  const selectedPlan = state.improvementPlans.find((plan) => plan.id === (planId || state.currentImprovementPlanId || ''))
    || state.improvementPlans.find((plan) => !(plan.status || '').toLowerCase().includes('cerrad'))
    || state.improvementPlans[0]
    || null;

  state.currentImprovementPlanId = selectedPlan?.id || null;

  if (!selectedPlan) {
    detailView.innerHTML = '<article class="panel"><div class="empty-state"><strong>Sin planes de mejora.</strong><p>Crea un plan desde el botón "Nuevo plan de mejora".</p></div></article>';
    refreshIcons();
    return;
  }

  const phase = resolveImprovementPhase(selectedPlan.phase);
  const followups = state.improvementFollowupsByPlan[selectedPlan.id] || [];
  const relatedRisks = state.risks.filter((risk) => (risk.process_id ? getProcessName(risk.process_id) : '').toLowerCase() === (selectedPlan.process || '').toLowerCase());
  const relatedAudits = state.audits.filter((audit) => (audit.notes || '').toLowerCase().includes((selectedPlan.process || '').toLowerCase()));
  const relatedIndicators = state.indicators.filter((indicator) => (indicator.process_id ? getProcessName(indicator.process_id) : '').toLowerCase() === (selectedPlan.process || '').toLowerCase());
  const relatedActions = state.actions.filter((action) => (action.source || '').toLowerCase() === (selectedPlan.process || '').toLowerCase());
  const completion = Math.max(0, Math.min(100, Number(selectedPlan.progress || 0)));
  const latestFollowup = followups[0] || null;

  detailView.innerHTML = `
    <button class="back-button" data-view-target="improvement" type="button"><i data-lucide="arrow-left"></i>Todos los planes de mejora</button>
    <div class="process-detail-head">
      <span class="plan-phase ${phase.className}">${phase.code}</span>
      <div>
        <span class="eyebrow">PLAN DE MEJORA · ${selectedPlan.id.slice(0, 8).toUpperCase()} · ${phase.name.toUpperCase()}</span>
        <h1>${selectedPlan.name || 'Plan de mejora'}</h1>
        <p>Origen: ${selectedPlan.origin || 'Sin origen'} · Proceso: ${selectedPlan.process || 'Sin proceso'} · Responsable: ${selectedPlan.owner || 'No aplica'}</p>
      </div>
      <em class="tag ${(selectedPlan.status || '').toLowerCase().includes('cerrad') ? 'done' : 'medium'}">${selectedPlan.status || 'Activo'}</em>
      <button class="primary-button" type="button" data-open="improvement-modal"><i data-lucide="pencil"></i>Editar plan</button>
    </div>
    <div class="improvement-workspace">
      <article class="panel plan-editor">
        <div class="panel-header"><div><h3>Definición del plan</h3><p>Información central del PHVA.</p></div></div>
        <div class="plan-fields">
          <label>Situación y causa<textarea rows="3" readonly>${selectedPlan.situation || 'Sin situación registrada.'}</textarea></label>
          <div class="form-grid">
            <label>Meta de mejora<input value="${selectedPlan.goal || 'Sin meta'}" readonly /></label>
            <label>Fecha objetivo<input value="${selectedPlan.target_date ? formatDisplayDate(selectedPlan.target_date) : 'Sin fecha'}" readonly /></label>
          </div>
          <div class="form-grid">
            <label>Proceso origen<input value="${selectedPlan.process || 'Sin proceso'}" readonly /></label>
            <label>Fase PHVA<input value="${phase.name}" readonly /></label>
          </div>
        </div>
      </article>
      <article class="panel plan-actions">
        <div class="panel-header"><div><h3>Elementos relacionados</h3><p>Trazabilidad del plan PHVA.</p></div><button class="primary-button small" type="button" data-open="improvement-followup-modal">Registrar seguimiento</button></div>
        <div>
          <button type="button" data-view-target="actions"><span><strong>Acciones vinculadas</strong><small>${relatedActions.length} asociadas al proceso del plan</small></span><b>${completion}%</b><i data-lucide="chevron-right"></i></button>
          <button type="button" data-view-target="risks"><span><strong>Riesgos relacionados</strong><small>${relatedRisks.length} encontrados por tratamiento</small></span><b>${relatedRisks.length}</b><i data-lucide="chevron-right"></i></button>
          <button type="button" data-view-target="audit"><span><strong>Auditorías relacionadas</strong><small>${relatedAudits.length} con evidencia vinculada</small></span><b>${relatedAudits.length}</b><i data-lucide="chevron-right"></i></button>
          <button type="button" data-view-target="indicators"><span><strong>Indicadores del proceso</strong><small>${relatedIndicators.length} asociados al origen</small></span><b>${relatedIndicators.length}</b><i data-lucide="chevron-right"></i></button>
        </div>
      </article>
      <article class="panel followup-log">
        <div class="panel-header"><div><h3>Seguimiento y efectividad</h3><p>${followups.length} registro(s) de seguimiento.</p></div></div>
        ${followups.length > 0
          ? followups.slice(0, 5).map((entry) => `<div class="followup-entry"><time>${entry.followup_date ? formatDisplayDate(entry.followup_date) : 'Sin fecha'}</time><span><strong>${entry.decision || 'Seguimiento registrado'}</strong><small>${entry.progress_note || 'Sin detalle.'}</small><b>${entry.effectiveness_comment || entry.verified_result || ''}</b></span><em class="tag review">Seguimiento</em></div>`).join('')
          : '<div class="empty-state"><strong>Sin seguimientos.</strong><p>Registra el primer seguimiento del plan.</p></div>'}
      </article>
      <aside class="panel effectiveness-card">
        <h3>Estado de efectividad</h3>
        <div class="effectiveness-stat"><strong>${completion}%</strong><span>avance</span><b>Fase: ${phase.name}</b></div>
        <p>${latestFollowup?.effectiveness_comment || 'Sin evaluación de efectividad registrada.'}</p>
        <div class="effectiveness-actions"><button class="secondary-button" type="button" data-open="improvement-modal">Ajustar plan</button><button class="primary-button" type="button" data-open="improvement-followup-modal">Nuevo seguimiento</button></div>
      </aside>
    </div>`;

  refreshIcons();
}

function renderImprovementSection() {
  const host = document.querySelector<HTMLElement>('#improvement');
  if (!host) return;

  const plans = state.improvementPlans;
  const activePlans = plans.filter((plan) => !(plan.status || '').toLowerCase().includes('cerrad'));
  const completedPlans = plans.filter((plan) => (plan.status || '').toLowerCase().includes('cerrad'));
  const effectiveness = plans.length > 0 ? Math.round((completedPlans.length / plans.length) * 100) : 0;
  const processCount = new Set(plans.map((plan) => plan.process || '').filter(Boolean)).size;
  const phaseCounts = {
    plan: plans.filter((plan) => resolveImprovementPhase(plan.phase).name === 'Planear').length,
    do: plans.filter((plan) => resolveImprovementPhase(plan.phase).name === 'Hacer').length,
    check: plans.filter((plan) => resolveImprovementPhase(plan.phase).name === 'Verificar').length,
    act: plans.filter((plan) => resolveImprovementPhase(plan.phase).name === 'Actuar').length,
  };

  const nextReview = [...activePlans]
    .filter((plan) => Boolean(plan.target_date))
    .sort((a, b) => new Date(a.target_date || '').getTime() - new Date(b.target_date || '').getTime())[0] || activePlans[0] || null;

  host.innerHTML = `
    <div class="page-heading"><div><span class="eyebrow">APRENDIZAJE ORGANIZACIONAL</span><h1>Mejora continua</h1><p>Convierte hallazgos, resultados y decisiones en mejoras verificables.</p></div><button class="primary-button" type="button" data-open="improvement-modal" data-improvement-create="true"><i data-lucide="plus"></i>Nuevo plan de mejora</button></div>
    <div class="improvement-stats">
      <article><span>Planes activos</span><strong>${activePlans.length}</strong><small>en ${processCount} procesos</small></article>
      <article><span>Planes PHVA</span><strong>${plans.length}</strong><small>${phaseCounts.do + phaseCounts.check} en ejecución · ${phaseCounts.plan} por iniciar</small></article>
      <article><span>Efectividad verificada</span><strong class="text-green">${effectiveness}%</strong><small>${completedPlans.length} de ${plans.length} cerrados</small></article>
    </div>
    <div class="phva-flow">
      <article class="active"><span>01</span><div><strong>Planear</strong><small>Analizar causa y definir meta.</small></div><b>${phaseCounts.plan}</b></article>
      <i data-lucide="arrow-right"></i>
      <article><span>02</span><div><strong>Hacer</strong><small>Ejecutar acciones y registrar evidencia.</small></div><b>${phaseCounts.do}</b></article>
      <i data-lucide="arrow-right"></i>
      <article><span>03</span><div><strong>Verificar</strong><small>Medir resultado y efectividad.</small></div><b>${phaseCounts.check}</b></article>
      <i data-lucide="arrow-right"></i>
      <article><span>04</span><div><strong>Actuar</strong><small>Estandarizar o ajustar el plan.</small></div><b>${phaseCounts.act}</b></article>
    </div>
    <div class="improvement-layout">
      <article class="panel improvement-plans">
        <div class="panel-header"><div><h3>Planes de mejora</h3><p>Registro PHVA independiente del módulo de acciones.</p></div></div>
        ${plans.length > 0 ? plans.map((plan) => {
          const phase = resolveImprovementPhase(plan.phase);
          const statusClass = (plan.status || '').toLowerCase().includes('cerrad') ? 'done' : 'medium';
          return `<button class="improvement-plan" type="button" data-improvement-open="${plan.id}"><span class="plan-phase ${phase.className}">${phase.code}</span><span><strong>${plan.name || 'Plan sin título'}</strong><small>Origen: ${plan.origin || 'Sin origen'} · ${plan.process || 'Sin proceso'}</small><em>${plan.goal || 'Sin meta registrada'}</em></span><b><em class="tag ${statusClass}">${plan.status || 'Activo'}</em></b><i data-lucide="chevron-right"></i></button>`;
        }).join('') : '<div class="empty-state"><strong>Sin planes de mejora.</strong><p>Crea un plan para comenzar el ciclo PHVA.</p></div>'}
      </article>
      <aside class="panel improvement-review">
        <div class="panel-header"><div><h3>Próxima revisión</h3><p>${nextReview?.target_date ? formatDisplayDate(nextReview.target_date) : 'Sin fecha programada'}</p></div></div>
        <div class="review-highlight"><span class="plan-phase ${nextReview ? resolveImprovementPhase(nextReview.phase).className : 'phase-plan'}">${nextReview ? resolveImprovementPhase(nextReview.phase).code : 'P'}</span><strong>${nextReview?.name || 'No hay revisión pendiente'}</strong><small>${nextReview?.situation || 'Registra un plan con objetivo y fecha para ver seguimiento.'}</small></div>
        <div class="review-check">
          <label><input type="checkbox" ${nextReview?.goal ? 'checked' : ''} disabled />Objetivo de mejora definido</label>
          <label><input type="checkbox" ${nextReview?.phase && nextReview.phase.toLowerCase().includes('verificar') ? 'checked' : ''} disabled />Resultado del indicador registrado</label>
          <label><input type="checkbox" ${nextReview?.status && nextReview.status.toLowerCase().includes('cerrad') ? 'checked' : ''} disabled />Decisión de efectividad documentada</label>
        </div>
        <button class="primary-button" type="button" data-open="improvement-followup-modal">Registrar seguimiento</button>
      </aside>
    </div>`;

  renderImprovementDetail(state.currentImprovementPlanId);
  refreshIcons();
}

function renderDocumentTable(documents: Document[]) {
  state.documents = documents;

  const categorizeDocument = (doc: Document) => {
    const value = `${doc.category || ''} ${doc.format || ''} ${doc.status || ''}`.toLowerCase();
    if (value.includes('pol') || value.includes('policy')) return 'policy';
    if (value.includes('formato') || value.includes('plantilla') || value.includes('registro') || value.includes('xls') || value.includes('csv')) return 'format';
    if (value.includes('obsole') || value.includes('hist')) return 'archive';
    return 'process';
  };

  const folders = document.querySelectorAll<HTMLButtonElement>('.document-folders [data-doc-filter]');
  const folderCounts = {
    all: documents.length,
    process: documents.filter((doc) => categorizeDocument(doc) === 'process').length,
    policy: documents.filter((doc) => categorizeDocument(doc) === 'policy').length,
    format: documents.filter((doc) => categorizeDocument(doc) === 'format').length,
    archive: documents.filter((doc) => categorizeDocument(doc) === 'archive').length,
  };

  folders.forEach((button) => {
    const key = (button.dataset.docFilter || 'all') as keyof typeof folderCounts;
    button.classList.toggle('selected', key === state.currentDocumentCategoryFilter);
    const badge = button.querySelector<HTMLElement>('b');
    if (badge) badge.textContent = String(folderCounts[key] ?? 0);
  });

  const processFilter = document.querySelector<HTMLSelectElement>('#document-process-filter');
  if (processFilter) {
    const processNames = [...new Set(state.processes.map((process) => process.name).filter(Boolean))];
    processFilter.innerHTML = ['<option value="all">Todos los procesos</option>', ...processNames.map((name) => `<option value="${name}">${name}</option>`)].join('');
    processFilter.value = processNames.includes(state.currentDocumentProcessFilter) ? state.currentDocumentProcessFilter : 'all';
    state.currentDocumentProcessFilter = processFilter.value;
  }

  const statusButton = document.querySelector<HTMLElement>('#document-status-filter');
  if (statusButton) {
    const label = state.currentDocumentStatusFilter === 'all' ? 'Estado: todos' : `Estado: ${state.currentDocumentStatusFilter}`;
    statusButton.innerHTML = `<i data-lucide="sliders-horizontal"></i>${label}`;
  }

  const searchInput = document.querySelector<HTMLInputElement>('#documents .search-field input');
  if (searchInput && searchInput.value !== state.currentDocumentSearch) {
    searchInput.value = state.currentDocumentSearch;
  }

  const filtered = documents.filter((doc) => {
    const category = categorizeDocument(doc);
    const categoryMatch = state.currentDocumentCategoryFilter === 'all' ? true : category === state.currentDocumentCategoryFilter;
    const processMatch = state.currentDocumentProcessFilter === 'all' ? true : (doc.process || '') === state.currentDocumentProcessFilter;
    const statusMatch = state.currentDocumentStatusFilter === 'all' ? true : (doc.status || '') === state.currentDocumentStatusFilter;
    const searchMatch = state.currentDocumentSearch
      ? `${doc.name} ${doc.process || ''} ${doc.category || ''} ${doc.format || ''}`.toLowerCase().includes(state.currentDocumentSearch.toLowerCase())
      : true;
    return categoryMatch && processMatch && statusMatch && searchMatch;
  });

  const tbody = document.querySelector<HTMLTableSectionElement>('.document-table table tbody');
  if (!tbody) return;
  tbody.innerHTML = filtered.length > 0
    ? filtered
      .map((doc) => `
      <tr data-document-open="${doc.id}" style="cursor:pointer">
        <td>
          <span class="file-icon ${doc.format?.toLowerCase() || 'doc'}">${(doc.format || 'DOC').slice(0, 3).toUpperCase()}</span>
          <strong>${doc.name}</strong>
        </td>
        <td>${doc.process || 'Sin proceso'}</td>
        <td>${doc.version || 'v1.0'}</td>
        <td>${new Date(doc.updated_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
        <td><em class="tag ${doc.status?.toLowerCase().includes('vigen') ? 'done' : doc.status?.toLowerCase().includes('por revisar') ? 'review' : 'medium'}">${doc.status || 'Pendiente'}</em></td>
        <td class="document-row-actions"><button class="icon-button" type="button" data-doc-manage="${doc.id}" aria-label="Gestionar documento"><i data-lucide="more-horizontal"></i></button></td>
      </tr>`)
      .join('')
    : '<tr><td colspan="6"><div class="empty-state"><strong>Sin documentos.</strong><p>No hay resultados para el filtro actual.</p></div></td></tr>';
  refreshIcons();
}

function openDocumentEditor(documentId: string) {
  const documentItem = state.documents.find((doc) => doc.id === documentId);
  if (!documentItem) return;
  state.currentDocumentId = documentItem.id;

  const modal = document.querySelector<HTMLDialogElement>('#document-editor-modal');
  if (!modal) return;

  const title = modal.querySelector<HTMLElement>('#editor-title');
  const meta = modal.querySelector<HTMLElement>('#editor-meta');
  const status = modal.querySelector<HTMLSelectElement>('#editor-status');
  const editorWord = modal.querySelector<HTMLElement>('#editor-word');
  const editorSheet = modal.querySelector<HTMLElement>('#editor-sheet');
  const textCommandButtons = modal.querySelectorAll<HTMLElement>('[data-editor-command]');
  const sheetRowButton = modal.querySelector<HTMLElement>('#sheet-add-row');
  const sheetColumnButton = modal.querySelector<HTMLElement>('#sheet-add-column');
  const sheetDeleteButton = modal.querySelector<HTMLElement>('#sheet-delete-sheet');
  if (!editorWord || !editorSheet) return;

  if (!defaultWordEditorTemplate) defaultWordEditorTemplate = editorWord.innerHTML;
  if (!defaultSheetEditorTemplate) defaultSheetEditorTemplate = editorSheet.innerHTML;

  const spreadsheetMode = isSpreadsheetFormat(documentItem.format);
  editorWord.hidden = spreadsheetMode;
  editorSheet.hidden = !spreadsheetMode;
  textCommandButtons.forEach((button) => {
    button.style.display = spreadsheetMode ? 'none' : '';
  });
  if (sheetRowButton) sheetRowButton.style.display = spreadsheetMode ? '' : 'none';
  if (sheetColumnButton) sheetColumnButton.style.display = spreadsheetMode ? '' : 'none';
  if (sheetDeleteButton) sheetDeleteButton.style.display = spreadsheetMode ? '' : 'none';

  if (title) title.textContent = documentItem.name;
  if (meta) meta.textContent = `${(documentItem.format || 'DOC').toUpperCase()} · ${documentItem.process || 'General'} · ${documentItem.version || 'v1.0'}`;
  if (status) {
    if (!Array.from(status.options).some((option) => option.value === (documentItem.status || 'Por revisar'))) {
      const option = document.createElement('option');
      option.value = documentItem.status || 'Por revisar';
      option.textContent = documentItem.status || 'Por revisar';
      status.append(option);
    }
    status.value = documentItem.status || 'Por revisar';
  }
  if (spreadsheetMode) {
    editorSheet.innerHTML = defaultSheetEditorTemplate;
    initializeSpreadsheetWorkbook(editorSheet, documentItem.content || null);
  } else {
    spreadsheetWorkbook = null;
    spreadsheetSelection = null;
    editorWord.innerHTML = (documentItem.content && documentItem.content.trim().length > 0)
      ? documentItem.content
      : `<p class="doc-kicker">DOCUMENTO CONTROLADO · ${documentItem.version || 'v1.0'}</p><h1>${documentItem.name}</h1><h2>Resumen</h2><p>Categoría: ${documentItem.category || 'No definida'}</p><p>Proceso: ${documentItem.process || 'Sin proceso'}</p><p>Formato: ${(documentItem.format || 'DOC').toUpperCase()}</p><h2>Estado</h2><p>${documentItem.status || 'Por revisar'}</p>`;
  }

  setDocumentEditorSaveState('Guardado automaticamente');

  if (!modal.open) modal.showModal();
  refreshIcons();
}

function ensureDocumentManageModal() {
  if (document.querySelector('#document-manage-modal')) return;

  const modal = document.createElement('dialog');
  modal.id = 'document-manage-modal';
  modal.innerHTML = `
    <form id="document-manage-form">
      <header>
        <div>
          <span class="eyebrow">GESTION DOCUMENTAL</span>
          <h2 id="document-manage-title">Gestionar documento</h2>
        </div>
        <button class="icon-button" type="button" data-modal-close><i data-lucide="x"></i></button>
      </header>
      <div class="record-context">
        <span>Opciones</span>
        <strong id="document-manage-context">Documento seleccionado</strong>
        <em>Actualiza categoria y estado sin salir del modulo.</em>
      </div>
      <div class="form-grid">
        <label>Estado
          <select id="document-manage-status">
            <option>Vigente</option>
            <option>Por revisar</option>
            <option>Proximo a vencer</option>
            <option>Obsoleto</option>
          </select>
        </label>
        <label>Categoria
          <select id="document-manage-category">
            <option>Proceso</option>
            <option>Politica</option>
            <option>Formato</option>
            <option>Historico</option>
          </select>
        </label>
      </div>
      <footer>
        <span id="document-manage-feedback" class="dialog-feedback" aria-live="polite"></span>
        <div>
          <button class="secondary-button" type="button" id="document-manage-delete">Eliminar</button>
          <button class="secondary-button" type="button" data-modal-close>Cancelar</button>
          <button class="primary-button" type="submit">Guardar cambios</button>
        </div>
      </footer>
    </form>`;

  document.body.appendChild(modal);
  refreshIcons();
}

function openDocumentManageModal(documentId: string) {
  ensureDocumentManageModal();
  const doc = state.documents.find((item) => item.id === documentId);
  const modal = document.querySelector<HTMLDialogElement>('#document-manage-modal');
  if (!doc || !modal) return;

  state.currentManagedDocumentId = doc.id;
  const title = modal.querySelector<HTMLElement>('#document-manage-title');
  const context = modal.querySelector<HTMLElement>('#document-manage-context');
  const status = modal.querySelector<HTMLSelectElement>('#document-manage-status');
  const category = modal.querySelector<HTMLSelectElement>('#document-manage-category');
  const feedback = modal.querySelector<HTMLElement>('#document-manage-feedback');

  if (title) title.textContent = `Gestionar ${doc.name}`;
  if (context) context.textContent = `${doc.name} · ${(doc.format || 'DOC').toUpperCase()} · ${doc.version || 'v1.0'}`;
  if (status) status.value = doc.status || 'Por revisar';
  if (category) category.value = doc.category || 'Proceso';
  if (feedback) feedback.textContent = '';

  if (!modal.open) modal.showModal();
}

function ensurePersonStatusModal() {
  if (document.querySelector('#person-status-modal')) return;

  const modal = document.createElement('dialog');
  modal.id = 'person-status-modal';
  modal.innerHTML = `
    <form id="person-status-form">
      <header>
        <div>
          <span class="eyebrow">GESTION DE PERSONAS</span>
          <h2 id="person-status-title">Confirmar cambio de estado</h2>
        </div>
        <button class="icon-button" type="button" data-modal-close><i data-lucide="x"></i></button>
      </header>
      <div class="record-context">
        <span>Persona seleccionada</span>
        <strong id="person-status-context">Sin persona</strong>
        <em id="person-status-message">Confirma para continuar.</em>
      </div>
      <footer>
        <span id="person-status-feedback" class="dialog-feedback" aria-live="polite"></span>
        <div>
          <button class="secondary-button" type="button" data-modal-close>Cancelar</button>
          <button id="person-status-confirm" class="primary-button" type="submit">Confirmar</button>
        </div>
      </footer>
    </form>`;

  document.body.appendChild(modal);
  refreshIcons();
}

function openPersonStatusModal(person: Person, nextStatus: string) {
  ensurePersonStatusModal();
  const modal = document.querySelector<HTMLDialogElement>('#person-status-modal');
  if (!modal) return;

  state.currentManagedPersonId = person.id;
  state.currentManagedPersonNextStatus = nextStatus;

  const title = modal.querySelector<HTMLElement>('#person-status-title');
  const context = modal.querySelector<HTMLElement>('#person-status-context');
  const message = modal.querySelector<HTMLElement>('#person-status-message');
  const confirm = modal.querySelector<HTMLElement>('#person-status-confirm');
  const feedback = modal.querySelector<HTMLElement>('#person-status-feedback');

  if (title) title.textContent = nextStatus === 'Inactivo' ? 'Inactivar persona' : 'Activar persona';
  if (context) context.textContent = `${person.name} · ${person.email || 'Sin correo'}`;
  if (message) message.textContent = nextStatus === 'Inactivo'
    ? 'La persona quedara en estado inactivo y podras reactivarla cuando quieras.'
    : 'La persona volvera a estado activo y estara disponible para asignaciones.';
  if (confirm) confirm.textContent = nextStatus === 'Inactivo' ? 'Si, inactivar' : 'Si, activar';
  if (feedback) feedback.textContent = '';

  if (!modal.open) modal.showModal();
}

function attachPersonStatusModalActions() {
  ensurePersonStatusModal();
  const modal = document.querySelector<HTMLDialogElement>('#person-status-modal');
  const form = document.querySelector<HTMLFormElement>('#person-status-form');
  if (!modal || !form) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.currentWorkspace || !state.currentManagedPersonId || !state.currentManagedPersonNextStatus) return;
    const feedback = form.querySelector<HTMLElement>('#person-status-feedback');

    try {
      await updatePerson(state.currentWorkspace.id, state.currentManagedPersonId, {
        status: state.currentManagedPersonNextStatus,
      });
      const people = await getPeople(state.currentWorkspace.id);
      renderPeopleSection(people);
      modal.close();
      state.currentManagedPersonId = null;
      state.currentManagedPersonNextStatus = null;
    } catch (error) {
      console.error('Error al cambiar estado de la persona:', error);
      if (feedback) feedback.textContent = 'No se pudo actualizar el estado.';
    }
  });
}

function attachDocumentManageModalActions() {
  ensureDocumentManageModal();
  const modal = document.querySelector<HTMLDialogElement>('#document-manage-modal');
  const form = document.querySelector<HTMLFormElement>('#document-manage-form');
  const deleteButton = document.querySelector<HTMLButtonElement>('#document-manage-delete');
  if (!modal || !form || !deleteButton) return;

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!state.currentWorkspace || !state.currentManagedDocumentId) return;

    const status = form.querySelector<HTMLSelectElement>('#document-manage-status')?.value || 'Por revisar';
    const category = form.querySelector<HTMLSelectElement>('#document-manage-category')?.value || 'Proceso';
    const feedback = form.querySelector<HTMLElement>('#document-manage-feedback');

    try {
      await updateDocument(state.currentWorkspace.id, state.currentManagedDocumentId, { status, category });
      const docs = await getDocuments(state.currentWorkspace.id);
      renderDocumentTable(docs);
      updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, docs.length);
      if (feedback) feedback.textContent = 'Documento actualizado correctamente.';
      modal.close();
      state.currentManagedDocumentId = null;
    } catch (error) {
      console.error('Error al actualizar documento:', error);
      if (feedback) feedback.textContent = 'No se pudo actualizar el documento.';
    }
  });

  deleteButton.addEventListener('click', async () => {
    if (!state.currentWorkspace || !state.currentManagedDocumentId) return;
    const feedback = form.querySelector<HTMLElement>('#document-manage-feedback');
    try {
      await deleteDocument(state.currentWorkspace.id, state.currentManagedDocumentId);
      const docs = await getDocuments(state.currentWorkspace.id);
      renderDocumentTable(docs);
      updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, docs.length);
      modal.close();
      state.currentManagedDocumentId = null;
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      if (feedback) feedback.textContent = 'No se pudo eliminar el documento.';
    }
  });
}

function renderAuditSection(audits: Audit[]) {
  state.audits = audits;
  const plannedAudits = audits.filter((audit) => !(audit.status || '').toLowerCase().includes('complet'));
  const completedAudits = audits.filter((audit) => (audit.status || '').toLowerCase().includes('complet'));
  const allFindings = Object.values(state.auditFindingsByAudit).flat();
  const openFindings = allFindings.filter((finding) => !(finding.status || '').toLowerCase().includes('cerrad'));
  const allCommitments = Object.values(state.auditCommitmentsByFinding).flat();
  const today = new Date();
  const commitmentsOnTime = allCommitments.filter((commitment) => {
    const status = (commitment.status || '').toLowerCase();
    if (status.includes('cerrad') || status.includes('complet')) return true;
    if (!commitment.due_date) return true;
    const due = new Date(commitment.due_date);
    if (Number.isNaN(due.getTime())) return true;
    return due.getTime() >= today.getTime();
  }).length;
  const onTimeCommitments = allCommitments.length > 0 ? Math.round((commitmentsOnTime / allCommitments.length) * 100) : 0;

  const auditQuarterCount = document.querySelector<HTMLElement>('#audit-quarter-count');
  const auditQuarterText = document.querySelector<HTMLElement>('#audit-quarter-text');
  const auditFindingsCount = document.querySelector<HTMLElement>('#audit-findings-count');
  const auditFindingsText = document.querySelector<HTMLElement>('#audit-findings-text');
  const auditCommitmentsPercent = document.querySelector<HTMLElement>('#audit-commitments-percent');
  const auditCommitmentsText = document.querySelector<HTMLElement>('#audit-commitments-text');
  const auditTabPlannedCount = document.querySelector<HTMLElement>('#audit-tab-planned-count');
  const auditTabCompletedCount = document.querySelector<HTMLElement>('#audit-tab-completed-count');
  const auditTabFindingsCount = document.querySelector<HTMLElement>('#audit-tab-findings-count');

  if (auditQuarterCount) auditQuarterCount.textContent = String(audits.length);
  if (auditQuarterText) auditQuarterText.textContent = `${completedAudits.length} realizadas · ${plannedAudits.length} programadas`;
  if (auditFindingsCount) auditFindingsCount.textContent = String(openFindings.length);
  if (auditFindingsText) auditFindingsText.textContent = `${openFindings.length} requieren atención inmediata`;
  if (auditCommitmentsPercent) auditCommitmentsPercent.textContent = `${onTimeCommitments}%`;
  if (auditCommitmentsText) auditCommitmentsText.textContent = `${commitmentsOnTime} de ${allCommitments.length} dentro de plazo`;
  if (auditTabPlannedCount) auditTabPlannedCount.textContent = String(plannedAudits.length);
  if (auditTabCompletedCount) auditTabCompletedCount.textContent = String(completedAudits.length);
  if (auditTabFindingsCount) auditTabFindingsCount.textContent = String(openFindings.length);

  const tabs = document.querySelectorAll<HTMLButtonElement>('#audit-tabs [data-audit-tab]');
  tabs.forEach((tab) => tab.classList.toggle('active', tab.dataset.auditTab === state.currentAuditTab));

  const tabContent = document.querySelector<HTMLElement>('#audit-tab-content');
  if (!tabContent) return;

  const auditsForTab = state.currentAuditTab === 'completed' ? completedAudits : plannedAudits;
  if (state.currentAuditTab === 'findings') {
    tabContent.innerHTML = `
      <div class="audit-layout">
        <article class="panel">
          <div class="panel-header"><div><h3>Hallazgos registrados</h3><p>${allFindings.length} hallazgo(s) en base de datos.</p></div></div>
          <div class="audit-list">
            ${allFindings.length > 0
              ? allFindings.map((finding) => {
                const audit = audits.find((item) => item.id === finding.audit_id);
                const commitments = state.auditCommitmentsByFinding[finding.id] || [];
                return `<button type="button" data-finding-open="${finding.id}" data-finding-audit="${finding.audit_id}"><span class="audit-date"><b>${commitments.length}</b><small>ACC</small></span><span><strong>${finding.title}</strong><small>${audit?.title || 'Auditoria'} · Estado: ${finding.status || 'Abierto'}</small></span><em class="tag review">${finding.action_status || 'Por iniciar'}</em><i data-lucide="chevron-right"></i></button>`;
              }).join('')
              : '<div class="empty-state"><strong>Sin hallazgos.</strong><p>Registra el primer hallazgo de auditoria para hacer seguimiento.</p></div>'}
          </div>
        </article>
      </div>`;
  } else {
    tabContent.innerHTML = `
      <div class="audit-layout">
        <article class="panel">
          <div class="panel-header"><div><h3>Plan de auditoria</h3><p>${state.currentAuditTab === 'completed' ? 'Auditorias realizadas' : 'Proximas revisiones internas'}</p></div><button class="text-button" type="button" data-open="audit-modal">${state.currentAuditTab === 'completed' ? 'Registrar nueva' : 'Planificar'}</button></div>
          <div class="audit-list">
            ${auditsForTab.length > 0
              ? auditsForTab.map((audit) => {
                const dateValue = audit.completed_at || '';
                const dateParts = dateValue.split('-');
                const dateLabel = dateParts.length === 3 ? `${dateParts[2]} ${dateParts[1].toUpperCase().slice(0, 3)}` : 'SF';
                return `<button type="button" data-audit-open="${audit.id}"><span class="audit-date"><b>${dateLabel.split(' ')[0] || 'SF'}</b><small>${dateLabel.split(' ')[1] || ''}</small></span><span><strong>${audit.title}</strong><small>${audit.executed_by || 'Sin responsable'} · ${audit.status || 'Programada'}</small></span><em class="tag review">${audit.status || 'Programada'}</em><i data-lucide="chevron-right"></i></button>`;
              }).join('')
              : '<div class="empty-state"><strong>Sin auditorias en esta vista.</strong><p>Crea o actualiza auditorias para poblar esta seccion.</p></div>'}
          </div>
        </article>
      </div>`;
  }

  refreshIcons();
}

function renderPeopleSection(people: Person[]) {
  state.people = people;
  const teamGrid = document.querySelector<HTMLElement>('.team-grid');
  if (!teamGrid) return;
  teamGrid.innerHTML = people
    .map((person) => `
      <article class="panel team-card" data-person-id="${person.id}">
        <span class="person-large teal">${person.name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase()}</span>
        <h3>${person.name}</h3>
        <p>${person.position || 'Sin cargo definido'}</p>
        <small>${person.email || 'Sin correo registrado'}</small>
        <em class="tag ${(person.status || 'Activo').toLowerCase().includes('inact') ? 'review' : 'done'}">${person.status || 'Activo'}</em>
        <div>
          <span><i data-lucide="git-branch"></i>0 procesos</span>
          <span><i data-lucide="circle-check-big"></i>0 acciones</span>
        </div>
        <button type="button" data-person-toggle-status="${person.id}">${(person.status || 'Activo').toLowerCase().includes('inact') ? 'Activar persona' : 'Inactivar persona'}</button>
      </article>`)
    .join('');
  populatePersonRoleOptions();
  populateProcessOwnerOptions();
  populateRiskOwnerOptions();
  populateActionAssigneeOptions();
  refreshIcons();
}

function renderRolesSection(roles: Role[]) {
  state.roles = roles;
  populatePersonRoleOptions();
  populateProcessOwnerOptions();
  const rolesTable = document.querySelector<HTMLElement>('.roles-table');
  if (!rolesTable) return;

  if (!state.currentRoleId && roles[0]) {
    state.currentRoleId = roles[0].id;
  }

  rolesTable.innerHTML = `
    <div class="panel-header">
      <div>
        <h3>Cargos de la organización</h3>
        <p>${roles.length} cargos activos</p>
      </div>
      <div class="search-field">
        <i data-lucide="search"></i>
        <input placeholder="Buscar cargo" />
      </div>
    </div>
    ${roles
      .map((role) => {
        const participationEntries = getRoleParticipationEntries(role);
        const selectedClass = role.id === state.currentRoleId ? ' selected-role' : '';
        return `
        <div class="role-row${selectedClass}" data-role-id="${role.id}">
          <span class="role-icon blue"><i data-lucide="briefcase-business"></i></span>
          <span><strong>${role.name}</strong><small>${role.authority || role.purpose || 'Sin propósito definido'}</small></span>
          <span class="role-count">${participationEntries.length} ${participationEntries.length === 1 ? 'proceso' : 'procesos'}</span>
          <i data-lucide="chevron-right"></i>
        </div>`;
      })
      .join('')}`;

  rolesTable.querySelectorAll<HTMLElement>('.role-row').forEach((row) => {
    row.addEventListener('click', () => {
      const roleId = row.dataset.roleId;
      if (!roleId) return;
      state.currentRoleId = roleId;
      renderRolesSection(state.roles);
    });
  });

  renderSelectedRoleDetail(roles.find((role) => role.id === state.currentRoleId) || roles[0] || null);
  refreshIcons();
}

function getRoleParticipationEntries(role: Role) {
  const rawParticipation = role.process_participation;
  const participationObject = rawParticipation && typeof rawParticipation === 'object'
    ? rawParticipation
    : {};

  return Object.entries(participationObject)
    .filter(([, value]) => typeof value === 'string' && value.length > 0)
    .map(([processId, value]) => ({
      processId,
      code: String(value),
      processName: state.processes.find((process) => process.id === processId)?.name || 'Proceso no encontrado',
    }));
}

function renderSelectedRoleDetail(role: Role | null) {
  const title = document.querySelector<HTMLElement>('#role-selected-title');
  const owner = document.querySelector<HTMLElement>('#role-selected-owner');
  const summary = document.querySelector<HTMLElement>('#role-selected-summary');
  const responsibilitiesList = document.querySelector<HTMLElement>('#role-selected-responsibilities');
  const participation = document.querySelector<HTMLElement>('#role-selected-participation');
  const editButton = document.querySelector<HTMLButtonElement>('#edit-selected-role');
  const deleteButton = document.querySelector<HTMLButtonElement>('#delete-selected-role');

  if (!role) {
    if (title) title.textContent = 'Sin cargos creados';
    if (owner) owner.textContent = 'Responsable: no asignado';
    if (summary) summary.textContent = 'Crea un cargo para definir responsabilidades.';
    if (responsibilitiesList) responsibilitiesList.innerHTML = '<li><i data-lucide="check"></i>Sin responsabilidades definidas.</li>';
    if (participation) participation.innerHTML = '<strong>Participación en procesos</strong><span>Sin participación asignada</span>';
    if (editButton) editButton.disabled = true;
    if (deleteButton) deleteButton.disabled = true;
    refreshIcons();
    return;
  }

  if (title) title.textContent = role.name;
  if (owner) owner.textContent = `Responsable: ${role.owner || 'Sin responsable'}`;
  if (summary) summary.textContent = role.purpose || 'Sin propósito definido.';
  if (editButton) {
    editButton.disabled = false;
    editButton.dataset.roleId = role.id;
  }
  if (deleteButton) {
    deleteButton.disabled = false;
    deleteButton.dataset.roleId = role.id;
  }

  const responsibilities = (role.responsibilities || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  if (responsibilitiesList) {
    responsibilitiesList.innerHTML = (responsibilities.length > 0 ? responsibilities : ['Sin responsabilidades definidas.'])
      .map((item) => `<li><i data-lucide="check"></i>${item}</li>`)
      .join('');
  }

  const entries = getRoleParticipationEntries(role);
  if (participation) {
    participation.innerHTML = `<strong>Participación en procesos</strong>${entries.length > 0
      ? entries.map((entry) => `<span><b class="${entry.code === 'A' ? 'a' : entry.code === 'C' ? 'c' : entry.code === 'I' ? 'i' : ''}">${entry.code}</b> ${entry.processName}</span>`).join('')
      : '<span>Sin participación asignada</span>'}`;
  }

  refreshIcons();
}

function updateNavCounts(processCount: number, actionCount: number, riskCount: number, improvementCount = 0) {
  const processBadge = document.querySelector<HTMLElement>('#nav-process-count');
  const actionBadge = document.querySelector<HTMLElement>('#nav-action-count');
  const riskBadge = document.querySelector<HTMLElement>('#nav-risk-count');
  const improvementBadge = document.querySelector<HTMLElement>('#nav-improvement-count');

  if (processBadge) processBadge.textContent = String(processCount);
  if (actionBadge) actionBadge.textContent = String(actionCount);
  if (riskBadge) riskBadge.textContent = String(riskCount);
  if (improvementBadge) improvementBadge.textContent = String(improvementCount);
}

function updateDashboardWidgets() {
  const performancePanel = document.querySelector<HTMLElement>('[data-dashboard-widget="performance"]');
  if (performancePanel) {
    const allMeasurements = Object.values(state.measurementsByIndicator).flat();
    const parsedMeasurements = allMeasurements
      .map((measurement) => ({
        date: measurement.date || measurement.registered_at || '',
        value: parseNumericValue(measurement.result || ''),
      }))
      .filter((item) => item.date && item.value !== null) as Array<{ date: string; value: number }>;

    parsedMeasurements.sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime());

    const monthLabels: string[] = [];
    const monthSeriesMap = new Map<string, number[]>();
    parsedMeasurements.forEach((item) => {
      const date = new Date(item.date);
      if (Number.isNaN(date.getTime())) return;
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthSeriesMap.has(key)) monthSeriesMap.set(key, []);
      monthSeriesMap.get(key)!.push(item.value);
    });

    const monthEntries = Array.from(monthSeriesMap.entries()).slice(-6);
    monthEntries.forEach(([key]) => {
      const [year, month] = key.split('-').map(Number);
      const date = new Date(year, (month || 1) - 1, 1);
      monthLabels.push(date.toLocaleDateString('es-CO', { month: 'short' }));
    });

    let series = monthEntries.map(([, values]) => Math.round(values.reduce((sum, value) => sum + value, 0) / values.length));

    if (series.length === 0) {
      const indicatorValues = state.indicators
        .map((indicator) => {
          const snapshot = getIndicatorSnapshot(indicator);
          return parseNumericValue(snapshot.currentValue);
        })
        .filter((value) => value !== null) as number[];
      if (indicatorValues.length > 0) {
        const avg = Math.round(indicatorValues.reduce((sum, value) => sum + value, 0) / indicatorValues.length);
        series = [avg, avg, avg, avg, avg, avg];
        monthLabels.splice(0, monthLabels.length, 'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun');
      }
    }

    if (series.length > 0) {
      const min = Math.max(0, Math.min(...series) - 10);
      const max = Math.min(100, Math.max(...series) + 10);
      const range = Math.max(1, max - min);
      const width = 590;
      const height = 170;
      const step = series.length > 1 ? width / (series.length - 1) : width;
      const points = series.map((value, index) => {
        const ratio = (value - min) / range;
        const y = Math.round(height - ratio * height);
        return { x: Math.round(index * step), y };
      });

      const target = Math.round(state.indicators
        .map((indicator) => parseNumericValue(getIndicatorGoalLabel(indicator)))
        .filter((value) => value !== null)
        .reduce((sum, value, _, array) => sum + (value as number) / array.length, 0));
      const normalizedTarget = Number.isFinite(target) && target > 0 ? target : 70;
      const targetY = Math.round(height - ((normalizedTarget - min) / range) * height);

      const path = points.map((point, index) => `${index === 0 ? 'M' : 'L'}${point.x},${point.y}`).join(' ');
      const area = `${path} L${width},${height} L0,${height} Z`;
      const lastPoint = points[points.length - 1];

      const targetPath = performancePanel.querySelector<SVGPathElement>('path.target-line');
      const areaPath = performancePanel.querySelector<SVGPathElement>('path.area-fill');
      const trendPath = performancePanel.querySelector<SVGPathElement>('path.trend-line');
      const lastCircle = performancePanel.querySelector<SVGCircleElement>('svg circle');
      const xAxis = performancePanel.querySelector<HTMLElement>('.x-axis');

      if (targetPath) targetPath.setAttribute('d', `M0,${targetY} L${width},${targetY}`);
      if (areaPath) areaPath.setAttribute('d', area);
      if (trendPath) trendPath.setAttribute('d', path);
      if (lastCircle) {
        lastCircle.setAttribute('cx', String(lastPoint.x));
        lastCircle.setAttribute('cy', String(lastPoint.y));
      }
      if (xAxis && monthLabels.length > 0) {
        xAxis.innerHTML = monthLabels.map((label) => `<span>${label}</span>`).join('');
      }
    }
  }

  const attentionPanel = document.querySelector<HTMLElement>('[data-dashboard-widget="attention"] .attention-list');
  if (attentionPanel) {
    const criticalRiskItems = state.risks
      .filter((risk) => (risk.probability || '').toLowerCase().includes('alta') || (risk.impact || '').toLowerCase().includes('alto'))
      .slice(0, 2)
      .map((risk) => `<button data-view-target="risks"><span class="attention-icon risk-bg"><i data-lucide="shield-alert"></i></span><span><strong>Riesgo: ${risk.name}</strong><small>${getProcessName(risk.process_id)}</small></span><em class="tag critical">Crítico</em><i data-lucide="chevron-right"></i></button>`);

    const pendingActions = state.actions
      .filter((action) => !(action.status || '').toLowerCase().includes('complet'))
      .slice(0, 2)
      .map((action) => `<button data-view-target="actions"><span class="attention-icon action-bg"><i data-lucide="clock-3"></i></span><span><strong>${action.title}</strong><small>${action.due_date ? `Vence ${formatDisplayDate(action.due_date)}` : 'Sin fecha límite'}</small></span><em class="tag medium">Pendiente</em><i data-lucide="chevron-right"></i></button>`);

    const docsToReview = state.documents
      .filter((doc) => !(doc.status || '').toLowerCase().includes('vigen'))
      .slice(0, 1)
      .map((doc) => `<button data-view-target="documents"><span class="attention-icon doc-bg"><i data-lucide="file-clock"></i></span><span><strong>${doc.name}</strong><small>${doc.status || 'Revisión requerida'}</small></span><em class="tag review">Por revisar</em><i data-lucide="chevron-right"></i></button>`);

    const items = [...criticalRiskItems, ...pendingActions, ...docsToReview].slice(0, 3);
    attentionPanel.innerHTML = items.length > 0
      ? items.join('')
      : '<div class="empty-state"><strong>Sin prioridades críticas.</strong><p>No hay alertas pendientes para esta semana.</p></div>';
  }

  const processBars = document.querySelector<HTMLElement>('[data-dashboard-widget="processes"] .process-bars');
  if (processBars) {
    const topProcesses = [...state.processes]
      .sort((left, right) => (right.health || 0) - (left.health || 0))
      .slice(0, 4);
    processBars.innerHTML = topProcesses.length > 0
      ? topProcesses
        .map((process) => {
          const health = Math.max(0, Math.min(100, process.health || 0));
          const barClass = health < 75 ? 'yellow' : '';
          return `<div><span>${process.name}</span><b><i style="width:${health}%" class="${barClass}"></i></b><strong>${health}%</strong></div>`;
        })
        .join('')
      : '<div class="empty-state"><strong>Sin procesos.</strong><p>Crea procesos para visualizar salud operativa.</p></div>';
  }

  const aiCard = document.querySelector<HTMLElement>('[data-dashboard-widget="ai"]');
  if (aiCard) {
    const opportunities = Math.max(
      state.risks.filter((risk) => (risk.probability || '').toLowerCase().includes('alta') || (risk.impact || '').toLowerCase().includes('alto')).length,
      state.actions.filter((action) => !(action.status || '').toLowerCase().includes('complet')).length,
      state.documents.filter((doc) => !(doc.status || '').toLowerCase().includes('vigen')).length,
    );

    const heading = aiCard.querySelector<HTMLElement>('h3');
    const text = aiCard.querySelector<HTMLElement>('p');
    if (heading) heading.textContent = `Encontré ${opportunities} oportunidad${opportunities === 1 ? '' : 'es'} para mejorar la operación.`;
    if (text) text.textContent = `Datos reales: ${state.processes.length} procesos, ${state.actions.length} acciones, ${state.risks.length} riesgos y ${state.documents.length} documentos cargados.`;
  }
}

function updateDashboardSummary(processCount = state.processes.length, actionCount = state.actions.length, riskCount = state.risks.length, documentCount = state.documents.length) {
  recalculateProcessesHealth();
  const healthScore = processCount > 0 ? Math.round(state.processes.reduce((sum, item) => sum + (item.health || 0), 0) / processCount) : 0;
  const completedActions = state.actions.filter((action) => (action.status || '').toLowerCase().includes('completadas') || (action.status || '').toLowerCase().includes('completado')).length;
  const performanceScore = actionCount > 0 ? Math.round((completedActions / actionCount) * 100) : 0;
  const compliantDocuments = state.documents.filter((doc) => (doc.status || '').toLowerCase().includes('vigen')).length;
  const complianceScore = documentCount > 0 ? Math.round((compliantDocuments / documentCount) * 100) : 0;
  const goodProcesses = state.processes.filter((process) => (process.health || 0) >= 70).length;
  const attentionActions = state.actions.filter((action) => !(action.status || '').toLowerCase().includes('completadas')).length;
  const criticalRisks = state.risks.filter((risk) => (risk.status || '').toLowerCase().includes('crítico') || (risk.probability || '').toLowerCase().includes('alto') || (risk.impact || '').toLowerCase().includes('alto')).length;
  const newRisksThisWeek = state.risks.filter((risk) => {
    if (!risk.created_at) return false;
    const created = new Date(risk.created_at);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return created >= weekAgo;
  }).length;
  const reviewDocuments = state.documents.filter((doc) => !(doc.status || '').toLowerCase().includes('vigen')).length;

  const scoreEl = document.querySelector<HTMLElement>('#dashboard-health-score');
  const titleEl = document.querySelector<HTMLElement>('#dashboard-health-title');
  const trendEl = document.querySelector<HTMLElement>('#dashboard-health-trend');
  const processMetricEl = document.querySelector<HTMLElement>('#dashboard-metric-processes');
  const performanceMetricEl = document.querySelector<HTMLElement>('#dashboard-metric-performance');
  const complianceMetricEl = document.querySelector<HTMLElement>('#dashboard-metric-compliance');
  const processMetricChangeEl = document.querySelector<HTMLElement>('#dashboard-metric-processes-change');
  const performanceMetricChangeEl = document.querySelector<HTMLElement>('#dashboard-metric-performance-change');
  const complianceMetricChangeEl = document.querySelector<HTMLElement>('#dashboard-metric-compliance-change');
  const processGoodEl = document.querySelector<HTMLElement>('#dashboard-summary-processes');
  const processGoodLabelEl = document.querySelector<HTMLElement>('#dashboard-summary-processes-good');
  const actionsOpenEl = document.querySelector<HTMLElement>('#dashboard-summary-actions');
  const actionsAttentionEl = document.querySelector<HTMLElement>('#dashboard-summary-actions-attention');
  const risksCriticalEl = document.querySelector<HTMLElement>('#dashboard-summary-risks');
  const risksNewEl = document.querySelector<HTMLElement>('#dashboard-summary-risks-new');
  const documentsReviewEl = document.querySelector<HTMLElement>('#dashboard-summary-documents');
  const documentsReviewTextEl = document.querySelector<HTMLElement>('#dashboard-summary-documents-text');

  if (scoreEl) scoreEl.innerHTML = `${healthScore}<small>/100</small>`;
  if (titleEl) titleEl.textContent = healthScore >= 70 ? 'La empresa está en buen rumbo' : 'Necesita atención en salud empresarial';
  if (trendEl) trendEl.textContent = `Basado en ${processCount} procesos y ${actionCount} acciones.`;
  if (processMetricEl) processMetricEl.innerHTML = `${healthScore}<small>%</small>`;
  if (performanceMetricEl) performanceMetricEl.innerHTML = `${performanceScore}<small>%</small>`;
  if (complianceMetricEl) complianceMetricEl.innerHTML = `${complianceScore}<small>%</small>`;
  if (processMetricChangeEl) processMetricChangeEl.textContent = `${goodProcesses}/${Math.max(processCount, 1)} en buen estado`;
  if (performanceMetricChangeEl) {
    performanceMetricChangeEl.classList.toggle('positive', performanceScore >= 70);
    performanceMetricChangeEl.classList.toggle('negative', performanceScore < 70);
    performanceMetricChangeEl.textContent = `${completedActions}/${Math.max(actionCount, 1)} completadas`;
  }
  if (complianceMetricChangeEl) complianceMetricChangeEl.textContent = `${compliantDocuments}/${Math.max(documentCount, 1)} vigentes`;
  if (complianceMetricChangeEl) {
    complianceMetricChangeEl.classList.toggle('positive', complianceScore >= 70);
    complianceMetricChangeEl.classList.toggle('negative', complianceScore < 70);
  }
  if (processGoodEl) processGoodEl.textContent = String(processCount);
  if (processGoodLabelEl) processGoodLabelEl.textContent = String(goodProcesses);
  if (actionsOpenEl) actionsOpenEl.textContent = String(actionCount);
  if (actionsAttentionEl) actionsAttentionEl.textContent = String(attentionActions);
  if (risksCriticalEl) risksCriticalEl.textContent = String(criticalRisks);
  if (risksNewEl) risksNewEl.textContent = String(newRisksThisWeek);
  if (documentsReviewEl) documentsReviewEl.textContent = String(reviewDocuments);
  if (documentsReviewTextEl) documentsReviewTextEl.innerHTML = reviewDocuments > 0 ? `Próximos ${reviewDocuments} días <i data-lucide="arrow-right"></i>` : 'Sin documentos por revisar';
  updateDashboardWidgets();
  updateNavCounts(processCount, actionCount, riskCount, state.improvementPlans.filter((plan) => !(plan.status || '').toLowerCase().includes('cerrad')).length);
  refreshIcons();
}

async function loadWorkspaceData(workspaceId: string) {
  const getResult = <T>(result: PromiseSettledResult<T>, scope: string, fallback: T): T => {
    if (result.status === 'fulfilled') return result.value;
    console.error(`Error al cargar ${scope}:`, result.reason);
    return fallback;
  };

  const [
    processesResult,
    companiesResult,
    indicatorsResult,
    risksResult,
    actionsResult,
    improvementPlansResult,
    documentsResult,
    auditsResult,
    peopleResult,
    rolesResult,
  ] = await Promise.allSettled([
    getProcesses(workspaceId),
    getCompanies(workspaceId),
    getIndicators(workspaceId),
    getRisks(workspaceId),
    getActions(workspaceId),
    getImprovementPlans(workspaceId),
    getDocuments(workspaceId),
    getAudits(workspaceId),
    getPeople(workspaceId),
    getRoles(workspaceId),
  ]);

  const processes = getResult(processesResult, 'procesos', [] as ProcessSummary[]);
  const companies = getResult(companiesResult, 'empresa', [] as Company[]);
  const indicators = getResult(indicatorsResult, 'indicadores', [] as Indicator[]);
  const risks = getResult(risksResult, 'riesgos', [] as Risk[]);
  const actions = getResult(actionsResult, 'acciones', [] as Action[]);
  const improvementPlans = getResult(improvementPlansResult, 'planes de mejora', [] as ImprovementPlan[]);
  const documents = getResult(documentsResult, 'documentos', [] as Document[]);
  const audits = getResult(auditsResult, 'auditorias', [] as Audit[]);
  const people = getResult(peopleResult, 'personas', [] as Person[]);
  const roles = getResult(rolesResult, 'roles', [] as Role[]);

  state.processes = processes;
  state.indicators = indicators;
  state.risks = risks;
  state.actions = actions;
  state.improvementPlans = improvementPlans;
  state.documents = documents;
  state.audits = audits;
  state.people = people;
  state.roles = roles;

  await hydrateAuditRelations(audits);

  const followupsEntries = await Promise.all(
    improvementPlans.map(async (plan) => {
      try {
        const followups = await getImprovementFollowups(plan.id);
        return [plan.id, followups] as const;
      } catch (error) {
        console.error(`Error al cargar seguimientos del plan ${plan.id}:`, error);
        return [plan.id, [] as ImprovementFollowup[]] as const;
      }
    }),
  );
  state.improvementFollowupsByPlan = Object.fromEntries(followupsEntries);

  await loadMeasurementsByIndicator(indicators);

  renderProcessCards(processes);
  renderIndicatorSection(indicators);
  renderRiskSection(risks);
  renderActionBoard(actions);
  renderImprovementSection();
  renderDocumentTable(documents);
  renderAuditSection(audits);
  renderPeopleSection(people);
  renderCompanySection(companies);
  renderRolesSection(roles);
  renderProcessDetail(state.currentProcessId, state.currentProcessTab);
  updateNavCounts(processes.length, actions.length, risks.length, improvementPlans.filter((plan) => !(plan.status || '').toLowerCase().includes('cerrad')).length);
  updateDashboardSummary(processes.length, actions.length, risks.length, documents.length);
}

async function initializeApp() {
  const { data } = await getCurrentSession();
  const session = data.session;
  if (!session) {
    setAuthenticated(false);
    return;
  }

  setAuthenticated(true);
  const profile = await getCurrentProfile();
  state.currentProfile = profile;
  if (profile) setUserProfile(profile);
  const workspaces = await getWorkspaces();
  if (workspaces.length > 0) {
    renderWorkspaceMenu(workspaces);
    if (state.currentWorkspace) {
      await loadWorkspaceData(state.currentWorkspace.id);
    }
  }
}

function getLoginErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error || '');
  if (/invalid login credentials/i.test(message)) return 'Correo o contraseña incorrectos.';
  if (/email not confirmed/i.test(message)) return 'Debes confirmar tu correo antes de ingresar.';
  if (/failed to fetch|network|load failed/i.test(message)) return 'No se pudo conectar con el servicio de autenticación.';
  if (/rate limit|too many requests/i.test(message)) return 'Demasiados intentos. Espera unos minutos y vuelve a intentar.';
  return message || 'No fue posible iniciar sesión.';
}

function showLoginError(show: boolean, message = 'Correo o contraseña incorrectos.') {
  const loginError = document.querySelector<HTMLElement>('#login-error');
  if (loginError) {
    loginError.textContent = message;
    loginError.hidden = !show;
  }
}

function attachAuthActions() {
  const loginForm = document.querySelector('#login-form') as HTMLFormElement | null;
  if (!loginForm) return;

  loginForm.addEventListener('submit', async (event) => {
    event.preventDefault();
    const email = (document.querySelector('#login-username') as HTMLInputElement).value.trim();
    const password = (document.querySelector('#login-password') as HTMLInputElement).value;
    if (!email || !password) {
      showLoginError(true);
      return;
    }

    const submitButton = loginForm.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (submitButton) submitButton.disabled = true;
    try {
      const { data, error } = await signIn(email, password);
      if (error || !data.session) {
        showLoginError(true, getLoginErrorMessage(error));
        return;
      }

      showLoginError(false);
      setAuthenticated(true);
      await initializeApp();
    } catch (error) {
      console.error('Error de inicio de sesión:', error);
      showLoginError(true, getLoginErrorMessage(error));
    } finally {
      if (submitButton) submitButton.disabled = false;
    }
  });

  const logoutButton = document.querySelector('#logout-button');
  if (logoutButton) {
    logoutButton.addEventListener('click', async () => {
      await signOut();
      setAuthenticated(false);
      window.location.reload();
    });
  }
}

function attachNavigation() {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const viewTrigger = target.closest<HTMLElement>('[data-view]');
    if (viewTrigger?.dataset.view) {
      event.preventDefault();
      selectView(viewTrigger.dataset.view);
      return;
    }

    const targetTrigger = target.closest<HTMLElement>('[data-view-target]');
    if (targetTrigger?.dataset.viewTarget) {
      event.preventDefault();
      selectView(targetTrigger.dataset.viewTarget);
    }
  });
}

function openModalById(modalId: string) {
  const modal = document.getElementById(modalId) as HTMLDialogElement | null;
  if (!modal) return;
  if (modalId === 'company-modal') {
    populateCompanyForm(state.currentCompany);
  }
  if (modalId === 'process-modal') {
    prepareProcessModalForCreate();
  }
  if (modalId === 'risk-modal') {
    prepareRiskModalForCreate();
  }
  if (modalId === 'role-modal') {
    prepareRoleModalForCreate();
  }
  if (modalId === 'person-modal') {
    populatePersonRoleOptions();
  }
  if (modalId === 'action-modal') {
    prepareActionModalForCreate();
  }
  if (modalId === 'document-modal') {
    prepareDocumentModalForCreate();
  }
  if (modalId === 'audit-modal') {
    state.currentAuditId = null;
    prepareAuditModalForCreate();
  }
  if (modalId === 'finding-modal') {
    const fallbackAuditId = state.currentAuditId || state.audits[0]?.id || null;
    if (fallbackAuditId) prepareFindingModalForCreate(fallbackAuditId);
  }
  if (modalId === 'improvement-modal') {
    prepareImprovementModalForCreate();
  }
  if (modalId === 'improvement-followup-modal') {
    prepareImprovementFollowupModal();
  }
  if (!modal.open) modal.showModal();
}

function closeNearestModal(trigger: HTMLElement) {
  const modal = trigger.closest('dialog') as HTMLDialogElement | null;
  if (modal?.open) modal.close();
}

function attachModalControls() {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const openButton = target.closest<HTMLElement>('[data-open]');
    if (openButton?.dataset.open) {
      event.preventDefault();
      openModalById(openButton.dataset.open);
      return;
    }

    const closeButton = target.closest<HTMLElement>('[data-modal-close], button[value="cancel"]');
    if (closeButton) {
      event.preventDefault();
      closeNearestModal(closeButton);
    }
  });

  document.querySelectorAll<HTMLDialogElement>('dialog').forEach((modal) => {
    modal.addEventListener('click', (event) => {
      // Close when clicking backdrop area to avoid stuck dialogs.
      if (event.target === modal && modal.open) {
        modal.close();
      }
    });
    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      modal.close();
    });
  });
}

function attachIndicatorInteractions() {
  const tabs = document.querySelector<HTMLElement>('#indicator-tabs');
  if (tabs) {
    tabs.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-indicator-tab]');
      if (!button) return;
      const tab = button.dataset.indicatorTab as 'followup' | 'catalog' | 'calendar' | undefined;
      if (!tab) return;
      tabs.querySelectorAll<HTMLButtonElement>('button[data-indicator-tab]').forEach((item) => {
        item.classList.toggle('active', item === button);
      });
      renderIndicatorTabContent(tab);
    });
  }

  const tabContent = document.querySelector<HTMLElement>('#indicator-tab-content');
  if (!tabContent) return;
  tabContent.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const tabTargetButton = target.closest<HTMLButtonElement>('[data-indicator-tab-target]');
    if (tabTargetButton) {
      const nextTab = tabTargetButton.dataset.indicatorTabTarget as 'followup' | 'catalog' | 'calendar' | undefined;
      if (!nextTab) return;
      const tabsHost = document.querySelector<HTMLElement>('#indicator-tabs');
      tabsHost?.querySelectorAll<HTMLButtonElement>('button[data-indicator-tab]').forEach((item) => {
        item.classList.toggle('active', item.dataset.indicatorTab === nextTab);
      });
      renderIndicatorTabContent(nextTab);
      return;
    }

    const openDetailButton = target.closest<HTMLButtonElement>('[data-indicator-open]');
    if (openDetailButton) {
      const indicatorId = openDetailButton.dataset.indicatorOpen;
      if (!indicatorId) return;
      renderIndicatorDetail(indicatorId);
      selectView('indicator-detail');
      return;
    }

    const openMeasurementButton = target.closest<HTMLButtonElement>('[data-open-measurement]');
    if (openMeasurementButton) {
      const indicatorId = openMeasurementButton.dataset.openMeasurement;
      if (!indicatorId) return;
      openMeasurementModal(indicatorId);
    }
  });
}

function attachRiskInteractions() {
  const risksView = document.querySelector<HTMLElement>('#risks');
  if (risksView) {
    risksView.addEventListener('change', (event) => {
      const target = event.target as HTMLElement;
      const processFilter = target.closest<HTMLSelectElement>('[data-risk-process-filter]');
      if (!processFilter) return;
      state.currentRiskProcessFilter = processFilter.value || null;
      state.currentRiskPage = 1;
      renderRiskSection(state.risks);
    });

    risksView.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const pageButton = target.closest<HTMLButtonElement>('[data-risk-page-action]');
      if (pageButton) {
        const action = pageButton.dataset.riskPageAction;
        if (action === 'prev') state.currentRiskPage -= 1;
        if (action === 'next') state.currentRiskPage += 1;
        renderRiskSection(state.risks);
        return;
      }

      const openRiskButton = target.closest<HTMLButtonElement>('[data-risk-open]');
      if (!openRiskButton) return;
      const riskId = openRiskButton.dataset.riskOpen;
      if (!riskId) return;
      state.currentRiskId = riskId;
      renderRiskDetail(riskId);
      selectView('risk-detail');
    });
  }

  const riskDetailView = document.querySelector<HTMLElement>('#risk-detail');
  if (riskDetailView) {
    riskDetailView.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const editButton = target.closest<HTMLButtonElement>('[data-risk-edit]');
      if (!editButton) return;
      const riskId = editButton.dataset.riskEdit;
      if (!riskId) return;
      const risk = state.risks.find((item) => item.id === riskId);
      if (!risk) return;
      prepareRiskModalForEdit(risk);
      (document.querySelector('#risk-modal') as HTMLDialogElement | null)?.showModal();
    });
  }
}

function attachActionInteractions() {
  const actionsView = document.querySelector<HTMLElement>('#actions');
  if (!actionsView) return;

  actionsView.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const scopeButton = target.closest<HTMLButtonElement>('[data-action-scope]');
    if (scopeButton?.dataset.actionScope) {
      event.preventDefault();
      state.currentActionScope = scopeButton.dataset.actionScope as 'mine' | 'all';
      state.actionVisibleCount = 6;
      renderActionBoard(state.actions);
      return;
    }

    const viewButton = target.closest<HTMLButtonElement>('[data-action-view]');
    if (viewButton?.dataset.actionView) {
      event.preventDefault();
      state.currentActionView = viewButton.dataset.actionView as 'board' | 'table' | 'calendar';
      state.actionVisibleCount = 6;
      renderActionBoard(state.actions);
      return;
    }

    const showMoreButton = target.closest<HTMLButtonElement>('#show-more-actions');
    if (showMoreButton) {
      event.preventDefault();
      state.actionVisibleCount += 6;
      renderActionBoard(state.actions);
      return;
    }

    const openAction = target.closest<HTMLElement>('[data-action-open]');
    if (openAction?.dataset.actionOpen) {
      const action = state.actions.find((item) => item.id === openAction.dataset.actionOpen);
      if (!action) return;
      prepareActionModalForEdit(action);
      (document.querySelector('#action-modal') as HTMLDialogElement | null)?.showModal();
    }
  });

  actionsView.addEventListener('change', (event) => {
    const target = event.target as HTMLElement;
    const hideCompleted = target.closest<HTMLInputElement>('#hide-completed');
    if (!hideCompleted) return;
    state.hideCompletedActions = hideCompleted.checked;
    state.actionVisibleCount = 6;
    renderActionBoard(state.actions);
  });
}

function attachImprovementInteractions() {
  const improvementView = document.querySelector<HTMLElement>('#improvement');
  if (improvementView) {
    improvementView.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;

      const createButton = target.closest<HTMLElement>('[data-improvement-create]');
      if (createButton) {
        state.currentImprovementPlanId = null;
      }

      const openPlan = target.closest<HTMLButtonElement>('[data-improvement-open]');
      if (!openPlan?.dataset.improvementOpen) return;
      state.currentImprovementPlanId = openPlan.dataset.improvementOpen;
      renderImprovementDetail(state.currentImprovementPlanId);
      selectView('improvement-detail');
    });
  }
}

function attachAuditInteractions() {
  const auditView = document.querySelector<HTMLElement>('#audit');
  if (!auditView) return;

  auditView.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const tabButton = target.closest<HTMLButtonElement>('#audit-tabs [data-audit-tab]');
    if (tabButton?.dataset.auditTab) {
      event.preventDefault();
      state.currentAuditTab = tabButton.dataset.auditTab as 'planned' | 'completed' | 'findings';
      renderAuditSection(state.audits);
      return;
    }

    const openAudit = target.closest<HTMLButtonElement>('[data-audit-open]');
    if (openAudit?.dataset.auditOpen) {
      event.preventDefault();
      const audit = state.audits.find((item) => item.id === openAudit.dataset.auditOpen);
      if (!audit) return;
      state.currentAuditId = audit.id;
      prepareAuditModalForEdit(audit);
      (document.querySelector('#audit-modal') as HTMLDialogElement | null)?.showModal();
      return;
    }

    const openFinding = target.closest<HTMLButtonElement>('[data-finding-open]');
    if (openFinding?.dataset.findingOpen && openFinding.dataset.findingAudit) {
      event.preventDefault();
      prepareFindingModalForEdit(openFinding.dataset.findingAudit, openFinding.dataset.findingOpen);
      (document.querySelector('#finding-modal') as HTMLDialogElement | null)?.showModal();
    }
  });
}

function attachDocumentInteractions() {
  const documentsView = document.querySelector<HTMLElement>('#documents');
  if (!documentsView) return;

  documentsView.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const folderButton = target.closest<HTMLButtonElement>('.document-folders [data-doc-filter]');
    if (folderButton?.dataset.docFilter) {
      event.preventDefault();
      state.currentDocumentCategoryFilter = folderButton.dataset.docFilter as 'all' | 'process' | 'policy' | 'format' | 'archive';
      renderDocumentTable(state.documents);
      return;
    }

    const statusButton = target.closest<HTMLButtonElement>('#document-status-filter');
    if (statusButton) {
      event.preventDefault();
      const order: Array<'all' | 'Vigente' | 'Por revisar' | 'Próximo a vencer'> = ['all', 'Vigente', 'Por revisar', 'Próximo a vencer'];
      const index = order.indexOf(state.currentDocumentStatusFilter);
      state.currentDocumentStatusFilter = order[(index + 1) % order.length];
      renderDocumentTable(state.documents);
      return;
    }

    const manageTrigger = target.closest<HTMLButtonElement>('[data-doc-manage]');
    if (manageTrigger?.dataset.docManage) {
      event.preventDefault();
      event.stopPropagation();
      openDocumentManageModal(manageTrigger.dataset.docManage);
      return;
    }

    const row = target.closest<HTMLElement>('[data-document-open]');
    if (row?.dataset.documentOpen) {
      event.preventDefault();
      openDocumentEditor(row.dataset.documentOpen);
    }
  });

  documentsView.addEventListener('change', (event) => {
    const target = event.target as HTMLElement;
    const processFilter = target.closest<HTMLSelectElement>('#document-process-filter');
    if (!processFilter) return;
    state.currentDocumentProcessFilter = processFilter.value;
    renderDocumentTable(state.documents);
  });

  documentsView.addEventListener('input', (event) => {
    const target = event.target as HTMLElement;
    const searchInput = target.closest<HTMLInputElement>('.search-field input');
    if (!searchInput) return;
    state.currentDocumentSearch = searchInput.value.trim();
    renderDocumentTable(state.documents);
  });
}

function attachPeopleInteractions() {
  const peopleView = document.querySelector<HTMLElement>('#people');
  if (!peopleView) return;

  peopleView.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const toggleButton = target.closest<HTMLButtonElement>('[data-person-toggle-status]');
    if (!toggleButton?.dataset.personToggleStatus || !state.currentWorkspace) return;

    const personId = toggleButton.dataset.personToggleStatus;
    const person = state.people.find((item) => item.id === personId);
    if (!person) return;
    const nextStatus = (person.status || 'Activo').toLowerCase().includes('inact') ? 'Activo' : 'Inactivo';
    openPersonStatusModal(person, nextStatus);
  });
}

function attachDocumentEditorInteractions() {
  const editorModal = document.querySelector<HTMLDialogElement>('#document-editor-modal');
  if (!editorModal) return;

  const editorWord = editorModal.querySelector<HTMLElement>('#editor-word');
  const editorSheet = editorModal.querySelector<HTMLElement>('#editor-sheet');
  const editorStatus = editorModal.querySelector<HTMLSelectElement>('#editor-status');
  const exportButton = editorModal.querySelector<HTMLButtonElement>('header .secondary-button.small');
  const addRowButton = editorModal.querySelector<HTMLButtonElement>('#sheet-add-row');
  const addColumnButton = editorModal.querySelector<HTMLButtonElement>('#sheet-add-column');
  let deleteSheetButton = editorModal.querySelector<HTMLButtonElement>('#sheet-delete-sheet');
  let deleteRowButton = editorModal.querySelector<HTMLButtonElement>('#sheet-delete-row');
  let deleteColumnButton = editorModal.querySelector<HTMLButtonElement>('#sheet-delete-column');

  if (!deleteSheetButton && addColumnButton) {
    deleteSheetButton = document.createElement('button');
    deleteSheetButton.type = 'button';
    deleteSheetButton.id = 'sheet-delete-sheet';
    deleteSheetButton.title = 'Eliminar hoja activa';
    deleteSheetButton.innerHTML = '<i data-lucide="trash-2"></i>';
    addColumnButton.insertAdjacentElement('afterend', deleteSheetButton);
  }

  if (!deleteRowButton && addRowButton) {
    deleteRowButton = document.createElement('button');
    deleteRowButton.type = 'button';
    deleteRowButton.id = 'sheet-delete-row';
    deleteRowButton.title = 'Eliminar fila(s) seleccionada(s)';
    deleteRowButton.innerHTML = '<i data-lucide="rows-3"></i>';
    addRowButton.insertAdjacentElement('afterend', deleteRowButton);
  }

  if (!deleteColumnButton && addColumnButton) {
    deleteColumnButton = document.createElement('button');
    deleteColumnButton.type = 'button';
    deleteColumnButton.id = 'sheet-delete-column';
    deleteColumnButton.title = 'Eliminar columna(s) seleccionada(s)';
    deleteColumnButton.innerHTML = '<i data-lucide="columns-3"></i>';
    addColumnButton.insertAdjacentElement('afterend', deleteColumnButton);
  }

  refreshIcons();

  if (editorWord && !defaultWordEditorTemplate) defaultWordEditorTemplate = editorWord.innerHTML;
  if (editorSheet && !defaultSheetEditorTemplate) defaultSheetEditorTemplate = editorSheet.innerHTML;

  if (editorWord) {
    editorWord.addEventListener('input', () => {
      scheduleDocumentAutosave();
    });
  }

  if (editorSheet) {
    editorSheet.addEventListener('input', () => {
      syncWorkbookFromSheetEditor(editorSheet);
      scheduleDocumentAutosave();
    });

    editorSheet.addEventListener('click', (event) => {
      const tabButton = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-sheet-index]');
      if (tabButton?.dataset.sheetIndex) {
        switchSpreadsheetSheet(editorSheet, Number(tabButton.dataset.sheetIndex));
        refreshIcons();
        return;
      }

      const addSheetButton = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-sheet-add]');
      if (addSheetButton) {
        appendSpreadsheetSheet(editorSheet);
        refreshIcons();
        return;
      }

      const removeSheetButton = (event.target as HTMLElement).closest<HTMLButtonElement>('[data-sheet-delete]');
      if (removeSheetButton) {
        deleteActiveSpreadsheetSheet(editorSheet);
        refreshIcons();
        return;
      }
    });

    editorSheet.addEventListener('mousedown', (event) => {
      const targetCell = (event.target as HTMLElement).closest<HTMLElement>('.sheet-cell');
      if (!targetCell) return;

      const row = Number(targetCell.dataset.row || 0);
      const column = Number(targetCell.dataset.column || 0);

      if (event.shiftKey && spreadsheetSelection) {
        applySpreadsheetSelection(editorSheet, spreadsheetSelection.anchorRow, spreadsheetSelection.anchorColumn, row, column);
      } else {
        spreadsheetSelection = {
          anchorRow: row,
          anchorColumn: column,
          dragging: true,
        };
        applySpreadsheetSelection(editorSheet, row, column, row, column);
      }
    });

    editorSheet.addEventListener('mouseover', (event) => {
      if (!spreadsheetSelection?.dragging) return;
      const targetCell = (event.target as HTMLElement).closest<HTMLElement>('.sheet-cell');
      if (!targetCell) return;
      const row = Number(targetCell.dataset.row || 0);
      const column = Number(targetCell.dataset.column || 0);
      applySpreadsheetSelection(editorSheet, spreadsheetSelection.anchorRow, spreadsheetSelection.anchorColumn, row, column);
    });

    editorSheet.addEventListener('click', (event) => {
      const targetCell = (event.target as HTMLElement).closest<HTMLElement>('.sheet-cell');
      if (!targetCell) return;
      if (!event.shiftKey) {
        const row = Number(targetCell.dataset.row || 0);
        const column = Number(targetCell.dataset.column || 0);
        spreadsheetSelection = {
          anchorRow: row,
          anchorColumn: column,
          dragging: false,
        };
        applySpreadsheetSelection(editorSheet, row, column, row, column);
      }
    });
  }

  document.addEventListener('mouseup', () => {
    if (spreadsheetSelection) spreadsheetSelection.dragging = false;
  });

  if (addRowButton) {
    addRowButton.addEventListener('click', () => {
      if (editorSheet && !editorSheet.hidden) {
        appendSheetRow(editorSheet);
      }
    });
  }

  if (addColumnButton) {
    addColumnButton.addEventListener('click', () => {
      if (editorSheet && !editorSheet.hidden) {
        appendSheetColumn(editorSheet);
      }
    });
  }

  if (deleteRowButton) {
    deleteRowButton.addEventListener('click', () => {
      if (editorSheet && !editorSheet.hidden) {
        deleteSheetRows(editorSheet);
      }
    });
  }

  if (deleteSheetButton) {
    deleteSheetButton.addEventListener('click', () => {
      if (editorSheet && !editorSheet.hidden) {
        deleteActiveSpreadsheetSheet(editorSheet);
      }
    });
  }

  if (deleteColumnButton) {
    deleteColumnButton.addEventListener('click', () => {
      if (editorSheet && !editorSheet.hidden) {
        deleteSheetColumns(editorSheet);
      }
    });
  }

  if (editorStatus) {
    editorStatus.addEventListener('change', () => {
      scheduleDocumentAutosave();
    });
  }

  if (exportButton) {
    exportButton.addEventListener('click', (event) => {
      event.preventDefault();
      const currentDoc = state.documents.find((doc) => doc.id === state.currentDocumentId);
      if (!currentDoc) return;

      const fileBase = sanitizeFilename(currentDoc.name || 'documento');
      const spreadsheetMode = Boolean(editorSheet && !editorSheet.hidden && isSpreadsheetFormat(currentDoc.format));

      if (spreadsheetMode && editorSheet) {
        syncWorkbookFromSheetEditor(editorSheet);
        const workbook = spreadsheetWorkbook || parseWorkbookFromSheetEditor(editorSheet);
        const matrix = workbook.sheets[workbook.activeSheetIndex] || createDefaultSheetMatrix();
        const csv = buildCsvFromMatrix(matrix);
        triggerFileDownload(`${fileBase}.csv`, csv, 'text/csv;charset=utf-8');
        return;
      }

      const content = editorWord?.innerHTML || '';
      const html = `<!doctype html><html><head><meta charset="utf-8" /><title>${currentDoc.name || 'Documento'}</title></head><body>${content}</body></html>`;
      triggerFileDownload(`${fileBase}.html`, html, 'text/html;charset=utf-8');
    });
  }

  editorModal.addEventListener('close', () => {
    if (documentAutosaveTimer) {
      window.clearTimeout(documentAutosaveTimer);
      documentAutosaveTimer = null;
    }
    if (editorSheet && !editorSheet.hidden) {
      syncWorkbookFromSheetEditor(editorSheet);
    }
    spreadsheetSelection = null;
    void saveCurrentDocumentEditor();
  });
}

async function loadMeasurementsByIndicator(indicators: Indicator[]) {
  const entries = await Promise.all(
    indicators.map(async (indicator) => {
      const measurements = await getMeasurements(indicator.id);
      return [indicator.id, measurements] as const;
    }),
  );
  state.measurementsByIndicator = Object.fromEntries(entries);
}

async function hydrateAuditRelations(audits: Audit[]) {
  const findingEntries = await Promise.all(
    audits.map(async (audit) => {
      try {
        const findings = await getAuditFindings(audit.id);
        return [audit.id, findings] as const;
      } catch (error) {
        console.error(`Error al cargar hallazgos de auditoria ${audit.id}:`, error);
        return [audit.id, [] as AuditFinding[]] as const;
      }
    }),
  );
  state.auditFindingsByAudit = Object.fromEntries(findingEntries);

  const allFindings = findingEntries.flatMap(([, findings]) => findings);
  const commitmentEntries = await Promise.all(
    allFindings.map(async (finding) => {
      try {
        const commitments = await getAuditCommitments(finding.id);
        return [finding.id, commitments] as const;
      } catch (error) {
        console.error(`Error al cargar compromisos del hallazgo ${finding.id}:`, error);
        return [finding.id, [] as AuditCommitment[]] as const;
      }
    }),
  );
  state.auditCommitmentsByFinding = Object.fromEntries(commitmentEntries);
}

function attachWorkspaceControls() {
  const workspaceTrigger = document.querySelector<HTMLElement>('#workspace-trigger');
  const workspaceMenu = document.querySelector<HTMLElement>('#workspace-menu');
  if (workspaceTrigger && workspaceMenu) {
    workspaceTrigger.addEventListener('click', (event) => {
      event.stopPropagation();
      const visible = workspaceMenu.hidden;
      workspaceMenu.hidden = !visible;
      workspaceTrigger.setAttribute('aria-expanded', String(!visible));
    });
  }
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target && !target.closest('#workspace-trigger') && !target.closest('#workspace-menu')) {
      workspaceMenu?.setAttribute('hidden', '');
      workspaceTrigger?.setAttribute('aria-expanded', 'false');
    }
  });
}

function attachProfileMenu() {
  const profileTrigger = document.querySelector<HTMLElement>('#profile-menu-trigger');
  const profileMenu = document.querySelector<HTMLElement>('#profile-menu');
  if (!profileTrigger || !profileMenu) return;

  profileTrigger.addEventListener('click', (event) => {
    event.stopPropagation();
    const visible = profileMenu.hidden;
    profileMenu.hidden = !visible;
    profileTrigger.setAttribute('aria-expanded', String(!visible));
  });

  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (!target?.closest('.profile')) {
      profileMenu.hidden = true;
      profileTrigger.setAttribute('aria-expanded', 'false');
    }
  });
}

function attachAppActions() {
  const supportForm = document.querySelector('#support-form') as HTMLFormElement | null;
  if (supportForm) {
    supportForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const feedback = supportForm.querySelector<HTMLElement>('#support-feedback');
      const message = (supportForm.querySelector('#support-message') as HTMLInputElement).value.trim();
      if (!message) {
        if (feedback) feedback.textContent = 'Escribe un mensaje para enviar la solicitud.';
        return;
      }
      if (feedback) feedback.textContent = 'Solicitud enviada. Soporte responderá a este espacio de trabajo.';
      (supportForm.querySelector('#support-message') as HTMLInputElement).value = '';
    });
  }

  const workspaceForm = document.querySelector('#workspace-modal form') as HTMLFormElement | null;
  if (workspaceForm) {
    workspaceForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const name = (workspaceForm.querySelector('#new-workspace-name') as HTMLInputElement).value.trim();
      if (!name) return;
      try {
        const workspace = await createWorkspace(name);
        const workspaces = await getWorkspaces();
        renderWorkspaceMenu(workspaces);
        (document.querySelector('#workspace-modal') as HTMLDialogElement | null)?.close();
        if (workspace?.id) await loadWorkspaceData(workspace.id);
      } catch (error) {
        console.error('Error al crear espacio de trabajo:', error);
      }
    });
  }

  const processForm = document.querySelector('#process-form') as HTMLFormElement | null;
  if (processForm) {
    processForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const name = (processForm.querySelector('#process-name') as HTMLInputElement).value.trim();
      if (!name) return;
      const process_type = (processForm.querySelector('#process-type') as HTMLSelectElement).value;
      const ownerValue = (processForm.querySelector('#process-owner') as HTMLSelectElement).value;
      const owner = ownerValue || null;
      const description = (processForm.querySelector('#process-description') as HTMLTextAreaElement).value.trim();
      const objective = (processForm.querySelector('#process-objective') as HTMLInputElement).value.trim();
      const inputs = (processForm.querySelector('#process-inputs') as HTMLTextAreaElement).value.trim();
      const outputs = (processForm.querySelector('#process-outputs') as HTMLTextAreaElement).value.trim();
      const scope = (processForm.querySelector('#process-scope') as HTMLTextAreaElement).value.trim();
      const flowRaw = (processForm.querySelector('#process-flow-steps') as HTMLInputElement).value.trim();
      const flow_steps = flowRaw
        ? flowRaw.split(',').map((item) => item.trim()).filter(Boolean)
        : [];
      const status = (processForm.querySelector('#process-status') as HTMLSelectElement).value;
      const calculatedHealth = state.processFormMode === 'edit' && state.currentProcessId
        ? calculateProcessHealth(state.currentProcessId)
        : 100;
      const selectedIcon = (processForm.querySelector('#process-icon') as HTMLInputElement | null)?.value;
      const fallbackIcon = process_type === 'Misional' ? 'git-branch' : process_type === 'Estratégico' ? 'award' : process_type === 'Apoyo' ? 'settings' : 'shield-check';
      const icon = selectedIcon || fallbackIcon;
      const payload = {
        name,
        process_type,
        description,
        objective,
        inputs,
        outputs,
        flow_steps,
        scope,
        status,
        owner,
        icon,
        health: calculatedHealth,
      };
      try {
        if (state.processFormMode === 'edit' && state.currentProcessId) {
          await updateProcess(state.currentWorkspace.id, state.currentProcessId, payload);
        } else {
          const createdProcess = await createProcess(state.currentWorkspace.id, payload);
          state.currentProcessId = createdProcess.id;
        }
        const processes = await getProcesses(state.currentWorkspace.id);
        renderProcessCards(processes);
        renderProcessDetail(state.currentProcessId, state.currentProcessTab);
        updateDashboardSummary(processes.length, state.actions.length, state.risks.length, state.documents.length);
        (document.querySelector('#process-modal') as HTMLDialogElement | null)?.close();
        processForm.reset();
        prepareProcessModalForCreate();
      } catch (error) {
        console.error('Error al crear proceso:', error);
      }
    });
  }

  const companyForm = document.querySelector('#company-form') as HTMLFormElement | null;
  if (companyForm) {
    companyForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const payload = {
        short_name: (companyForm.querySelector('#company-short-name') as HTMLInputElement).value.trim(),
        legal_name: (companyForm.querySelector('#company-legal-name') as HTMLInputElement).value.trim(),
        nit: (companyForm.querySelector('#company-nit') as HTMLInputElement).value.trim(),
        sector: (companyForm.querySelector('#company-sector') as HTMLSelectElement).value,
        city: (companyForm.querySelector('#company-city') as HTMLInputElement).value.trim(),
        company_size: (companyForm.querySelector('#company-size') as HTMLSelectElement).value,
        description: (companyForm.querySelector('#company-description') as HTMLTextAreaElement).value.trim(),
        mission: (companyForm.querySelector('#company-mission') as HTMLTextAreaElement).value.trim(),
        vision: (companyForm.querySelector('#company-vision') as HTMLTextAreaElement).value.trim(),
        company_values: (companyForm.querySelector('#company-values') as HTMLInputElement).value.trim(),
        direction_owner: (companyForm.querySelector('#company-direction-owner') as HTMLSelectElement).value,
        strategy_period: (companyForm.querySelector('#company-strategy-period') as HTMLSelectElement).value,
        company_scope: (companyForm.querySelector('#company-scope') as HTMLTextAreaElement).value.trim(),
        process_families: Array.from(companyForm.querySelectorAll<HTMLInputElement>('input[name="company-process-family"]:checked')).map((input) => input.value),
        review_frequency: (companyForm.querySelector('#company-review-frequency') as HTMLSelectElement).value,
        management_year_start: (companyForm.querySelector('#company-management-year-start') as HTMLSelectElement).value,
        company_policy: (companyForm.querySelector('#company-policy') as HTMLTextAreaElement).value.trim(),
        communication_channel: (companyForm.querySelector('#company-communication-channel') as HTMLSelectElement).value,
        company_objectives: collectCompanyObjectives(companyForm),
      } as Partial<Company>;

      if (!payload.short_name || !payload.legal_name) return;
      try {
        if (state.currentCompany?.id) {
          await updateCompany(state.currentWorkspace.id, state.currentCompany.id, payload);
        } else {
          await createCompany(state.currentWorkspace.id, payload);
        }

        const companies = await getCompanies(state.currentWorkspace.id);
        renderCompanySection(companies);
        (document.querySelector('#company-modal') as HTMLDialogElement | null)?.close();
        const feedback = document.querySelector<HTMLElement>('#company-save-feedback');
        if (feedback) feedback.textContent = 'Cambios guardados en la empresa.';
      } catch (error) {
        console.error('Error al guardar empresa:', error);
      }
    });
    attachStrategicObjectiveControls();
  }

  const riskForm = document.querySelector('#risk-form') as HTMLFormElement | null;
  if (riskForm) {
    riskForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const feedback = riskForm.querySelector<HTMLElement>('#risk-feedback');
      if (feedback) feedback.textContent = '';
      const riskName = (riskForm.querySelector('#risk-name') as HTMLInputElement).value.trim();
      if (!riskName) {
        if (feedback) feedback.textContent = 'El nombre del riesgo es obligatorio.';
        return;
      }
      const risk = {
        name: riskName,
        process_id: (riskForm.querySelector('#risk-process') as HTMLSelectElement).value || null,
        probability: (riskForm.querySelector('#risk-probability') as HTMLSelectElement).value,
        impact: (riskForm.querySelector('#risk-impact') as HTMLSelectElement).value,
        status: (riskForm.querySelector('#risk-status') as HTMLSelectElement).value,
        causes: (riskForm.querySelector('#risk-causes') as HTMLTextAreaElement).value.trim(),
        controls: (riskForm.querySelector('#risk-control') as HTMLTextAreaElement).value.trim(),
        treatment: (riskForm.querySelector('#risk-treatment') as HTMLTextAreaElement).value.trim(),
        owner: (riskForm.querySelector('#risk-owner') as HTMLSelectElement).value || null,
        review_date: (riskForm.querySelector('#risk-review-date') as HTMLInputElement).value || null,
      };
      try {
        if (state.riskFormMode === 'edit' && state.currentRiskId) {
          await updateRisk(state.currentWorkspace.id, state.currentRiskId, risk);
        } else {
          const createdRisk = await createRisk(state.currentWorkspace.id, risk);
          if (createdRisk?.id) state.currentRiskId = createdRisk.id;
        }
        const risks = await getRisks(state.currentWorkspace.id);
        renderRiskSection(risks);
        renderRiskDetail(state.currentRiskId);
        renderProcessDetail(state.currentProcessId, state.currentProcessTab);
        updateDashboardSummary(state.processes.length, state.actions.length, risks.length, state.documents.length);
        prepareRiskModalForCreate();
        (document.querySelector('#risk-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al guardar riesgo:', error);
        if (feedback) feedback.textContent = 'No se pudo guardar el riesgo. Intenta de nuevo.';
      }
    });
  }

  const actionModalForm = document.querySelector('#action-modal form') as HTMLFormElement | null;
  if (actionModalForm) {
    actionModalForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;

      const feedback = actionModalForm.querySelector<HTMLElement>('#action-feedback');
      if (feedback) feedback.textContent = '';
      const actionName = (actionModalForm.querySelector('#action-name') as HTMLInputElement).value.trim();
      if (!actionName) {
        if (feedback) feedback.textContent = 'El nombre de la acción es obligatorio.';
        return;
      }

      const action = {
        title: actionName,
        source: (actionModalForm.querySelector('#action-source') as HTMLSelectElement).value || null,
        assignee: (actionModalForm.querySelector('#action-assignee') as HTMLSelectElement).value || null,
        status: (actionModalForm.querySelector('#action-status') as HTMLSelectElement).value,
        description: (actionModalForm.querySelector('#action-description') as HTMLTextAreaElement).value.trim(),
        due_date: (actionModalForm.querySelector('#action-due-date') as HTMLInputElement).value || null,
      };

      try {
        if (state.actionFormMode === 'edit' && state.currentActionId) {
          await updateAction(state.currentWorkspace.id, state.currentActionId, action);
        } else {
          const createdAction = await createAction(state.currentWorkspace.id, action);
          if (createdAction?.id) state.currentActionId = createdAction.id;
        }
        const actions = await getActions(state.currentWorkspace.id);
        renderActionBoard(actions);
        renderImprovementSection();
        updateDashboardSummary(state.processes.length, actions.length, state.risks.length, state.documents.length);
        prepareActionModalForCreate();
        (document.querySelector('#action-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al guardar acción:', error);
        if (feedback) feedback.textContent = 'No se pudo guardar la acción. Intenta de nuevo.';
      }
    });
  }

  const improvementForm = document.querySelector('#improvement-form') as HTMLFormElement | null;
  if (improvementForm) {
    improvementForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;

      const feedback = improvementForm.querySelector<HTMLElement>('#improvement-feedback');
      if (feedback) feedback.textContent = '';

      const planName = (improvementForm.querySelector('#improvement-name') as HTMLInputElement).value.trim();
      if (!planName) {
        if (feedback) feedback.textContent = 'El nombre del plan es obligatorio.';
        return;
      }

      try {
        const payload = {
          name: planName,
          origin: (improvementForm.querySelector('#improvement-origin') as HTMLSelectElement).value,
          process: (improvementForm.querySelector('#improvement-process') as HTMLSelectElement).value || null,
          situation: (improvementForm.querySelector('#improvement-situation') as HTMLTextAreaElement).value.trim(),
          goal: (improvementForm.querySelector('#improvement-goal') as HTMLInputElement).value.trim(),
          owner: (improvementForm.querySelector('#improvement-owner') as HTMLSelectElement).value || null,
          target_date: (improvementForm.querySelector('#improvement-target-date') as HTMLInputElement).value || null,
          phase: (improvementForm.querySelector('#improvement-phase') as HTMLSelectElement).value,
          status: (improvementForm.querySelector('#improvement-status') as HTMLSelectElement).value,
          progress: Number((improvementForm.querySelector('#improvement-progress') as HTMLInputElement).value || '0'),
        };

        if (state.currentImprovementPlanId) {
          await updateImprovementPlan(state.currentWorkspace.id, state.currentImprovementPlanId, payload);
        } else {
          const createdPlan = await createImprovementPlan(state.currentWorkspace.id, payload);
          state.currentImprovementPlanId = createdPlan?.id || null;
        }

        const plans = await getImprovementPlans(state.currentWorkspace.id);
        state.improvementPlans = plans;
        const followupsEntries = await Promise.all(
          plans.map(async (plan) => [plan.id, await getImprovementFollowups(plan.id)] as const),
        );
        state.improvementFollowupsByPlan = Object.fromEntries(followupsEntries);
        renderImprovementSection();
        updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, state.documents.length);
        state.currentImprovementPlanId = null;
        prepareImprovementModalForCreate();
        (document.querySelector('#improvement-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al crear plan de mejora:', error);
        if (feedback) feedback.textContent = 'No se pudo crear el plan. Intenta de nuevo.';
      }
    });
  }

  const improvementFollowupForm = document.querySelector('#improvement-followup-form') as HTMLFormElement | null;
  if (improvementFollowupForm) {
    improvementFollowupForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace || !state.currentImprovementPlanId) return;

      const feedback = improvementFollowupForm.querySelector<HTMLElement>('#improvement-followup-feedback');
      if (feedback) feedback.textContent = '';

      const nextStatus = (improvementFollowupForm.querySelector('#improvement-followup-plan-status') as HTMLSelectElement).value;
      const notes = (improvementFollowupForm.querySelector('#improvement-followup-progress-note') as HTMLTextAreaElement).value.trim();
      const result = (improvementFollowupForm.querySelector('#improvement-followup-result') as HTMLInputElement).value.trim();
      const reviewDate = (improvementFollowupForm.querySelector('#improvement-followup-date') as HTMLInputElement).value || null;
      const decision = (improvementFollowupForm.querySelector('#improvement-followup-decision') as HTMLSelectElement).value;
      const effectiveness = (improvementFollowupForm.querySelector('#improvement-followup-effectiveness') as HTMLTextAreaElement).value.trim();

      try {
        await createImprovementFollowup(state.currentImprovementPlanId, {
          followup_date: reviewDate,
          progress_note: notes,
          verified_result: result,
          decision,
          effectiveness_comment: effectiveness,
        });

        await updateImprovementPlan(state.currentWorkspace.id, state.currentImprovementPlanId, {
          status: nextStatus,
          target_date: reviewDate,
        });

        const plans = await getImprovementPlans(state.currentWorkspace.id);
        state.improvementPlans = plans;
        const followupsEntries = await Promise.all(
          plans.map(async (plan) => [plan.id, await getImprovementFollowups(plan.id)] as const),
        );
        state.improvementFollowupsByPlan = Object.fromEntries(followupsEntries);
        renderImprovementSection();
        updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, state.documents.length);
        (document.querySelector('#improvement-followup-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al registrar seguimiento:', error);
        if (feedback) feedback.textContent = 'No se pudo registrar el seguimiento. Intenta de nuevo.';
      }
    });
  }

  const documentForm = document.querySelector('#document-modal form') as HTMLFormElement | null;
  if (documentForm) {
    documentForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;

      const feedback = documentForm.querySelector<HTMLElement>('#document-feedback');
      if (feedback) feedback.textContent = '';

      const name = (documentForm.querySelector('#document-name') as HTMLInputElement).value.trim();
      const format = (documentForm.querySelector('#document-format') as HTMLSelectElement).value;
      const category = (documentForm.querySelector('#document-category') as HTMLSelectElement).value;
      const process = (documentForm.querySelector('#document-process') as HTMLSelectElement | null)?.value || null;
      if (!name || !format || !category) {
        if (feedback) feedback.textContent = 'Nombre, formato y categoría son obligatorios.';
        return;
      }

      const documentData = {
        name,
        format,
        process,
        version: 'v1.0',
        status: 'Por revisar',
        category,
      };
      try {
        await createDocument(state.currentWorkspace.id, documentData);
        const documents = await getDocuments(state.currentWorkspace.id);
        renderDocumentTable(documents);
        updateDashboardSummary(state.processes.length, state.actions.length, state.risks.length, documents.length);
        prepareDocumentModalForCreate();
        (document.querySelector('#document-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al cargar documento:', error);
        if (feedback) feedback.textContent = 'No se pudo crear el documento. Intenta de nuevo.';
      }
    });
  }

  const auditForm = document.querySelector('#audit-modal form') as HTMLFormElement | null;
  if (auditForm) {
    auditForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;

      ensureAuditModalFieldIds();
      const feedback = auditForm.querySelector<HTMLElement>('#audit-feedback');
      if (feedback) feedback.textContent = '';

      const title = (auditForm.querySelector('#audit-title') as HTMLInputElement | null)?.value.trim() || '';
      const status = (auditForm.querySelector('#audit-status') as HTMLSelectElement | null)?.value || 'Programada';
      const executedBy = (auditForm.querySelector('#audit-executor') as HTMLSelectElement | null)?.value || null;
      const completedAtRaw = (auditForm.querySelector('#audit-date') as HTMLInputElement | HTMLSelectElement | null)?.value || '';
      const completedAt = /^\d{4}-\d{2}-\d{2}$/.test(completedAtRaw) ? completedAtRaw : null;
      const scope = (auditForm.querySelector('#audit-scope') as HTMLInputElement | null)?.value.trim() || '';
      const notesBase = (auditForm.querySelector('#audit-notes') as HTMLTextAreaElement | null)?.value.trim() || '';
      const notes = scope ? `Alcance: ${scope}${notesBase ? `\n\n${notesBase}` : ''}` : notesBase;

      if (!title) {
        if (feedback) feedback.textContent = 'El nombre de la auditoria es obligatorio.';
        return;
      }

      const audit = {
        title,
        status,
        executed_by: executedBy,
        completed_at: completedAt,
        notes,
      };

      try {
        let auditId = state.currentAuditId;
        if (auditId) {
          await updateAudit(state.currentWorkspace.id, auditId, audit);
        } else {
          const createdAudit = await createAudit(state.currentWorkspace.id, audit);
          auditId = createdAudit.id;
          state.currentAuditId = auditId;
        }

        const findingTitle = (auditForm.querySelector('#audit-finding-title') as HTMLInputElement | null)?.value.trim() || '';
        if (auditId && findingTitle) {
          const createdFinding = await createAuditFinding(auditId, {
            title: findingTitle,
            severity: 'Media',
            status: 'Abierto',
            action_status: 'Por iniciar',
          });

          const commitmentOwner = (auditForm.querySelector('#audit-commitment-owner') as HTMLSelectElement | null)?.value || null;
          const commitmentDue = (auditForm.querySelector('#audit-commitment-due') as HTMLInputElement | null)?.value || null;
          if (commitmentOwner || commitmentDue) {
            await createAuditCommitment(createdFinding.id, {
              title: `Compromiso: ${findingTitle}`,
              owner: commitmentOwner,
              due_date: commitmentDue,
              status: 'Por iniciar',
            });
          }
        }

        const audits = await getAudits(state.currentWorkspace.id);
        await hydrateAuditRelations(audits);
        renderAuditSection(audits);
        state.currentAuditId = null;
        prepareAuditModalForCreate();
        (document.querySelector('#audit-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al guardar auditoría:', error);
        if (feedback) feedback.textContent = 'No se pudo guardar la auditoria. Intenta de nuevo.';
      }
    });
  }

  const findingForm = document.querySelector('#finding-form') as HTMLFormElement | null;
  if (findingForm) {
    findingForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace || !state.currentAuditId) return;

      ensureFindingModalFieldIds();
      const feedback = findingForm.querySelector<HTMLElement>('#finding-feedback');
      if (feedback) feedback.textContent = '';

      const title = (findingForm.querySelector('#finding-title') as HTMLInputElement | null)?.value.trim() || '';
      const status = (findingForm.querySelector('#finding-status') as HTMLSelectElement | null)?.value || 'Abierto';
      const actionStatus = (findingForm.querySelector('#finding-action-status') as HTMLSelectElement | null)?.value || 'Por iniciar';
      const owner = (findingForm.querySelector('#finding-owner') as HTMLSelectElement | null)?.value || null;
      const dueDate = (findingForm.querySelector('#finding-verify-date') as HTMLInputElement | null)?.value || null;
      const evidence = (findingForm.querySelector('#finding-evidence') as HTMLTextAreaElement | null)?.value.trim() || '';
      const comment = (findingForm.querySelector('#finding-comment') as HTMLTextAreaElement | null)?.value.trim() || '';

      if (!title) {
        if (feedback) feedback.textContent = 'El titulo del hallazgo es obligatorio.';
        return;
      }

      try {
        let findingId = state.currentFindingId;
        if (findingId) {
          await updateAuditFinding(state.currentAuditId, findingId, {
            title,
            status,
            action_status: actionStatus,
          });
        } else {
          const createdFinding = await createAuditFinding(state.currentAuditId, {
            title,
            status,
            action_status: actionStatus,
            severity: 'Media',
          });
          findingId = createdFinding.id;
          state.currentFindingId = findingId;
        }

        if (findingId) {
          const currentCommitments = state.auditCommitmentsByFinding[findingId] || [];
          if (currentCommitments[0]) {
            await updateAuditCommitment(findingId, currentCommitments[0].id, {
              owner,
              due_date: dueDate,
              status: actionStatus,
              title: currentCommitments[0].title,
            });
          } else if (owner || dueDate || evidence || comment) {
            await createAuditCommitment(findingId, {
              title: title,
              owner,
              due_date: dueDate,
              status: actionStatus,
            });
          }

          const targetAudit = state.audits.find((auditItem) => auditItem.id === state.currentAuditId);
          if (targetAudit) {
            const mergedNotes = [targetAudit.notes, evidence, comment].filter(Boolean).join('\n\n');
            await updateAudit(state.currentWorkspace.id, targetAudit.id, { notes: mergedNotes });
          }
        }

        const audits = await getAudits(state.currentWorkspace.id);
        await hydrateAuditRelations(audits);
        renderAuditSection(audits);
        state.currentFindingId = null;
        (document.querySelector('#finding-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al guardar hallazgo:', error);
        if (feedback) feedback.textContent = 'No se pudo guardar el hallazgo.';
      }
    });
  }

  const personForm = document.querySelector('#person-modal form') as HTMLFormElement | null;
  if (personForm) {
    personForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const person = {
        name: (personForm.querySelector('#person-name') as HTMLInputElement).value.trim(),
        email: (personForm.querySelector('#person-email') as HTMLInputElement).value.trim(),
        position: (personForm.querySelector('#person-position') as HTMLSelectElement).value || null,
      };
      try {
        await createPerson(state.currentWorkspace.id, person);
        const people = await getPeople(state.currentWorkspace.id);
        renderPeopleSection(people);
        (document.querySelector('#person-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al invitar persona:', error);
      }
    });
  }

  const roleForm = document.querySelector('#role-modal form') as HTMLFormElement | null;
  if (roleForm) {
    roleForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const feedback = roleForm.querySelector<HTMLElement>('#role-feedback');
      if (feedback) feedback.textContent = '';
      const roleName = (roleForm.querySelector('#role-name') as HTMLInputElement).value.trim();
      if (!roleName) {
        if (feedback) feedback.textContent = 'El nombre del cargo es obligatorio.';
        return;
      }
      const role = {
        name: roleName,
        authority: (roleForm.querySelector('#role-authority') as HTMLSelectElement).value,
        purpose: (roleForm.querySelector('#role-purpose') as HTMLTextAreaElement).value.trim(),
        responsibilities: (roleForm.querySelector('#role-responsibilities') as HTMLTextAreaElement).value.trim(),
        process_participation: collectRoleParticipation(roleForm),
      };
      try {
        if (state.roleFormMode === 'edit' && state.currentRoleId) {
          await updateRole(state.currentWorkspace.id, state.currentRoleId, role);
        } else {
          const createdRole = await createRole(state.currentWorkspace.id, role);
          if (createdRole?.id) {
            state.currentRoleId = createdRole.id;
          }
        }
        const roles = await getRoles(state.currentWorkspace.id);
        renderRolesSection(roles);
        prepareRoleModalForCreate();
        (document.querySelector('#role-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al crear rol:', error);
        if (feedback) feedback.textContent = 'No se pudo guardar el cargo. Intenta de nuevo.';
      }
    });
  }

  const indicatorForm = document.querySelector('#indicator-modal form') as HTMLFormElement | null;
  if (indicatorForm) {
    indicatorForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!state.currentWorkspace) return;
      const goal = (indicatorForm.querySelector('#indicator-goal') as HTMLInputElement).value.trim();
      const nextDueRaw = (indicatorForm.querySelector('#indicator-next-due') as HTMLInputElement | null)?.value || '';
      const indicator = {
        name: (indicatorForm.querySelector('#indicator-name') as HTMLInputElement).value.trim(),
        process_id: (indicatorForm.querySelector('#indicator-process') as HTMLSelectElement).value || null,
        code: goal,
        goal,
        periodicity: (indicatorForm.querySelector('#indicator-periodicity') as HTMLSelectElement).value,
        next_due: nextDueRaw || null,
        status: 'Activo',
      };
      try {
        await createIndicator(state.currentWorkspace.id, indicator);
        const indicators = await getIndicators(state.currentWorkspace.id);
        await loadMeasurementsByIndicator(indicators);
        renderIndicatorSection(indicators);
        renderProcessDetail(state.currentProcessId, state.currentProcessTab);
        indicatorForm.reset();
        (document.querySelector('#indicator-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al crear indicador:', error);
      }
    });
  }

  const measurementForm = document.querySelector('#measurement-modal form') as HTMLFormElement | null;
  if (measurementForm) {
    measurementForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const indicatorId = state.currentMeasurementIndicatorId;
      if (!indicatorId) return;
      const periodLabel = (measurementForm.querySelector('#measurement-period') as HTMLElement | null)?.textContent || '';
      const measurement = {
        period: periodLabel,
        result: (measurementForm.querySelector('#measurement-result') as HTMLInputElement).value.trim(),
        status: 'Registrado',
        author: state.currentProfile?.full_name || '',
        source: (measurementForm.querySelector('#measurement-source') as HTMLInputElement).value.trim(),
        calculation: (measurementForm.querySelector('#measurement-calculation') as HTMLInputElement).value.trim(),
        notes: (measurementForm.querySelector('#measurement-notes') as HTMLTextAreaElement).value.trim(),
        date: (measurementForm.querySelector('#measurement-date') as HTMLInputElement | null)?.value || toIsoDate(new Date()),
      };
      try {
        await createMeasurement(indicatorId, measurement);
        state.measurementsByIndicator[indicatorId] = await getMeasurements(indicatorId);
        const indicators = state.currentWorkspace
          ? await getIndicators(state.currentWorkspace.id)
          : state.indicators;
        renderIndicatorSection(indicators);
        renderIndicatorDetail(state.currentIndicatorId || indicatorId);
        renderProcessDetail(state.currentProcessId, state.currentProcessTab);
        measurementForm.reset();
        (document.querySelector('#measurement-modal') as HTMLDialogElement | null)?.close();
      } catch (error) {
        console.error('Error al registrar medición:', error);
      }
    });
  }
}

function populateCompanyForm(company: Company | null) {
  const companyForm = document.querySelector('#company-form') as HTMLFormElement | null;
  if (!companyForm) return;

  const setValue = (selector: string, value: string | undefined) => {
    const element = companyForm.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(selector);
    if (!element) return;
    if ('value' in element) element.value = value || '';
  };

  setValue('#company-short-name', company?.short_name);
  setValue('#company-legal-name', company?.legal_name);
  setValue('#company-nit', company?.nit);
  setValue('#company-sector', company?.sector);
  setValue('#company-city', company?.city);
  setValue('#company-size', company?.company_size);
  setValue('#company-description', company?.description);
  setValue('#company-mission', company?.mission);
  setValue('#company-vision', company?.vision);
  setValue('#company-values', company?.company_values);
  setValue('#company-direction-owner', company?.direction_owner);
  setValue('#company-strategy-period', company?.strategy_period);
  setValue('#company-scope', company?.company_scope);
  setValue('#company-review-frequency', company?.review_frequency);
  setValue('#company-management-year-start', company?.management_year_start);
  setValue('#company-policy', company?.company_policy);
  setValue('#company-communication-channel', company?.communication_channel);

  const selectedFamilyInputs = companyForm.querySelectorAll<HTMLInputElement>('input[name="company-process-family"]');
  selectedFamilyInputs.forEach((input) => {
    input.checked = Array.isArray(company?.process_families) && company.process_families.includes(input.value);
  });

  const objectiveFields = companyForm.querySelector<HTMLDivElement>('#strategic-objective-fields');
  if (objectiveFields) {
    objectiveFields.innerHTML = '';
    const objectives = company?.company_objectives || [];
    if (objectives.length > 0) {
      objectives.forEach((objective, index) => {
        const field = document.createElement('div');
        field.className = 'strategic-objective-field';
        field.innerHTML = `<span class="objective-index">${String(index + 1).padStart(2, '0')}</span><label>Objetivo<input value="${objective.objective || ''}" /></label><label>Resultado esperado<input value="${objective.expected_result || ''}" /></label><label>Meta anual<input value="${objective.annual_target || ''}" /></label><button type="button" class="icon-button remove-objective" aria-label="Eliminar objetivo"><i data-lucide="trash-2"></i></button>`;
        objectiveFields.append(field);
      });
    } else {
      addStrategicObjective(companyForm);
    }
    renumberStrategicObjectives(companyForm);
  }
}

function setProcessIconSelection(icon: string) {
  const iconInput = document.querySelector<HTMLInputElement>('#process-icon');
  const picker = document.querySelector<HTMLElement>('#process-icon-picker');
  if (!iconInput || !picker) return;
  iconInput.value = icon;
  picker.querySelectorAll<HTMLButtonElement>('.icon-picker-option').forEach((button) => {
    const selected = button.dataset.icon === icon;
    button.classList.toggle('selected', selected);
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
  });
}

function populatePersonRoleOptions(selectedRoleName?: string | null) {
  const roleSelect = document.querySelector<HTMLSelectElement>('#person-position');
  if (!roleSelect) return;
  const defaultOption = '<option value="">Selecciona un cargo</option>';
  const options = state.roles.map((role) => `<option value="${role.name}">${role.name}</option>`).join('');
  roleSelect.innerHTML = `${defaultOption}${options}`;
  if (selectedRoleName && state.roles.some((role) => role.name === selectedRoleName)) {
    roleSelect.value = selectedRoleName;
  }
}

function populateProcessOwnerOptions(selectedOwner?: string | null) {
  const ownerSelect = document.querySelector<HTMLSelectElement>('#process-owner');
  if (!ownerSelect) return;

  const preservedOwner = selectedOwner !== undefined ? selectedOwner : ownerSelect.value || null;
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'No aplica' }];

  state.roles.forEach((role) => {
    const roleName = (role.name || '').trim();
    if (!roleName) return;
    const assignedPeople = state.people.filter((person) => (person.position || '').trim().toLowerCase() === roleName.toLowerCase());
    if (assignedPeople.length > 0) {
      assignedPeople.forEach((person) => {
        options.push({ value: person.name, label: `${roleName} - ${person.name}` });
      });
      return;
    }
    options.push({ value: roleName, label: `${roleName} - Sin persona asignada` });
  });

  if (preservedOwner && !options.some((option) => option.value === preservedOwner)) {
    options.push({ value: preservedOwner, label: preservedOwner });
  }

  ownerSelect.innerHTML = options.map((option) => `<option value="${option.value}">${option.label}</option>`).join('');
  ownerSelect.value = preservedOwner && options.some((option) => option.value === preservedOwner) ? preservedOwner : '';
}

function populateRiskOwnerOptions(selectedOwner?: string | null) {
  const ownerSelect = document.querySelector<HTMLSelectElement>('#risk-owner');
  if (!ownerSelect) return;
  const preservedOwner = selectedOwner !== undefined ? selectedOwner : ownerSelect.value || null;
  const options = [{ value: '', label: 'No aplica' }, ...state.people.map((person) => ({ value: person.name, label: person.position ? `${person.name} · ${person.position}` : person.name }))];

  if (preservedOwner && !options.some((option) => option.value === preservedOwner)) {
    options.push({ value: preservedOwner, label: preservedOwner });
  }

  ownerSelect.innerHTML = options.map((option) => `<option value="${option.value}">${option.label}</option>`).join('');
  ownerSelect.value = preservedOwner && options.some((option) => option.value === preservedOwner) ? preservedOwner : '';
}

function renderRoleParticipationEditor(selectedParticipation: Record<string, string> = {}) {
  const fieldset = document.querySelector<HTMLElement>('#role-process-participation');
  if (!fieldset) return;
  const rows = state.processes
    .map((process) => {
      const selected = selectedParticipation[process.id] || '';
      return `<div><span>${process.name}</span><select data-role-process="${process.id}"><option value=""${selected === '' ? ' selected' : ''}>Sin participación</option><option value="R"${selected === 'R' ? ' selected' : ''}>R · Ejecuta</option><option value="A"${selected === 'A' ? ' selected' : ''}>A · Aprueba</option><option value="C"${selected === 'C' ? ' selected' : ''}>C · Consulta</option><option value="I"${selected === 'I' ? ' selected' : ''}>I · Informa</option></select></div>`;
    })
    .join('');
  fieldset.innerHTML = `<legend>Participación en procesos</legend><p>Asigna el tipo de participación de este cargo en cada proceso.</p>${rows || '<div><span>No hay procesos disponibles</span></div>'}`;
}

function collectRoleParticipation(roleForm: HTMLFormElement) {
  const entries = Array.from(roleForm.querySelectorAll<HTMLSelectElement>('[data-role-process]'))
    .map((select) => ({
      processId: select.dataset.roleProcess || '',
      value: select.value,
    }))
    .filter((entry) => entry.processId && entry.value);

  return entries.reduce((acc, entry) => {
    acc[entry.processId] = entry.value;
    return acc;
  }, {} as Record<string, string>);
}

function prepareRoleModalForCreate() {
  state.roleFormMode = 'create';
  const roleForm = document.querySelector('#role-modal form') as HTMLFormElement | null;
  if (roleForm) roleForm.reset();
  const feedback = document.querySelector<HTMLElement>('#role-feedback');
  if (feedback) feedback.textContent = '';
  const eyebrow = document.querySelector<HTMLElement>('#role-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#role-modal-title');
  const submit = document.querySelector<HTMLElement>('#role-save');
  if (eyebrow) eyebrow.textContent = 'NUEVO CARGO';
  if (title) title.textContent = 'Definir rol y responsabilidades';
  if (submit) submit.textContent = 'Crear cargo';
  renderRoleParticipationEditor({});
}

function prepareRoleModalForEdit(role: Role) {
  state.roleFormMode = 'edit';
  state.currentRoleId = role.id;
  const roleForm = document.querySelector('#role-modal form') as HTMLFormElement | null;
  if (!roleForm) return;
  (roleForm.querySelector('#role-name') as HTMLInputElement).value = role.name || '';
  (roleForm.querySelector('#role-authority') as HTMLSelectElement).value = role.authority || 'Ejecuta';
  (roleForm.querySelector('#role-purpose') as HTMLTextAreaElement).value = role.purpose || '';
  (roleForm.querySelector('#role-responsibilities') as HTMLTextAreaElement).value = role.responsibilities || '';
  const eyebrow = document.querySelector<HTMLElement>('#role-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#role-modal-title');
  const submit = document.querySelector<HTMLElement>('#role-save');
  const feedback = document.querySelector<HTMLElement>('#role-feedback');
  if (eyebrow) eyebrow.textContent = 'EDITAR CARGO';
  if (title) title.textContent = `Editar ${role.name}`;
  if (submit) submit.textContent = 'Guardar cambios';
  if (feedback) feedback.textContent = '';
  const participation = role.process_participation && typeof role.process_participation === 'object'
    ? role.process_participation
    : {};
  renderRoleParticipationEditor(participation);
}

function attachRoleEditButton() {
  const editButton = document.querySelector<HTMLButtonElement>('#edit-selected-role');
  if (!editButton) return;
  editButton.addEventListener('click', () => {
    if (!state.currentRoleId) return;
    const role = state.roles.find((item) => item.id === state.currentRoleId);
    if (!role) return;
    prepareRoleModalForEdit(role);
    const modal = document.querySelector<HTMLDialogElement>('#role-modal');
    modal?.showModal();
  });
}

function attachRoleDeleteButton() {
  const deleteButton = document.querySelector<HTMLButtonElement>('#delete-selected-role');
  if (!deleteButton) return;
  deleteButton.addEventListener('click', async () => {
    if (!state.currentWorkspace || !state.currentRoleId) return;
    const role = state.roles.find((item) => item.id === state.currentRoleId);
    if (!role) return;
    const confirmed = window.confirm(`¿Seguro que deseas eliminar el cargo "${role.name}"? Esta acción no se puede deshacer.`);
    if (!confirmed) return;

    try {
      await deleteRole(state.currentWorkspace.id, role.id);
      const roles = await getRoles(state.currentWorkspace.id);
      state.currentRoleId = roles[0]?.id || null;
      renderRolesSection(roles);
      prepareRoleModalForCreate();
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      window.alert('No se pudo eliminar el cargo. Intenta de nuevo.');
    }
  });
}

function prepareRiskModalForCreate() {
  state.riskFormMode = 'create';
  state.currentRiskId = state.currentRiskId || null;
  const riskForm = document.querySelector('#risk-form') as HTMLFormElement | null;
  if (riskForm) riskForm.reset();
  populateRiskOwnerOptions(null);
  const eyebrow = document.querySelector<HTMLElement>('#risk-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#risk-modal-title');
  const submit = document.querySelector<HTMLElement>('#risk-save');
  const feedback = document.querySelector<HTMLElement>('#risk-feedback');
  if (eyebrow) eyebrow.textContent = 'NUEVO RIESGO';
  if (title) title.textContent = 'Registrar riesgo';
  if (submit) submit.textContent = 'Guardar riesgo';
  if (feedback) feedback.textContent = '';
}

function prepareActionModalForCreate() {
  state.actionFormMode = 'create';
  state.currentActionId = null;
  const actionForm = document.querySelector('#action-form') as HTMLFormElement | null;
  if (actionForm) actionForm.reset();
  populateActionProcessOptions(null);
  populateActionAssigneeOptions(null);
  const statusField = document.querySelector<HTMLSelectElement>('#action-status');
  if (statusField) statusField.value = 'Por iniciar';
  const eyebrow = document.querySelector<HTMLElement>('#action-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#action-modal-title');
  const context = document.querySelector<HTMLElement>('#action-context');
  const contextStatus = document.querySelector<HTMLElement>('#action-context-status');
  const submit = document.querySelector<HTMLElement>('#action-save');
  const feedback = document.querySelector<HTMLElement>('#action-feedback');
  if (eyebrow) eyebrow.textContent = 'NUEVA ACCIÓN';
  if (title) title.textContent = 'Registrar acción de mejora';
  if (context) context.textContent = 'Acción vinculada al sistema de gestión';
  if (contextStatus) contextStatus.textContent = 'Define responsable, fecha y estado.';
  if (submit) submit.textContent = 'Guardar acción';
  if (feedback) feedback.textContent = '';
}

function prepareImprovementModalForCreate() {
  const improvementForm = document.querySelector('#improvement-form') as HTMLFormElement | null;
  if (improvementForm) improvementForm.reset();

  const processSelect = document.querySelector<HTMLSelectElement>('#improvement-process');
  if (processSelect) {
    processSelect.innerHTML = ['<option value="">Sin proceso</option>', ...state.processes.map((process) => `<option value="${process.name}">${process.name}</option>`)].join('');
  }

  const ownerSelect = document.querySelector<HTMLSelectElement>('#improvement-owner');
  if (ownerSelect) {
    ownerSelect.innerHTML = ['<option value="">No aplica</option>', ...state.people.map((person) => `<option value="${person.name}">${person.name}</option>`)].join('');
  }

  const selectedPlan = state.currentImprovementPlanId
    ? state.improvementPlans.find((plan) => plan.id === state.currentImprovementPlanId)
    : null;

  const title = document.querySelector<HTMLElement>('#improvement-modal-title');
  const submit = document.querySelector<HTMLElement>('#improvement-save');
  if (title) title.textContent = selectedPlan ? 'Editar plan de mejora' : 'Crear plan de mejora';
  if (submit) submit.textContent = selectedPlan ? 'Guardar cambios' : 'Crear plan';

  if (selectedPlan && improvementForm) {
    (improvementForm.querySelector('#improvement-name') as HTMLInputElement).value = selectedPlan.name || '';
    (improvementForm.querySelector('#improvement-origin') as HTMLSelectElement).value = selectedPlan.origin || 'Desviación de indicador';
    (improvementForm.querySelector('#improvement-process') as HTMLSelectElement).value = selectedPlan.process || '';
    (improvementForm.querySelector('#improvement-situation') as HTMLTextAreaElement).value = selectedPlan.situation || '';
    (improvementForm.querySelector('#improvement-goal') as HTMLInputElement).value = selectedPlan.goal || '';
    (improvementForm.querySelector('#improvement-owner') as HTMLSelectElement).value = selectedPlan.owner || '';
    (improvementForm.querySelector('#improvement-target-date') as HTMLInputElement).value = selectedPlan.target_date || '';
    (improvementForm.querySelector('#improvement-phase') as HTMLSelectElement).value = selectedPlan.phase || 'Planear';
    (improvementForm.querySelector('#improvement-status') as HTMLSelectElement).value = selectedPlan.status || 'Activo';
    (improvementForm.querySelector('#improvement-progress') as HTMLInputElement).value = String(selectedPlan.progress ?? 0);
  }

  const feedback = document.querySelector<HTMLElement>('#improvement-feedback');
  if (feedback) feedback.textContent = '';
}

function prepareImprovementFollowupModal() {
  const followupForm = document.querySelector('#improvement-followup-form') as HTMLFormElement | null;
  if (!followupForm) return;

  const plan = state.improvementPlans.find((item) => item.id === state.currentImprovementPlanId)
    || state.improvementPlans.find((item) => !(item.status || '').toLowerCase().includes('cerrad'))
    || state.improvementPlans[0]
    || null;

  if (plan) state.currentImprovementPlanId = plan.id;

  const context = followupForm.querySelector<HTMLElement>('#improvement-followup-context');
  const status = followupForm.querySelector<HTMLSelectElement>('#improvement-followup-plan-status');
  const dateField = followupForm.querySelector<HTMLInputElement>('#improvement-followup-date');
  const resultField = followupForm.querySelector<HTMLInputElement>('#improvement-followup-result');
  const notesField = followupForm.querySelector<HTMLTextAreaElement>('#improvement-followup-progress-note');
  const feedback = followupForm.querySelector<HTMLElement>('#improvement-followup-feedback');

  if (context) context.textContent = plan ? plan.name : 'Sin plan seleccionado';
  if (status) status.value = plan?.status || 'Activo';
  if (dateField) dateField.value = plan?.target_date || '';
  if (resultField) resultField.value = '';
  if (notesField) notesField.value = '';
  if (feedback) feedback.textContent = '';
}

function prepareDocumentModalForCreate() {
  const form = document.querySelector('#document-modal form') as HTMLFormElement | null;
  if (form) form.reset();
  populateDocumentProcessOptions();
  const feedback = document.querySelector<HTMLElement>('#document-feedback');
  if (feedback) feedback.textContent = '';
}

function ensureAuditModalFieldIds() {
  const form = document.querySelector('#audit-modal form') as HTMLFormElement | null;
  if (!form) return;

  const inputs = form.querySelectorAll<HTMLInputElement>('input');
  const selects = form.querySelectorAll<HTMLSelectElement>('select');
  const textareas = form.querySelectorAll<HTMLTextAreaElement>('textarea');

  if (!form.querySelector('#audit-title') && inputs[0]) inputs[0].id = 'audit-title';
  if (!form.querySelector('#audit-scope') && inputs[1]) inputs[1].id = 'audit-scope';
  if (!form.querySelector('#audit-finding-title') && inputs[2]) inputs[2].id = 'audit-finding-title';
  if (!form.querySelector('#audit-commitment-due') && inputs[3]) inputs[3].id = 'audit-commitment-due';

  if (!form.querySelector('#audit-process') && selects[0]) selects[0].id = 'audit-process';
  if (!form.querySelector('#audit-executor') && selects[1]) selects[1].id = 'audit-executor';
  if (!form.querySelector('#audit-date') && selects[2]) selects[2].id = 'audit-date';
  if (!form.querySelector('#audit-commitment-owner') && selects[3]) selects[3].id = 'audit-commitment-owner';

  if (!form.querySelector('#audit-notes') && textareas[0]) textareas[0].id = 'audit-notes';

  if (!form.querySelector('#audit-status')) {
    const statusWrapper = document.createElement('label');
    statusWrapper.innerHTML = 'Estado<select id="audit-status"><option>Programada</option><option>En seguimiento</option><option>Completada</option></select>';
    const firstFooter = form.querySelector('footer');
    form.insertBefore(statusWrapper, firstFooter || null);
  }

  if (!form.querySelector('#audit-feedback')) {
    const footer = form.querySelector('footer');
    if (footer) {
      const feedback = document.createElement('span');
      feedback.id = 'audit-feedback';
      feedback.className = 'dialog-feedback';
      feedback.setAttribute('aria-live', 'polite');
      footer.prepend(feedback);
    }
  }
}

function ensureFindingModalFieldIds() {
  const form = document.querySelector('#finding-form') as HTMLFormElement | null;
  if (!form) return;

  const selects = form.querySelectorAll<HTMLSelectElement>('select');
  const inputs = form.querySelectorAll<HTMLInputElement>('input');
  const textareas = form.querySelectorAll<HTMLTextAreaElement>('textarea');

  if (!form.querySelector('#finding-owner') && selects[2]) selects[2].id = 'finding-owner';
  if (!form.querySelector('#finding-verify-date') && inputs[0]) inputs[0].id = 'finding-verify-date';
  if (!form.querySelector('#finding-evidence') && textareas[0]) textareas[0].id = 'finding-evidence';
  if (!form.querySelector('#finding-comment') && textareas[1]) textareas[1].id = 'finding-comment';

  if (!form.querySelector('#finding-title')) {
    const field = document.createElement('label');
    field.innerHTML = 'Titulo del hallazgo<input id="finding-title" placeholder="Ej. Incumplimiento en registros de proceso" />';
    const context = form.querySelector('.record-context');
    context?.insertAdjacentElement('afterend', field);
  }
}

function prepareAuditModalForCreate() {
  ensureAuditModalFieldIds();
  const form = document.querySelector('#audit-modal form') as HTMLFormElement | null;
  if (!form) return;
  form.reset();

  const feedback = form.querySelector<HTMLElement>('#audit-feedback');
  if (feedback) feedback.textContent = '';

  const process = form.querySelector<HTMLSelectElement>('#audit-process');
  if (process) {
    process.innerHTML = ['<option value="">Sin proceso</option>', ...state.processes.map((item) => `<option value="${item.name}">${item.name}</option>`)].join('');
  }

  const executor = form.querySelector<HTMLSelectElement>('#audit-executor');
  if (executor) {
    executor.innerHTML = ['<option value="">Sin responsable</option>', ...state.people.map((person) => `<option value="${person.name}">${person.name}</option>`)].join('');
  }

  const owner = form.querySelector<HTMLSelectElement>('#audit-commitment-owner');
  if (owner) {
    owner.innerHTML = ['<option value="">Sin responsable</option>', ...state.people.map((person) => `<option value="${person.name}">${person.name}</option>`)].join('');
  }

  const title = form.querySelector<HTMLElement>('h2');
  if (title) title.textContent = 'Gestionar auditoria';
}

function prepareAuditModalForEdit(audit: Audit) {
  prepareAuditModalForCreate();
  const form = document.querySelector('#audit-modal form') as HTMLFormElement | null;
  if (!form) return;

  const titleField = form.querySelector<HTMLInputElement>('#audit-title');
  const statusField = form.querySelector<HTMLSelectElement>('#audit-status');
  const executorField = form.querySelector<HTMLSelectElement>('#audit-executor');
  const dateField = form.querySelector<HTMLInputElement | HTMLSelectElement>('#audit-date');
  const notesField = form.querySelector<HTMLTextAreaElement>('#audit-notes');
  const heading = form.querySelector<HTMLElement>('h2');

  if (titleField) titleField.value = audit.title || '';
  if (statusField) statusField.value = audit.status || 'Programada';
  if (executorField) executorField.value = audit.executed_by || '';
  if (dateField) dateField.value = audit.completed_at || '';
  if (notesField) notesField.value = audit.notes || '';
  if (heading) heading.textContent = `Editar ${audit.title}`;
}

function prepareFindingModalForCreate(auditId: string) {
  ensureFindingModalFieldIds();
  state.currentAuditId = auditId;
  state.currentFindingId = null;
  const form = document.querySelector('#finding-form') as HTMLFormElement | null;
  if (!form) return;
  form.reset();
  const feedback = form.querySelector<HTMLElement>('#finding-feedback');
  if (feedback) feedback.textContent = '';

  const owner = form.querySelector<HTMLSelectElement>('#finding-owner');
  if (owner) owner.innerHTML = ['<option value="">Sin responsable</option>', ...state.people.map((person) => `<option value="${person.name}">${person.name}</option>`)].join('');
}

function prepareFindingModalForEdit(auditId: string, findingId: string) {
  prepareFindingModalForCreate(auditId);
  const finding = (state.auditFindingsByAudit[auditId] || []).find((item) => item.id === findingId);
  if (!finding) return;
  state.currentFindingId = findingId;

  const commitment = (state.auditCommitmentsByFinding[findingId] || [])[0] || null;
  const form = document.querySelector('#finding-form') as HTMLFormElement | null;
  if (!form) return;

  const title = form.querySelector<HTMLInputElement>('#finding-title');
  const status = form.querySelector<HTMLSelectElement>('#finding-status');
  const actionStatus = form.querySelector<HTMLSelectElement>('#finding-action-status');
  const owner = form.querySelector<HTMLSelectElement>('#finding-owner');
  const due = form.querySelector<HTMLInputElement>('#finding-verify-date');

  if (title) title.value = finding.title || '';
  if (status) status.value = finding.status || 'Abierto';
  if (actionStatus) actionStatus.value = finding.action_status || 'Por iniciar';
  if (owner) owner.value = commitment?.owner || '';
  if (due) due.value = commitment?.due_date || '';
}

function populateDocumentProcessOptions(selectedProcess?: string | null) {
  const processSelect = document.querySelector<HTMLSelectElement>('#document-process');
  if (!processSelect) return;

  const options = ['<option value="">Sin proceso</option>', ...state.processes.map((process) => `<option value="${process.name}">${process.name}</option>`)].join('');
  processSelect.innerHTML = options;

  if (selectedProcess && Array.from(processSelect.options).some((option) => option.value === selectedProcess)) {
    processSelect.value = selectedProcess;
  } else {
    processSelect.value = '';
  }
}

function prepareActionModalForEdit(action: Action) {
  state.actionFormMode = 'edit';
  state.currentActionId = action.id;
  const actionForm = document.querySelector('#action-form') as HTMLFormElement | null;
  if (!actionForm) return;
  (actionForm.querySelector('#action-name') as HTMLInputElement).value = action.title || '';
  (actionForm.querySelector('#action-description') as HTMLTextAreaElement).value = action.description || '';
  (actionForm.querySelector('#action-due-date') as HTMLInputElement).value = action.due_date || '';
  const statusField = actionForm.querySelector<HTMLSelectElement>('#action-status');
  if (statusField) statusField.value = action.status || 'Por iniciar';
  populateActionProcessOptions(action.source || null);
  populateActionAssigneeOptions(action.assignee || null);
  const eyebrow = document.querySelector<HTMLElement>('#action-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#action-modal-title');
  const context = document.querySelector<HTMLElement>('#action-context');
  const contextStatus = document.querySelector<HTMLElement>('#action-context-status');
  const submit = document.querySelector<HTMLElement>('#action-save');
  const feedback = document.querySelector<HTMLElement>('#action-feedback');
  if (eyebrow) eyebrow.textContent = 'EDITAR ACCIÓN';
  if (title) title.textContent = `Editar ${action.title || 'acción'}`;
  if (context) context.textContent = action.source || 'Sin proceso relacionado';
  if (contextStatus) contextStatus.textContent = `${action.status || 'Por iniciar'}${action.due_date ? ` · Vence ${formatDisplayDate(action.due_date)}` : ''}`;
  if (submit) submit.textContent = 'Guardar cambios';
  if (feedback) feedback.textContent = '';
}

function prepareRiskModalForEdit(risk: Risk) {
  state.riskFormMode = 'edit';
  state.currentRiskId = risk.id;
  const riskForm = document.querySelector('#risk-form') as HTMLFormElement | null;
  if (!riskForm) return;
  (riskForm.querySelector('#risk-name') as HTMLInputElement).value = risk.name || '';
  (riskForm.querySelector('#risk-process') as HTMLSelectElement).value = risk.process_id || '';
  (riskForm.querySelector('#risk-probability') as HTMLSelectElement).value = risk.probability || 'Media';
  (riskForm.querySelector('#risk-impact') as HTMLSelectElement).value = risk.impact || 'Medio';
  (riskForm.querySelector('#risk-status') as HTMLSelectElement).value = risk.status || 'Abierto';
  (riskForm.querySelector('#risk-causes') as HTMLTextAreaElement).value = risk.causes || '';
  (riskForm.querySelector('#risk-control') as HTMLTextAreaElement).value = risk.controls || '';
  (riskForm.querySelector('#risk-treatment') as HTMLTextAreaElement).value = risk.treatment || '';
  (riskForm.querySelector('#risk-review-date') as HTMLInputElement).value = risk.review_date || '';
  populateRiskOwnerOptions(risk.owner || null);

  const eyebrow = document.querySelector<HTMLElement>('#risk-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#risk-modal-title');
  const submit = document.querySelector<HTMLElement>('#risk-save');
  const feedback = document.querySelector<HTMLElement>('#risk-feedback');
  if (eyebrow) eyebrow.textContent = 'EDITAR RIESGO';
  if (title) title.textContent = `Editar ${risk.name}`;
  if (submit) submit.textContent = 'Guardar cambios';
  if (feedback) feedback.textContent = '';
}

function prepareProcessModalForCreate() {
  state.processFormMode = 'create';
  const processForm = document.querySelector('#process-form') as HTMLFormElement | null;
  if (processForm) processForm.reset();
  populateProcessOwnerOptions(null);
  const eyebrow = document.querySelector<HTMLElement>('#process-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#process-modal-title');
  const submit = document.querySelector<HTMLElement>('#process-submit-button');
  if (eyebrow) eyebrow.textContent = 'NUEVO PROCESO';
  if (title) title.textContent = 'Crear proceso';
  if (submit) submit.textContent = 'Crear proceso';
  setProcessIconSelection('git-branch');
}

function prepareProcessModalForEdit(process: ProcessSummary) {
  state.processFormMode = 'edit';
  const processForm = document.querySelector('#process-form') as HTMLFormElement | null;
  if (!processForm) return;
  (processForm.querySelector('#process-name') as HTMLInputElement).value = process.name || '';
  (processForm.querySelector('#process-type') as HTMLSelectElement).value = process.process_type || resolveProcessType(process);
  populateProcessOwnerOptions(process.owner || null);
  (processForm.querySelector('#process-description') as HTMLTextAreaElement).value = process.description || '';
  (processForm.querySelector('#process-objective') as HTMLInputElement).value = process.objective || '';
  (processForm.querySelector('#process-inputs') as HTMLTextAreaElement).value = process.inputs || '';
  (processForm.querySelector('#process-outputs') as HTMLTextAreaElement).value = process.outputs || '';
  (processForm.querySelector('#process-scope') as HTMLTextAreaElement).value = process.scope || '';
  (processForm.querySelector('#process-flow-steps') as HTMLInputElement).value = Array.isArray(process.flow_steps) ? process.flow_steps.join(', ') : '';
  (processForm.querySelector('#process-status') as HTMLSelectElement).value = process.status || 'Activo';
  (processForm.querySelector('#process-health') as HTMLInputElement).value = String(process.health ?? 0);
  const eyebrow = document.querySelector<HTMLElement>('#process-modal-eyebrow');
  const title = document.querySelector<HTMLElement>('#process-modal-title');
  const submit = document.querySelector<HTMLElement>('#process-submit-button');
  if (eyebrow) eyebrow.textContent = 'EDITAR PROCESO';
  if (title) title.textContent = `Editar ${process.name}`;
  if (submit) submit.textContent = 'Guardar cambios';
  setProcessIconSelection(process.icon || 'git-branch');
}

function attachProcessEditButton() {
  const editButton = document.querySelector<HTMLButtonElement>('#process-edit-button');
  if (!editButton) return;
  editButton.addEventListener('click', () => {
    if (!state.currentProcessId) return;
    const process = state.processes.find((item) => item.id === state.currentProcessId);
    if (!process) return;
    prepareProcessModalForEdit(process);
    const modal = document.querySelector<HTMLDialogElement>('#process-modal');
    modal?.showModal();
  });
}

function attachProcessIconPicker() {
  const picker = document.querySelector<HTMLElement>('#process-icon-picker');
  if (!picker) return;
  picker.querySelectorAll<HTMLButtonElement>('.icon-picker-option').forEach((button) => {
    button.addEventListener('click', () => {
      const icon = button.dataset.icon || 'git-branch';
      setProcessIconSelection(icon);
    });
  });
  prepareProcessModalForCreate();
}

function attachUIHelpers() {
  attachModalControls();
}

window.addEventListener('DOMContentLoaded', async () => {
  attachAuthActions();
  attachNavigation();
  attachWorkspaceControls();
  attachProfileMenu();
  attachAppActions();
  attachIndicatorInteractions();
  attachRiskInteractions();
  attachActionInteractions();
  attachAuditInteractions();
  attachImprovementInteractions();
  attachDocumentInteractions();
  attachPeopleInteractions();
  attachPersonStatusModalActions();
  attachDocumentManageModalActions();
  attachDocumentEditorInteractions();
  attachRoleEditButton();
  attachRoleDeleteButton();
  attachProcessEditButton();
  attachProcessIconPicker();
  attachUIHelpers();
  refreshIcons();
  await initializeApp();
});
